import React, { useState, useEffect, useRef, useMemo } from 'react';
import { isQtyBearing, unitSuffix, defaultQtyFor } from '../../utils/addonUnits';
import { Box, Typography, TextField, Select, MenuItem, Menu, Snackbar, Chip, Checkbox, FormControlLabel, Button, FormControl, ToggleButtonGroup, ToggleButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, InputAdornment } from '@mui/material';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { PER_BATTERY_PROMO } from '../../utils/rscPromos';
import { useNavigate } from 'react-router-dom';
import useRscPricing, { commercialPricePerKwp, setAfaOn, getAfaRate, setAfaRate, parseInverterKw } from '../../hooks/useRscPricing';
import useCscBillProposal, { calcCommercialLvBill, calcCommercialMvBill, mvKwhFromBill, mvSolarRate, isMvTariff, calcCommercialLvTouBill, lvTouSolarRate, isLvTouTariff, lvFlatSolarRate, estimateSavingFromBills,estimateMonthlySaving, estimateMonthlySavingSELCo, inverterFromMd, inverterFromKwp, inverterModelAtLeast, inverterModelAtMost, RATIO_CAP_NO_BATT, getCommercialPrice, commercialInverterUpgrade, exceedsHybridMax, BRAND_STRING_ADJ_SMALL } from '../../hooks/useCscBillProposal';
import { buildCscVarMap, generateCscProposalPdf, todayDMY, plus30DMY, MY_STATE_GHI, cscSubstitute, postProcessCscProposal, buildBessSectionHtml, expandLoanTerms } from './cscProposalEngine';
import useCscProposalMath, { defaultSareTariffByKwp, effectiveRateFromFlat, flatRateFromEffective } from '../../hooks/useCscProposalMath';
import { loadCscHistory, pushCscHistory, removeCscHistory, formatSavedAt, CSC_HISTORY_MAX }
    from '../../utils/cscHistory';
import { buildCscWhatsAppMsg, buildCscBessMsg, buildCscSolarPlusBessMsg } from './cscWhatsApp';
import CscLoadProfileChart from './cscLoadProfileChart';
import { buildSyntheticAnalysis } from '../../utils/cscSynthProfile';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import * as XLSX from 'xlsx';
import { parseTnbBill } from '../../utils/tnbBill';
import { parseTnbRows, analyzeLoadProfile } from '../../utils/cscLoadProfile';
import { analyseBess, MV_DEMAND_RATE_PER_KW } from '../../utils/cscBessPeakShaving';
import { generateCommercialQuotation2, generateCommercialAtap, generateCommercialDeclaration, generateCommercialInsurance2, generateCommercialBooking, generateCommercialAll, getSalesTitle } from '../../api/formGeneratorApi';
import { getAddons } from '../../api/resourceApi';
import { getAllAgentGroups, getAgentsByGroupId, getAgentDetailsByAgentId } from '../../api/userApi';
import { useUser } from '../../context/userContext';
import handleAgentName from '../../utils/userUtils';
import { sstPortion, sstOn } from '../../utils/sst';
import { ccFromCash } from '../../utils/ccPricing';
import { createSales, stashBotPayload } from '../../api/salesApi';
import TelegramIcon from '@mui/icons-material/Telegram';
import {
    batteryCostFor, setLvBattUnitNet, nextBattStep, prevBattStep, isHvBattery,
    MAINT_TIERS, omForKwp, pickPct, fillInverter, fillPanelsForInverter, snapNear96, billTargetCard,
    BATT_BRAND_GOODWE, BATT_BRAND_HUAWEI, HUAWEI_BATT_BLOCK_KWH, snapBattToBrand, batteryBrandLabel,
    HUAWEI_MODULE_KWH, HUAWEI_MODULE_PRICE, HUAWEI_MODULE_MAX_KWH,
    warrantyForKwp, warrantyYears, warrantyHasCleaning,
    WARRANTY_TERM_OPTIONS, cleaningOptionsFor, composeWarranty, warrantyCleaningTimes, warrantyTermOnly,
    DEFAULT_WARRANTY_TERM,
} from '../../utils/cscSizing';
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

// Parse a catalog addon's price into { base, perUnit, total } for a given qty.
// Copied VERBATIM from residentialSolarCalculatorV2.jsx — the "+" base+rate
// syntax ("700+170" = RM 700 setup + RM 170 per unit) is honored FIRST,
// regardless of unitType, so a stale/missing unitType can't concatenate the
// digits into "700170". (Bug fix 2026-06-01.)
function computeAddonAmount(addon, qty) {
    if (!addon) return { base: 0, perUnit: 0, total: 0 };
    const raw = String(addon.price ?? '');
    const cleaned = raw.replace(/[^\d.+]/g, '');
    if (cleaned.includes('+')) {
        const [b, p] = cleaned.split('+');
        const base = parseFloat(b) || 0;
        const perUnit = parseFloat(p) || 0;
        return { base, perUnit, total: base + perUnit * (Number(qty) || 0) };
    }
    const qtyBearing = isQtyBearing(addon.unitType);
    if (qtyBearing) {
        const perUnit = parseFloat(cleaned) || 0;
        return { base: 0, perUnit, total: perUnit * (Number(qty) || 0) };
    }
    // 'None' or anything else — treat as a literal one-shot price. Preserve a leading
    // minus so DISCOUNT rows (e.g. Solis Inverter −2000 / Huawei −500 in the residential band) subtract
    // instead of adding. The magnitude strips commas/symbols; the sign follows a leading '-'.
    // (user 2026-08-07)
    const mag = parseFloat(raw.replace(/[^\d.]/g, '')) || 0;
    const num = raw.trim().startsWith('-') ? -mag : mag;
    return { base: 0, perUnit: num, total: num };
}

const PP = "'Poppins', sans-serif";

// "2nd", "3rd", "11th", "12th" — used on the BESS unit chips, where "12th block" is the
// difference between a figure for the whole system and a figure for the last block added.
const ordinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
};
// Single-language label helper — show ONLY the active language (no bilingual
// "中文 / English" mash-up). lang: 'en' | 'zh' | 'ms'. Falls back to EN.
const tr = (lang, en, zh, ms) => (lang === 'zh' ? zh : (lang === 'ms' && ms ? ms : en));
// sessionStorage key for the CSC Combined tab draft — keeps filled data across
// page navigation (cleared on tab close or via the Clear Data button).
// Free one-time cleaning is included from 30 kWp up. Below that the option is not offered
// at all — an option you have to remember not to pick is a promise somebody eventually
// makes by accident. (user 2026-10-14)
// warrantyOptionsFor / warrantyForKwp now live in ../../utils/cscSizing, shared with
// the multi-meter twin and the quotation preview. (user 2026-10-26)

const CSC_DRAFT_KEY = 'csc_draft_v1';
// Absolute URL for a stored (relative) profile photo, so it loads in the proposal.
const PHOTO_API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api$/, '');
const absPhoto = (u) => (u ? `${PHOTO_API_BASE}${u}` : '');
const COLORS = {
    primary: '#1A8CFF', dark: '#0F62CC', light: '#E8F4FF', bg: '#F8FAFC', card: '#FFFFFF',
    text: '#0F172A', sub: '#64748B', border: '#E2E8F0', success: '#10B981', warning: '#F59E0B',
};

const TARIFFS = [
    { rate: 0.515, label: 'Low Voltage (LV)' },
    // Time-of-Use LV: peak/off-peak energy, capacity and network per kWh, and a
    // rebate line. Priced by calcCommercialLvTouBill, never bill ÷ rate.
    { rate: 0.4539, label: 'Low Voltage ToU (LV ToU)' },
    { rate: 0.303, label: 'Medium Voltage (MV)' },
    { rate: 0.437, label: 'High Voltage (HV)' },
    { rate: 0.451, label: 'Agriculture' },
];

const fmtRM = (n) => 'RM ' + Math.round(Number(n) || 0).toLocaleString('en-MY');
// SST shown to 2 decimals (exact, not whole-ringgit) — user 2026-08-04.
const fmtRM2 = (n) => 'RM ' + Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// batteryCostFor / BATT_STEPS / nextBattStep / prevBattStep / isHvBattery / MAINT_TIERS /
// omForKwp now live in ../../utils/cscSizing (shared with cscMeterRow.jsx — see import above).

// Whether the Basic package is offered for this kWp (N/A above 100 kWp). Only used by this
// file's OM-package toggle (not duplicated in cscMeterRow), so it stays local.
const basicOmAvailable = (kwp) => { const k = Number(kwp) || 0; for (const t of MAINT_TIERS) if (k <= t.max) return t.basic != null; return false; };

const Label = ({ children }) => (
    <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>
        {children}
    </Typography>
);

// Section header — 4px accent bar + bold title + thin divider line (mirrors RSC AI BESS 360)
const SectionHeader = ({ title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, mb: 1 }}>
        <Box sx={{ width: 4, height: 24, bgcolor: '#1A8CFF', borderRadius: 1 }} />
        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, letterSpacing: '0.02em' }}>{title}</Typography>
        <Box sx={{ flex: 1, height: 1, bgcolor: '#E2E8F0' }} />
    </Box>
);

// Spec row restyled for on-gradient (white text) hero cards.
const Row = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.6, borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', fontFamily: PP }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', fontFamily: PP }}>{value}</Typography>
    </Box>
);

// One line item inside a white 原电费/新电费 bill sub-card (dark text on white).
const BillLine = ({ label, value, bold, credit }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.25, ...(bold ? { borderTop: '1px solid #E2E8F0', mt: 0.4, pt: 0.5 } : {}) }}>
        <Typography sx={{ fontSize: '0.62rem', color: bold ? COLORS.text : COLORS.sub, fontWeight: bold ? 700 : 500, fontFamily: PP }}>{label}</Typography>
        <Typography sx={{ fontSize: bold ? '0.7rem' : '0.62rem', fontWeight: bold ? 800 : 600, color: credit ? COLORS.success : COLORS.text, fontFamily: "'JetBrains Mono',monospace" }}>{value}</Typography>
    </Box>
);

// A row on the dark card with a dotted leader connecting the label to the right-aligned
// value — keeps the value column aligned without a huge empty gap on wide cards.
const LeaderRow = ({ label, value, labelSx, valueSx, dot = 'rgba(255,255,255,0.22)' }) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, py: 0.15 }}>
        <Typography sx={labelSx}>{label}</Typography>
        <Box sx={{ flex: 1, minWidth: 10, borderBottom: `1px dotted ${dot}`, transform: 'translateY(-3px)' }} />
        <Typography sx={valueSx}>{value}</Typography>
    </Box>
);

// A white bill breakdown box (原电费 / 新电费) — mirrors RSC AI BESS 360.
const BillBox = ({ title, sub, b, exportCredit, totalOverride }) => (
    <Box sx={{ bgcolor: '#fff', borderRadius: 1.5, p: 1, flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: COLORS.dark, fontFamily: PP, mb: 0.4 }}>{title}</Typography>
        {b.energyPeak != null ? (
            <>
                {/* Medium Voltage: energy is split peak / off-peak, and capacity + network are
                    charged on the maximum demand (RM/kW), not on kWh. (user 2026-09-23) */}
                <BillLine label="Energy (peak)" value={b.energyPeak.toFixed(2)} />
                <BillLine label="Energy (off-peak)" value={b.energyOff.toFixed(2)} />
                <BillLine label={`Capacity (${Math.round(b.mdKw)} kW)`} value={b.capacity.toFixed(2)} />
                <BillLine label={`Network (${Math.round(b.mdKw)} kW)`} value={b.network.toFixed(2)} />
            </>
        ) : (
            <>
                <BillLine label="Energy" value={b.energy.toFixed(2)} />
                <BillLine label="Capacity" value={b.capacity.toFixed(2)} />
                <BillLine label="Network" value={b.network.toFixed(2)} />
            </>
        )}
        <BillLine label="Retail" value={b.retail.toFixed(2)} />
        {b.afa ? <BillLine label="AFA" value={`${b.afa < 0 ? '−' : ''}${Math.abs(b.afa).toFixed(2)}`} credit={b.afa < 0} /> : null}
        <BillLine label="KWTBB" value={b.kwtbb.toFixed(2)} />
        {exportCredit > 0 && (
            <BillLine label="Export Cr." value={`−${exportCredit.toFixed(2)}`} credit />
        )}
        <BillLine label="Total" value={fmtRM(totalOverride != null ? totalOverride : b.total)} bold />
        {sub && <Typography sx={{ fontSize: '0.54rem', color: COLORS.sub, fontFamily: PP, mt: 0.3 }}>{sub}</Typography>}
    </Box>
);

// Compact translucent pill for the card's system-spec strip.
const SpecChip = ({ children }) => (
    <Box sx={{ bgcolor: 'rgba(255,255,255,0.14)', borderRadius: 1, px: 0.9, py: 0.35, display: 'inline-flex', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.64rem', fontWeight: 600, color: '#fff', fontFamily: PP, whiteSpace: 'nowrap' }}>{children}</Typography>
    </Box>
);

// On-gradient label for in-card inputs / sections.
const CardLabel = ({ children, sx }) => (
    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: PP, mb: 0.5, ...sx }}>{children}</Typography>
);

// One line in the on-gradient 价格明细 price breakdown.
const PriceRow = ({ label, value, bold, discount }) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, py: 0.3, ...(bold ? { borderTop: '1px solid rgba(255,255,255,0.2)', mt: 0.4, pt: 0.5 } : {}) }}>
        <Typography sx={{ fontSize: bold ? '0.74rem' : '0.68rem', fontWeight: bold ? 800 : 500, color: bold ? '#fff' : 'rgba(255,255,255,0.75)', fontFamily: PP, whiteSpace: 'nowrap' }}>{label}</Typography>
        <Box sx={{ flex: 1, minWidth: 10, borderBottom: `1px dotted rgba(255,255,255,${bold ? 0.3 : 0.22})`, transform: 'translateY(-3px)' }} />
        <Typography sx={{ fontSize: bold ? '0.86rem' : '0.7rem', fontWeight: bold ? 800 : 700, color: discount ? '#86EFAC' : '#fff', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>{value}</Typography>
    </Box>
);

// One of the ROI / CASH / 36M CC tiles.
const Tile = ({ label, value }) => (
    <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1.5, p: 0.8, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.54rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: PP }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#fff', fontFamily: "'JetBrains Mono',monospace" }}>{value}</Typography>
    </Box>
);

// Dark-on-gradient input styling so digits stay readable on the card gradient
// (ported from RSC's add-on rows).
const ADDON_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '6px', bgcolor: 'rgba(15,23,42,0.55)', height: 30,
        '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
        '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
    },
    // Self-selector — a bare <Select sx={ADDON_INPUT_SX}> has the OutlinedInput AS
    // its root, so the descendant rule above misses it (green gradient shows
    // through). Match the root directly so Selects get the same dark bar.
    '&.MuiOutlinedInput-root': {
        borderRadius: '6px', bgcolor: 'rgba(15,23,42,0.55)', height: 30,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#60a5fa' },
    },
    '& .MuiOutlinedInput-input': { padding: '4px 6px', color: '#fff', fontFamily: PP, fontSize: '0.74rem', fontWeight: 600 },
    '& .MuiSelect-select': { color: '#fff', py: '4px', pl: '8px' },
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.75)' },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { '-webkit-appearance': 'none', margin: 0 },
    '& input[type=number]': { '-moz-appearance': 'textfield' },
};

// In-card add-on package (dark-on-gradient). Ported from RSC's "配件包" block:
// pick from the shared catalog, set qty, edit Base + per-unit Rate separately,
// per-line green total, remove rows. CSC-simplified: no phase filter, no synth
// backup-box / lock. stopPropagation so editing doesn't toggle card selection.
function CardAddons({ rows, catalog, onAdd, onUpdate, onRemove, lang }) {
    rows = rows || [];
    const grid = '1fr 50px 1fr 22px';
    const total = rows.reduce((s, a) => {
        const qty = (isQtyBearing(a.unitType))
            ? (a._qty != null && a._qty !== '' && Number.isFinite(Number(a._qty)) ? Math.max(0, Number(a._qty)) : 1) : 1;
        return s + computeAddonAmount(a, qty).total;
    }, 0);
    return (
        <Box onClick={(e) => e.stopPropagation()} sx={{ mt: 1.25 }}>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5, fontFamily: PP }}>
                {tr(lang, 'Add-ons', '配件包')}
            </Typography>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, p: 1 }}>
                {rows.length === 0 && (
                    <Typography sx={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.45)', fontFamily: PP, fontStyle: 'italic', textAlign: 'center', py: 0.5 }}>
                        {tr(lang, 'No add-ons yet — pick from catalog below', '暂无配件 — 从下方目录选择')}
                    </Typography>
                )}
                {rows.length > 0 && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: grid, gap: 0.5, alignItems: 'center', pb: 0.4, borderBottom: '1px dashed rgba(255,255,255,0.18)' }}>
                        <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: PP }}>{tr(lang, 'Item', '项目')}</Typography>
                        <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: PP, textAlign: 'center' }}>{tr(lang, 'Qty', '数量')}</Typography>
                        <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: PP, textAlign: 'center' }}>{tr(lang, 'Price', '价格')}</Typography>
                        <span />
                    </Box>
                )}
                {rows.map((r) => {
                    if (r._autoInvUpg) {
                        // Auto inverter-upgrade add-on — read-only (final kW, priced from catalog).
                        const aline = computeAddonAmount(r, 1);
                        return (
                            <Box key={r.rowId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px dashed rgba(255,255,255,0.1)' }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#fff', fontFamily: PP, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.itemName}>
                                    {r.itemName} <span style={{ fontSize: '0.56rem', color: 'rgba(255,255,255,0.55)', fontWeight: 700 }}>· AUTO</span>
                                </Typography>
                                <Typography sx={{ fontSize: '0.66rem', color: '#86EFAC', fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, flexShrink: 0, ml: 1 }}>= RM {aline.total.toLocaleString()}</Typography>
                            </Box>
                        );
                    }
                    const qtyBearing = isQtyBearing(r.unitType);
                    const qtyShown = r._qty != null ? r._qty : 1;
                    const line = computeAddonAmount(r, Number(qtyShown) || 0);
                    const unitTag = unitSuffix(r.unitType);
                    // Parse price into Base / Rate parts so each gets its own field.
                    const cleaned = String(r.price ?? '').replace(/[^\d.+]/g, '');
                    let baseStr = '', rateStr = '';
                    if (cleaned.includes('+')) { const p = cleaned.split('+'); baseStr = p[0] || ''; rateStr = p[1] || ''; }
                    else { rateStr = cleaned; }
                    const baseNum = parseFloat(baseStr) || 0;
                    const rateNum = parseFloat(rateStr) || 0;
                    const showBase = baseNum > 0;
                    const showRate = rateNum > 0 || baseNum === 0;
                    const setPriceParts = (nb, nr) => {
                        const b = (nb || '').toString().replace(/[^\d.]/g, '');
                        const rt = (nr || '').toString().replace(/[^\d.]/g, '');
                        const combined = (parseFloat(b) > 0) ? (rt === '' ? b : `${b}+${rt}`) : rt;
                        onUpdate(r.rowId, { price: combined });
                    };
                    return (
                        <Box key={r.rowId || r.addonId} sx={{ display: 'grid', gridTemplateColumns: grid, gap: 0.5, alignItems: 'center', py: 0.5, borderBottom: '1px dashed rgba(255,255,255,0.1)' }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#fff', fontFamily: PP, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.itemName}>{r.itemName}</Typography>
                                    {unitTag && <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.55)', fontFamily: PP, fontWeight: 600, flexShrink: 0 }}>{unitTag}</Typography>}
                                </Box>
                                {line.total > 0 && (
                                    <Typography sx={{ fontSize: '0.6rem', color: '#86EFAC', fontFamily: "'JetBrains Mono',monospace", fontWeight: 600, mt: 0.1 }}>
                                        {(line.base > 0 && line.perUnit > 0)
                                            ? `RM ${line.base.toLocaleString()} + ${qtyShown}× RM ${line.perUnit.toLocaleString()} = RM ${line.total.toLocaleString()}`
                                            : `= RM ${line.total.toLocaleString()}`}
                                    </Typography>
                                )}
                            </Box>
                            <TextField size="small" type="number" value={r._qty ?? 1} disabled={!qtyBearing}
                                onChange={(e) => onUpdate(r.rowId, { _qty: e.target.value === '' ? '' : parseFloat(e.target.value) || 0 })}
                                inputProps={{ step: 1, min: 0 }}
                                sx={{ ...ADDON_INPUT_SX, '& .MuiOutlinedInput-input': { ...ADDON_INPUT_SX['& .MuiOutlinedInput-input'], textAlign: 'center' }, '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(255,255,255,0.6)' } }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                {showBase && (
                                    <TextField size="small" type="number" value={baseStr} placeholder="0"
                                        onChange={(e) => setPriceParts(e.target.value, rateStr)} inputProps={{ step: 1, min: 0 }}
                                        sx={{ ...ADDON_INPUT_SX, '& .MuiOutlinedInput-input': { ...ADDON_INPUT_SX['& .MuiOutlinedInput-input'], textAlign: 'right', fontSize: '0.7rem' } }}
                                        InputProps={showRate ? { startAdornment: <Typography sx={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.55)', fontFamily: PP, fontWeight: 600, mr: 0.4 }}>Base</Typography> } : undefined} />
                                )}
                                {showRate && (
                                    <TextField size="small" type="number" value={rateStr} placeholder="0"
                                        onChange={(e) => setPriceParts(baseStr, e.target.value)} inputProps={{ step: 1, min: 0 }}
                                        sx={{ ...ADDON_INPUT_SX, '& .MuiOutlinedInput-input': { ...ADDON_INPUT_SX['& .MuiOutlinedInput-input'], textAlign: 'right', fontSize: '0.7rem' } }} />
                                )}
                            </Box>
                            <Button size="small" onClick={() => onRemove(r.rowId)}
                                sx={{ minWidth: 0, width: 22, height: 22, p: 0, fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#FCA5A5', bgcolor: 'rgba(252,165,165,0.1)' } }}>×</Button>
                        </Box>
                    );
                })}
                {total > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.6, mt: 0.4 }}>
                        <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: 'rgba(255,255,255,0.75)', fontFamily: PP }}>Add-ons subtotal</Typography>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#fff', fontFamily: "'JetBrains Mono',monospace" }}>{fmtRM(total)}</Typography>
                    </Box>
                )}
                <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                    <Select size="small" fullWidth displayEmpty value=""
                        onChange={(e) => { if (e.target.value) onAdd(Number(e.target.value)); }}
                        sx={ADDON_INPUT_SX}>
                        <MenuItem value="" disabled sx={{ fontSize: '0.72rem', fontFamily: PP, fontStyle: 'italic' }}>+ {tr(lang, 'Add from catalog…', '从目录添加配件…')}</MenuItem>
                        {(() => {
                            const groups = {};
                            (catalog || []).forEach((a) => { const c = a.category || 'General'; (groups[c] = groups[c] || []).push(a); });
                            const nodes = [];
                            Object.keys(groups).sort().forEach((cat) => {
                                nodes.push(<MenuItem key={`h-${cat}`} disabled sx={{ fontWeight: 700, fontSize: '0.65rem', color: '#64748B', opacity: '1 !important', fontFamily: PP }}>{cat}</MenuItem>);
                                groups[cat].forEach((a) => {
                                    const priceNum = parseFloat(String(a.price ?? '').replace(/[^\d.+]/g, ''));
                                    const priceLabel = String(a.price ?? '').includes('+') ? a.price : (Number.isFinite(priceNum) ? priceNum.toLocaleString() : a.price);
                                    const unitTag = unitSuffix(a.unitType);
                                    nodes.push(
                                        <MenuItem key={a.addonId} value={a.addonId} sx={{ fontSize: '0.72rem', fontFamily: PP }}>
                                            {a.itemName} — RM {priceLabel}{unitTag}
                                        </MenuItem>
                                    );
                                });
                            });
                            return nodes;
                        })()}
                    </Select>
                </Box>
            </Box>
        </Box>
    );
}

function OptionCard({ card, label, selected, gradient, basis = 'p50', onBasisChange, hasMd = false, mdKw = 0, tariffRate = 0, sys = {}, onSysChange, priceForPanels, battery = '', onBatteryChange, onBatteryBlur, battBrand = BATT_BRAND_GOODWE, onBattBrandChange, disc = {}, onDiscChange, asternDiscAmt = 0, onApplyAstern, sstDeduct = {}, onSstDeductChange, om = '', onOmChange, omPkg = 'premium', onOmPkgChange, pricing = {}, addonRows, catalog, onAddAddon, onUpdateAddon, onRemoveAddon, mdWarn = null, lang = 'en', billFn = null, savings = null, batteryOff = false, bessLabel = '' }) {
    // ── Derive the original (pre-solar) consumption kWh and both itemised bills ──
    // billFn = the tab's tariff-aware bill (MV itemises peak / off-peak + MD charges);
    // without it the card falls back to the LV breakdown scaled to the tariff.
    const _bill = billFn || ((k) => calcCommercialLvBill(k, tariffRate));
    let body = null;
    if (card) {
        const consumptionKwh = Math.round((card.selfConsumed || 0) + (card.importAfterSolar || 0));
        const origBill = _bill(consumptionKwh);
        const newImportKwh = Math.round(card.importAfterSolar || 0);
        /* The card draws its OWN After-Solar bill, so the schedule's peak share has to be
           handed over here too — wiring it into the tab's copy alone left this one on the
           0.283 constant, and the two then disagreed about which energy line the solar
           came off. (user 2026-10-31) */
        const newBill = _bill(newImportKwh, card.selfPeakFrac);
        const exportCredit = card.mode === 'selco' ? 0 : (Math.round(card.exported || 0) * 0.20);
        const newTotalAfterCredit = Math.max(0, newBill.total - exportCredit);
        // Saving split: self-use saving (bill reduction from consuming your own solar) +
        // export credit (exported kWh × RM 0.20 SMP). Sum = the Monthly Saving. SELCo has
        // no export credit (curtailed). (user 2026-07-25)
        const exportSaving = exportCredit;
        const selfUseSaving = Math.max(0, (card.monthlySaving || 0) - exportSaving);

        // Changing the inverter auto-refills the panels to the MOST that inverter can carry
        // at the 1.42 ratio — inverter & panels stay in lock-step so the ratio can NEVER
        // exceed 1.42. > 60 kW can't be hybrid → flip type to String. (user 2026-09-26)
        const applyInverterKw = (newInv) => {
            if (!onSysChange || !(Number(newInv) > 0)) return;
            const wpk = (Number(sys.panelWatt) || 650) / 1000;
            let t = sys.type || 'string';
            if (newInv > 60 && t === 'hybrid' && !isHvBattery(battery)) { t = 'string'; onSysChange('type', 'string'); }
            const newPanels = fillPanelsForInverter(newInv, wpk, RATIO_CAP_NO_BATT);
            const newKwp = +(newPanels * wpk).toFixed(2);
            onSysChange('inverterKw', String(newInv));
            onSysChange('panels', String(newPanels));
            const _price = priceForPanels && priceForPanels(newPanels, newKwp, sys.brand || 'goodwe', t);
            if (_price > 0) { onSysChange('systemCost', String(Math.round(_price))); onSysChange('pricePerKwp', String(Math.round(_price / newKwp))); }
            else if (sys.pricePerKwp) { onSysChange('systemCost', String(Math.round(Number(sys.pricePerKwp) * newKwp))); }
        };

        body = (
            <>
                {/* System spec strip — so the card is self-contained (no scroll to
                    System & Pricing). Pulls each option's own sizing + the shared
                    panel watt / battery. */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 1.25 }}>
                    <SpecChip>{sys.panels || card.panels} panels × {sys.panelWatt || 650}Wp</SpecChip>
                    <SpecChip>{sys.inverterKw || card.inverterKw}kW {sys.brand || card.brandLabel || card.brand || ''}</SpecChip>
                    {mdKw > 0 && Number(sys.inverterKw || card.inverterKw) > mdKw && <SpecChip>SELCo on excess (&gt;{mdKw}kW MD)</SpecChip>}
                    {Number(battery) > 0 && <SpecChip>{battery}kWh</SpecChip>}
                    {card.stLicenseApplies && <SpecChip>ST License</SpecChip>}
                    {sys.pricePerKwp && <SpecChip>{fmtRM(sys.pricePerKwp)}/kWp</SpecChip>}
                </Box>

                {/* System Details — each card owns its FULL editable system spec
                    (dark-on-gradient). Defaults to the option's auto sizing; edits
                    stick. stopPropagation so editing doesn't toggle selection. */}
                <Box onClick={(e) => e.stopPropagation()} sx={{ mb: 1.25 }}>
                    <CardLabel>{tr(lang, 'System Details', '系统配置')}</CardLabel>
                    {/* Row 1: Inverter kW · Type · Brand — inverter-first layout (user 2026-07-22).
                        Mobile: wrap to 2-up so the Type/Brand toggle buttons don't overflow the
                        card; Brand spans full width (row 2) to fit GoodWe/Solis/Huawei. Desktop
                        keeps the 3-column row. (user 2026-08-02) */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1, alignItems: 'start' }}>
                        <Box>
                            <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>Inverter kW</CardLabel>
                            {/* Stepper (same design as Battery): step through REAL inverter
                                models only; panels auto-refill so ratio ≤ 1.42. No free-text.
                                (user 2026-07-22) */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {[['−', inverterModelAtMost, -0.01], ['+', inverterModelAtLeast, 0.01]].map(([sym, fn, d], i) => (
                                    <Button key={i} size="small" variant="outlined"
                                        onClick={() => { const t = sys.type || 'string'; const cur = Number(sys.inverterKw || card.inverterKw) || 0; applyInverterKw(fn(cur + d, t)); }}
                                        sx={{ minWidth: 26, width: 26, height: 26, p: 0, fontSize: '1rem', lineHeight: 1, color: '#fff', borderColor: 'rgba(255,255,255,0.35)', order: i === 0 ? 0 : 2 }}>{sym}</Button>
                                ))}
                                <Box sx={{ flex: 1, order: 1, textAlign: 'center', fontFamily: PP, fontSize: '0.72rem', fontWeight: 700, color: '#fff', py: 0.5, borderRadius: '6px', background: 'rgba(255,255,255,0.08)' }}>
                                    {Number(sys.inverterKw || card.inverterKw) > 0 ? `${sys.inverterKw || card.inverterKw} kW` : '—'}
                                </Box>
                            </Box>
                            {(() => {
                                // LIVE ratio = the kWp/inverter actually shown (recomputes on
                                // manual edits), not the stale candidate ratio. CSC cap is a
                                // hard 1.42 (no battery-raised cap) — over 1.42 → step up the
                                // inverter (panels-high/kW-low is not allowed). (user 2026-07-22)
                                const _k = Number(sys.kwp || card.kwp) || 0;
                                const _i = Number(sys.inverterKw || card.inverterKw) || 0;
                                if (_k <= 0 || _i <= 0) return null;
                                const _r = _k / _i;
                                const _over = _r > 1.451;
                                return (
                                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: _over ? '#F87171' : 'rgba(255,255,255,0.65)', fontFamily: PP, mt: 0.4, lineHeight: 1.2 }}>
                                        ratio {_r.toFixed(2)}{_over ? ' — over 1.45, step up inverter' : ''}
                                    </Typography>
                                );
                            })()}
                        </Box>
                        <Box>
                            {/* Choose inverter TYPE — it filters the brand options
                                (Hybrid → GoodWe only). Huawei has no String/Hybrid split: the
                                battery attaches through a power module, so the toggle is
                                replaced by a plain note there. (user 2026-09-26) */}
                            <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>Inverter Type</CardLabel>
                            {(sys.brand || 'goodwe') === 'huawei' ? (
                                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.75)', fontFamily: PP, py: 0.4 }}>
                                    Not applicable — battery runs on a Huawei power module
                                </Typography>
                            ) : (
                            <ToggleButtonGroup exclusive value={sys.type || 'string'} size="small"
                                onChange={(_, v) => {
                                    if (!v) return;
                                    onSysChange && onSysChange('type', v);
                                    // Hybrid = GoodWe only; String = GoodWe/Solis/Huawei. GoodWe now
                                    // covers BOTH types, so picking String no longer flips it to Solis.
                                    let b = sys.brand || 'goodwe';
                                    if (v === 'hybrid' && (b === 'huawei' || b === 'solis')) { b = 'goodwe'; onSysChange && onSysChange('brand', 'goodwe'); }
                                    const k = Number(sys.kwp) || 0;
                                    const ppk = commercialPricePerKwp(k, b, v, 0);
                                    if (ppk > 0 && k > 0) {
                                        onSysChange && onSysChange('pricePerKwp', String(ppk));
                                        onSysChange && onSysChange('systemCost', String(Math.round(ppk * k)));
                                    }
                                }}
                                sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.66rem', textTransform: 'none', py: 0.4, px: 1.2, color: '#fff', borderColor: 'rgba(255,255,255,0.3)', borderRadius: '6px !important' }, '& .Mui-selected': { background: 'linear-gradient(135deg,#1A8CFF,#0F62CC) !important', color: '#fff !important', borderColor: 'transparent !important' }, '& .Mui-disabled': { color: 'rgba(255,255,255,0.25)', borderColor: 'rgba(255,255,255,0.12)' } }}>
                                {/* LV battery (1..96 kWh) forces Hybrid; HV battery (≥100 kWh) forces
                                    String (AC-coupled). No battery → free choice. (user 2026-07-10)
                                    GoodWe builds a hybrid up to 20 kW and bigger ones are several of
                                    those PCS together, so the stack stops at 60 kW — above that the
                                    system is String and the battery is AC-coupled. (user 2026-09-26) */}
                                <ToggleButton value="hybrid" disabled={isHvBattery(battery) || (battBrand === BATT_BRAND_HUAWEI && Number(battery) > 0) || exceedsHybridMax(Number(sys.kwp) || 0, RATIO_CAP_NO_BATT)}>Hybrid</ToggleButton>
                                <ToggleButton value="string" disabled={Number(battery) > 0 && battBrand !== BATT_BRAND_HUAWEI && !isHvBattery(battery) && !exceedsHybridMax(Number(sys.kwp) || 0, RATIO_CAP_NO_BATT)}>String</ToggleButton>
                            </ToggleButtonGroup>
                            )}
                        </Box>
                        <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
                            <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>Inverter Brand</CardLabel>
                            <ToggleButtonGroup exclusive value={sys.brand || 'goodwe'} size="small"
                                onChange={(_, v) => {
                                    if (!v) return;
                                    onSysChange && onSysChange('brand', v);
                                    // Brand change re-prices per kWp + refolds system cost.
                                    // Huawei keeps whatever type is set — it has no String/Hybrid split,
                                    // and clearing it to String would grey GoodWe out on the way back.
                                    let t = sys.type || 'string';
                                    if (v === 'solis') t = 'string';                     // Solis String-only
                                    if (v === 'goodwe') t = 'hybrid';                    // GoodWe Hybrid-only
                                    if (v !== 'huawei') onSysChange && onSysChange('type', t);
                                    const k = Number(sys.kwp) || 0;
                                    const ppk = commercialPricePerKwp(k, v, t, 0);
                                    if (ppk > 0 && k > 0) {
                                        onSysChange && onSysChange('pricePerKwp', String(ppk));
                                        onSysChange && onSysChange('systemCost', String(Math.round(ppk * k)));
                                    }
                                }}
                                sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.66rem', textTransform: 'none', py: 0.4, px: 1.2, color: '#fff', borderColor: 'rgba(255,255,255,0.3)', borderRadius: '6px !important' }, '& .Mui-selected': { background: 'linear-gradient(135deg,#1A8CFF,#0F62CC) !important', color: '#fff !important', borderColor: 'transparent !important' }, '& .Mui-disabled': { color: 'rgba(255,255,255,0.25)', borderColor: 'rgba(255,255,255,0.12)' } }}>
                                {/* Brand↔type: GoodWe = Hybrid only (greyed under String); Solis =
                                    String only (greyed under Hybrid). Huawei has no String/Hybrid
                                    split — its power module takes the battery — so it stays
                                    selectable under both. (user 2026-09-26) */}
                                {/* A battery decides the brand: GoodWe battery → GoodWe inverter,
                                    Huawei battery → Huawei. Solis has no battery product at all, so
                                    it is greyed out the moment one is added. (user 2026-09-26) */}
                                {[['goodwe', 'GoodWe'], ['solis', 'Solis'], ['huawei', 'Huawei']]
                                    .map(([k, lbl]) => {
                                        const hasBatt = Number(battery) > 0;
                                        const battOwner = battBrand === BATT_BRAND_HUAWEI ? 'huawei' : 'goodwe';
                                        const disabled = hasBatt
                                            ? k !== battOwner
                                            : k === 'huawei' ? false
                                            : (k === 'goodwe' ? (sys.type || 'string') === 'string' : (sys.type || 'string') === 'hybrid');
                                        return <ToggleButton key={k} value={k} disabled={disabled}>{lbl}</ToggleButton>;
                                    })}
                            </ToggleButtonGroup>
                        </Box>
                    </Box>
                    {/* Row 2: Panels · System kWp · Panel Watt — mobile wraps 2-up (user 2026-08-02) */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1, mt: 1 }}>
                        <Box>
                            <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>Panels</CardLabel>
                            <TextField size="small" type="number" fullWidth value={sys.panels ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    const wpk = (Number(sys.panelWatt) || 650) / 1000;
                                    onSysChange && onSysChange('panels', v);
                                    // Price + inverter follow the panel count (user 2026-06-29:
                                    // "prices should change after change panel number"). Re-price
                                    // on the CORRECT basis — ≤30 kWp ATAP / >30 commercial — via
                                    // priceForPanels, then the per-kWp + inverter SKU.
                                    const _newKwp = (Number(v) || 0) * wpk;
                                    if (_newKwp > 0) {
                                        const _price = priceForPanels && priceForPanels(Number(v), _newKwp, sys.brand || 'goodwe', sys.type || 'string');
                                        if (_price > 0) {
                                            onSysChange && onSysChange('systemCost', String(Math.round(_price)));
                                            onSysChange && onSysChange('pricePerKwp', String(Math.round(_price / _newKwp)));
                                        } else if (sys.pricePerKwp) {
                                            onSysChange && onSysChange('systemCost', String(Math.round(Number(sys.pricePerKwp) * _newKwp)));
                                        }
                                        onSysChange && onSysChange('inverterKw', String(inverterFromKwp(_newKwp, RATIO_CAP_NO_BATT, sys.type || 'string', sys.brand)));
                                    }
                                }}
                                inputProps={{ min: 0, step: 1 }} sx={ADDON_INPUT_SX} />
                        </Box>
                        <Box>
                            <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>System kWp</CardLabel>
                            <TextField size="small" type="number" fullWidth value={sys.kwp ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    const wpk = (Number(sys.panelWatt) || 650) / 1000;
                                    const p = v ? Math.ceil(Number(v) / wpk) : 0;
                                    onSysChange && onSysChange('panels', p ? String(p) : '');
                                    // Price + inverter follow the edited kWp (see Panels handler).
                                    const _newKwp = (p || 0) * wpk;
                                    if (_newKwp > 0) {
                                        const _price = priceForPanels && priceForPanels(p, _newKwp, sys.brand || 'goodwe', sys.type || 'string');
                                        if (_price > 0) {
                                            onSysChange && onSysChange('systemCost', String(Math.round(_price)));
                                            onSysChange && onSysChange('pricePerKwp', String(Math.round(_price / _newKwp)));
                                        } else if (sys.pricePerKwp) {
                                            onSysChange && onSysChange('systemCost', String(Math.round(Number(sys.pricePerKwp) * _newKwp)));
                                        }
                                        onSysChange && onSysChange('inverterKw', String(inverterFromKwp(_newKwp, RATIO_CAP_NO_BATT, sys.type || 'string', sys.brand)));
                                    }
                                }}
                                inputProps={{ min: 0, step: 0.01 }} sx={ADDON_INPUT_SX} />
                        </Box>
                        <Box>
                            <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>Panel Watt (Wp)</CardLabel>
                            <Select size="small" fullWidth value={sys.panelWatt || '650'}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    onSysChange && onSysChange('panelWatt', v);
                                    // kWp auto-recomputes from panels × new wpk via sysFor;
                                    // keep systemCost consistent if a per-kWp price is set.
                                    const p = Number(sys.panels) || 0;
                                    if (p && sys.pricePerKwp) onSysChange && onSysChange('systemCost', String(Math.round(Number(sys.pricePerKwp) * p * (Number(v) / 1000))));
                                }} sx={ADDON_INPUT_SX}>
                                <MenuItem value="620" sx={{ fontSize: '0.72rem', fontFamily: PP }}>620 Wp</MenuItem>
                                <MenuItem value="640" sx={{ fontSize: '0.72rem', fontFamily: PP }}>640 Wp</MenuItem>
                                <MenuItem value="650" sx={{ fontSize: '0.72rem', fontFamily: PP }}>650 Wp</MenuItem>
                            </Select>
                        </Box>
                    </Box>
                    {sys.panels && sys.panelWatt && (
                        <Typography sx={{ fontSize: '0.56rem', color: 'rgba(255,255,255,0.6)', fontFamily: PP, mt: 0.3 }}>
                            Auto: {sys.panels} × {sys.panelWatt}Wp = {(Number(sys.panels) * Number(sys.panelWatt) / 1000).toFixed(2)} kWp
                        </Typography>
                    )}
                    {mdWarn && (
                        <Typography sx={{ fontSize: '0.56rem', fontWeight: 600, fontFamily: PP, mt: 0.4,
                            color: mdWarn.csMode === 'selco' ? '#FCD34D' : '#86EFAC' }}>
                            {mdWarn.csMode === 'selco'
                                ? `SELCo — inverter ${mdWarn.neededInv}kW exceeds MD ${mdWarn.atapMaxInv}kW (zero export, self-consumption only)`
                                : `ATAP — inverter ${mdWarn.neededInv}kW within MD ${mdWarn.atapMaxInv}kW`}
                        </Typography>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                        <Box>
                            <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>Per kWp (RM)</CardLabel>
                            <TextField size="small" type="number" fullWidth value={sys.pricePerKwp ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    onSysChange && onSysChange('pricePerKwp', v);
                                    onSysChange && onSysChange('systemCost', v && Number(sys.kwp) ? String(Math.round(Number(v) * Number(sys.kwp))) : '');
                                }}
                                inputProps={{ min: 0 }} sx={ADDON_INPUT_SX} />
                        </Box>
                        <Box>
                            <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>Panel Total (RM)</CardLabel>
                            <TextField size="small" type="number" fullWidth value={sys.systemCost ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    onSysChange && onSysChange('systemCost', v);
                                    onSysChange && onSysChange('pricePerKwp', v && Number(sys.kwp) ? String(Math.round(Number(v) / Number(sys.kwp))) : '');
                                }}
                                inputProps={{ min: 0 }} sx={ADDON_INPUT_SX} />
                        </Box>
                    </Box>
                    {Number(sys.kwp) > 0 && (
                        <Typography sx={{ fontSize: '0.56rem', color: 'rgba(255,255,255,0.6)', fontFamily: PP, mt: 0.3 }}>
                            {['goodwe', 'solis', 'huawei'].map(b => { const p = commercialPricePerKwp(Number(sys.kwp), b, b === 'goodwe' ? (sys.type || 'string') : 'string', 0); const lbl = b === 'goodwe' ? 'GoodWe' : b === 'solis' ? 'Solis' : 'Huawei'; return p > 0 ? `${lbl} RM ${p.toLocaleString()}/kWp` : null; }).filter(Boolean).join(' · ')}
                        </Typography>
                    )}
                    <Box sx={{ mt: 1 }}>
                        <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>Add-on Price (RM) — flat</CardLabel>
                        <TextField size="small" type="number" fullWidth value={sys.addOn ?? ''}
                            onChange={(e) => onSysChange && onSysChange('addOn', e.target.value)}
                            inputProps={{ min: 0 }} sx={ADDON_INPUT_SX} />
                    </Box>
                </Box>

                {/* 电池 Battery + 折扣 Discount — per-card inputs (RSC-style).
                    stopPropagation so editing doesn't toggle card selection. */}
                <Box onClick={(e) => e.stopPropagation()} sx={{ mb: 1.25 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <Box>
                            <CardLabel sx={batteryOff ? { opacity: 0.45 } : undefined}>
                                {tr(lang, 'Battery (kWh)', '电池')}
                                {batteryOff ? tr(lang, ' — in BESS', ' — 见 BESS') : ''}
                            </CardLabel>
                            {/* Battery brand (user 2026-09-21): GoodWe = the existing ladder (LV 16-kWh
                                units, HV 261-kWh blocks); Huawei = 241-kWh blocks only @ RM1,350/kWh. */}
                            <ToggleButtonGroup exclusive size="small" value={battBrand || BATT_BRAND_GOODWE} fullWidth disabled={batteryOff}
                                onChange={(_, v) => { if (v && onBattBrandChange) onBattBrandChange(v); }}
                                sx={{ mb: 0.5, '& .MuiToggleButton-root': { flex: 1, fontFamily: PP, fontSize: '0.6rem', textTransform: 'none', py: 0.2, color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.3)', borderRadius: '6px !important' }, '& .Mui-selected': { background: 'linear-gradient(135deg,#1A8CFF,#0F62CC) !important', color: '#fff !important', borderColor: 'transparent !important' } }}>
                                <ToggleButton value={BATT_BRAND_GOODWE}>GoodWe</ToggleButton>
                                <ToggleButton value={BATT_BRAND_HUAWEI}>Huawei</ToggleButton>
                            </ToggleButtonGroup>
                            {/* Stepper: GoodWe 0 → LV 16-kWh steps (16..96, Hybrid) → HV 261-kWh units (String);
                                Huawei 0 → 241 · 482 · 723 … (String). No free-text; no auto-suggest. (user 2026-07-10) */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {[['−', prevBattStep], ['+', nextBattStep]].map(([sym, fn], i) => (
                                    <Button key={i} size="small" disabled={batteryOff} onClick={() => onBatteryBlur && onBatteryBlur(String(fn(battery, battBrand)))}
                                        sx={{ minWidth: 26, width: 26, height: 26, p: 0, fontSize: '1rem', lineHeight: 1, color: '#fff', borderColor: 'rgba(255,255,255,0.35)', order: i === 0 ? 0 : 2 }}
                                        variant="outlined">{sym}</Button>
                                ))}
                                <Box sx={{ flex: 1, order: 1, textAlign: 'center', fontFamily: PP, fontSize: '0.72rem', fontWeight: 700, color: batteryOff ? 'rgba(255,255,255,0.4)' : '#fff', py: 0.5, borderRadius: '6px', background: 'rgba(255,255,255,0.08)' }}>
                                    {/* A card that says "None" while a 783 kWh BESS is quoted below reads as
                                        no battery at all. Name the actual pack. (user 2026-10-31:
                                        "the card need to write 3x261kwh = 783kwh battery") */}
                                    {Number(battery) > 0
                                        ? `${battery} kWh${(battBrand === BATT_BRAND_HUAWEI || isHvBattery(battery)) ? ' HV' : ''}`
                                        : (batteryOff && bessLabel) ? bessLabel : tr(lang, 'None', '无')}
                                </Box>
                            </Box>
                            {/* A greyed control with no reason beside it gets reported as a bug.
                                Say which product owns the battery instead. (user 2026-10-26) */}
                            {batteryOff && (
                                <Typography sx={{ fontSize: '0.56rem', color: 'rgba(255,255,255,0.55)', fontFamily: PP, mt: 0.35, fontStyle: 'italic', lineHeight: 1.35 }}>
                                    {tr(lang,
                                        'Priced under BESS — peak shaving below. Panels and BESS are separate solutions on MV.',
                                        '电池计入下方 BESS 削峰方案。中压项目中，电板与 BESS 为两套方案。')}
                                </Typography>
                            )}
                            {/* Battery per day: how much it CHARGES (from the solar surplus) vs
                                how much it actually USES (the night load it can discharge into).
                                used < charged ⇒ oversized (a bigger pack can't help); fills < 100%
                                ⇒ the surplus can't fully charge it. (user 2026-06-23) */}
                            {Number(battery) > 0 && card && card.battDailyChargeKwh != null && (() => {
                                const ch = Math.round(card.battDailyChargeKwh || 0);
                                const us = Math.round(card.battDailyUsedKwh || 0);
                                const oversized = us < ch - 0.5;
                                const cantFill = (card.battChargePct ?? 100) < 100;
                                // Excess the battery can't absorb (battery full → surplus left over).
                                // In SELCo that surplus is CLIPPED (wasted) — a bigger battery would
                                // capture it; in ATAP it's exported for SMP/NEM credit (not wasted).
                                const surplus = Math.round(card.dailySurplusKwh || 0);
                                const clip = Math.max(0, surplus - ch);
                                const isSelco = card.mode === 'selco';
                                const showExcess = clip > 1 && !cantFill;   // battery is the bottleneck
                                const warn = oversized || cantFill;
                                const detail = `charges ~${ch}kWh · uses ~${us}kWh${cantFill ? ` · fills ${card.battChargePct}%` : ''}`;
                                const detailCn = `充 ~${ch}kWh · 用 ~${us}kWh${cantFill ? ` · 充到${card.battChargePct}%` : ''}`;
                                return (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.4, mt: 0.4 }}>
                                            {warn && <WarningAmberRounded sx={{ fontSize: '0.85rem', color: '#FBBF24', mt: '1px', flexShrink: 0 }} />}
                                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: warn ? '#FBBF24' : '#86efac', fontFamily: PP, lineHeight: 1.25 }}>
                                                {warn
                                                    ? tr(lang, `Oversized — ${detail}`, `偏大 — ${detailCn}`)
                                                    : tr(lang, `Battery/day: ${detail}`, `电池/天：${detailCn}`)}
                                            </Typography>
                                        </Box>
                                        {showExcess && (
                                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: isSelco ? '#FBBF24' : '#93C5FD', fontFamily: PP, mt: 0.2, lineHeight: 1.25 }}>
                                                {isSelco
                                                    ? tr(lang, `Excess ~${clip}kWh/day clipped (wasted) — bigger battery captures more`, `多余 ~${clip}kWh/天 被削掉（浪费）— 加大电池可多储`)
                                                    : tr(lang, `Excess ~${clip}kWh/day exported (SMP credit)`, `多余 ~${clip}kWh/天 卖回电网 (SMP)`) }
                                            </Typography>
                                        )}
                                    </>
                                );
                            })()}
                        </Box>
                        <Box>
                            {/* DISCOUNT or MARK-UP, one switch, always a POSITIVE amount typed.
                                A mark-up used to mean typing a negative discount, which the
                                `> 0` guards below silently threw away — so it did nothing at
                                all. The sign now comes from the switch, and the sale still
                                stores a mark-up as a NEGATIVE discount, which is the one
                                convention the commission and the quotation already understand.
                                (user 2026-10-29: "can we do like discount and markup switch,
                                 then they key in the amount instead of negative?") */}
                            {(() => {
                                const isUp = disc.mode === 'markup';
                                return (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                        <CardLabel sx={{ mb: 0 }}>
                                            {isUp ? tr(lang, 'Mark-up (RM or %)', '加价 (RM 或 %)') : tr(lang, 'Discount (RM or %)', '折扣 (RM 或 %)')}
                                        </CardLabel>
                                        <ToggleButtonGroup exclusive size="small" value={isUp ? 'markup' : 'discount'}
                                            onChange={(_, v) => {
                                                if (!v || !onDiscChange) return;
                                                // Switching side clears the Astern tick — an Astern
                                                // discount is a discount, never a mark-up.
                                                onDiscChange(v === 'markup'
                                                    ? { mode: 'markup', kind: 'agent' }
                                                    : { mode: 'discount' });
                                            }}
                                            sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.54rem', textTransform: 'none', py: 0.1, px: 0.7, lineHeight: 1.2, color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.3)', borderRadius: '5px !important' }, '& .Mui-selected': { background: 'linear-gradient(135deg,#1A8CFF,#0F62CC) !important', color: '#fff !important', borderColor: 'transparent !important' } }}>
                                            <ToggleButton value="discount">{tr(lang, 'Discount', '折扣')}</ToggleButton>
                                            <ToggleButton value="markup">{tr(lang, 'Mark-up', '加价')}</ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                );
                            })()}
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <TextField size="small" type="number" placeholder="RM" value={disc.rm ?? ''}
                                    onChange={(e) => onDiscChange && onDiscChange({ rm: e.target.value, pct: '', kind: 'agent' })}
                                    inputProps={{ min: 0 }} sx={ADDON_INPUT_SX} />
                                <TextField size="small" type="number" placeholder="%" value={disc.pct ?? ''}
                                    onChange={(e) => onDiscChange && onDiscChange({ pct: e.target.value, rm: '', kind: 'agent' })}
                                    inputProps={{ min: 0, max: 100 }} sx={ADDON_INPUT_SX} />
                            </Box>
                            {/* RSC-style default Astern discount (≤ 30 kWp, the residential band) — checkbox so
                                it's obvious whether it's applied + easy to toggle off. */}
                            {asternDiscAmt > 0 && disc.mode !== 'markup' && (
                                <Box sx={{ mt: 0.5, borderRadius: '6px', border: '1px solid rgba(134,239,172,0.5)',
                                    bgcolor: disc.kind === 'astern' ? 'rgba(134,239,172,0.22)' : 'transparent', px: 0.75 }}>
                                    <FormControlLabel
                                        control={<Checkbox size="small" checked={disc.kind === 'astern'}
                                            onChange={() => onApplyAstern && onApplyAstern()}
                                            sx={{ py: 0.2, color: '#86efac', '&.Mui-checked': { color: '#86efac' } }} />}
                                        label={<span style={{ fontFamily: PP, fontSize: '0.62rem', fontWeight: 700, color: disc.kind === 'astern' ? '#fff' : '#86efac' }}>
                                            {tr(lang, `Astern Discount (RM ${asternDiscAmt.toLocaleString()})`, `Astern 折扣 (RM ${asternDiscAmt.toLocaleString()})`)}
                                        </span>}
                                        sx={{ m: 0, width: '100%' }} />
                                </Box>
                            )}
                            {/* Deduct SST — the Discount typed above deducts the SST first (SST
                                payable = max(0, fullSST − discount); excess comes off price). Tick
                                alone with no discount = no effect. Mirrors create-sales.
                                Hidden in mark-up mode: there is no discount to offset the SST
                                with, and letting the tick stay would read as if it did something. */}
                            {disc.mode !== 'markup' && (
                            <Box sx={{ mt: 0.6, borderRadius: '6px', border: '1px solid rgba(147,197,253,0.5)',
                                bgcolor: sstDeduct.on ? 'rgba(147,197,253,0.16)' : 'transparent', px: 0.75, py: 0.25 }}>
                                <FormControlLabel
                                    control={<Checkbox size="small" checked={!!sstDeduct.on}
                                        onChange={(e) => onSstDeductChange && onSstDeductChange({ on: e.target.checked })}
                                        sx={{ py: 0.2, color: '#93C5FD', '&.Mui-checked': { color: '#93C5FD' } }} />}
                                    label={<span style={{ fontFamily: PP, fontSize: '0.62rem', fontWeight: 700, color: sstDeduct.on ? '#fff' : '#93C5FD' }}>
                                        {tr(lang, 'Deduct SST — the Discount deducts SST first', '扣除 SST — 折扣先抵 SST')}
                                    </span>}
                                    sx={{ m: 0, width: '100%' }} />
                                {sstDeduct.on && (
                                    <Typography sx={{ fontSize: '0.54rem', color: 'rgba(255,255,255,0.6)', fontFamily: PP, mt: 0.2, lineHeight: 1.3 }}>
                                        {(pricing.discAmt || 0) > 0
                                            ? tr(lang, `Your ${fmtRM(pricing.discAmt)} discount deducts the SST first; any excess comes off the price.`, `${fmtRM(pricing.discAmt)} 折扣先抵 SST；超出部分从价格扣。`)
                                            : tr(lang, 'Type a Discount above — that amount deducts the SST. No discount = SST charged in full.', '在上方填折扣 — 该金额抵扣 SST。无折扣 = 全额收 SST。')}
                                    </Typography>
                                )}
                            </Box>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <CardLabel sx={{ mb: 0 }}>{tr(lang, 'O&M Annual (RM)', '维护费 (RM)')}</CardLabel>
                            <ToggleButtonGroup exclusive size="small" value={omPkg}
                                onChange={(_, v) => { if (v) onOmPkgChange && onOmPkgChange(v); }}
                                sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.56rem', textTransform: 'none', py: 0.1, px: 0.8, lineHeight: 1.2, color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.3)', borderRadius: '5px !important' }, '& .Mui-selected': { background: 'linear-gradient(135deg,#1A8CFF,#0F62CC) !important', color: '#fff !important', borderColor: 'transparent !important' }, '& .Mui-disabled': { color: 'rgba(255,255,255,0.25)', borderColor: 'rgba(255,255,255,0.12)' } }}>
                                <ToggleButton value="basic" disabled={!basicOmAvailable(sys.kwp)}>Basic</ToggleButton>
                                <ToggleButton value="premium">Premium</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <TextField size="small" type="number" fullWidth placeholder="0"
                            value={om ?? ''} onChange={(e) => onOmChange && onOmChange(e.target.value)}
                            inputProps={{ min: 0 }} sx={ADDON_INPUT_SX} />
                        <Typography sx={{ fontSize: '0.54rem', color: 'rgba(255,255,255,0.5)', fontFamily: PP, mt: 0.3 }}>
                            {omPkg === 'basic' ? tr(lang, 'Basic package', '基础配套') : tr(lang, 'Premium package', '高级配套')} · auto from kWp tier · editable
                        </Typography>
                    </Box>
                    {Number(battery) > 0 && (
                        <Typography sx={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.6)', fontFamily: PP, mt: 0.3 }}>
                            {/* A small Huawei bank is priced per 7 kWh module, so show the modules,
                                not a back-computed RM/kWh nobody quotes from. (user 2026-09-26) */}
                            {(battBrand === BATT_BRAND_HUAWEI && Number(battery) <= HUAWEI_MODULE_MAX_KWH)
                                ? `${Math.ceil(Number(battery) / HUAWEI_MODULE_KWH)} × ${HUAWEI_MODULE_KWH}kWh module × ${fmtRM(HUAWEI_MODULE_PRICE)} = ${fmtRM(batteryCostFor(battery, battBrand))}`
                                : (battBrand === BATT_BRAND_HUAWEI || isHvBattery(battery))
                                ? `${battery}kWh ${batteryBrandLabel(battBrand)} HV × RM${Math.round(batteryCostFor(battery, battBrand) / Number(battery)).toLocaleString()}/kWh = ${fmtRM(batteryCostFor(battery, battBrand))}`
                                : `${Math.ceil(Number(battery) / 16)} × 16kWh = ${fmtRM(batteryCostFor(battery, battBrand))}`}
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: '0.56rem', color: 'rgba(255,255,255,0.5)', fontFamily: PP, fontStyle: 'italic', mt: 0.3 }}>
                        {tr(lang, 'Fill RM or % · system only (panels+inverter), excludes battery & add-ons', 'RM 或 % 任填一项 · 仅适用于系统 (电板+逆变器), 不含电池及配件')}
                    </Typography>
                </Box>

                {/* 原电费 / 新电费 — two white bill boxes */}
                {/* WHOSE MONTH IS THIS? Without a label the pair reads as "your bill", and
                    with several months entered that is never true of any of them. It names
                    the heaviest month so the reader can match it against a bill they hold.
                    (user 2026-10-24: "its bit weird if i write 3 months, but which month u
                     use") */}
                {savings && (
                    <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: '#FCD34D', fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.4 }}>
                        {tr(lang, `Shown: your heaviest month (${fmtRM(savings.peakBill)}) of ${savings.count}`,
                                  `显示: ${savings.count} 个月中最高的一个月 (${fmtRM(savings.peakBill)})`)}
                    </Typography>
                )}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.25 }}>
                    <BillBox
                        title={tr(lang, 'Original', '原电费')}
                        sub={`${consumptionKwh} kWh`}
                        b={origBill}
                        exportCredit={0}
                    />
                    <BillBox
                        title={tr(lang, 'After Solar', '新电费')}
                        sub={`${newImportKwh} kWh import`}
                        b={newBill}
                        exportCredit={exportCredit}
                        totalOverride={newTotalAfterCredit}
                    />
                </Box>

                {/* Monthly Saving banner + self-use / export split */}
                <Box sx={{ p: 1, bgcolor: 'rgba(74,222,128,0.18)', borderRadius: 1.5, textAlign: 'center', border: '1px solid rgba(74,222,128,0.4)' }}>
                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#86EFAC', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: PP }}>{savings ? tr(lang, 'Saving · heaviest month', '每月省电费 · 最高月') : tr(lang, 'Monthly Saving', '每月省电费')}</Typography>
                    <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '1.2rem', fontWeight: 800, color: '#4ade80', lineHeight: 1.2 }}>{fmtRM(card.monthlySaving)}</Typography>
                    <Typography sx={{ fontSize: '0.56rem', fontWeight: 600, color: card.mode === 'selco' ? '#FCD34D' : '#86EFAC', fontFamily: PP, mt: 0.3, letterSpacing: '0.04em' }}>
                        {card.mode === 'selco' ? 'SELCo' : 'ATAP'}
                    </Typography>
                    {/* THE SPREAD, three ways. The headline above is ONE month, and a single
                        number invites the reader to multiply it by twelve - which is wrong by
                        the whole spread on a 20k/10k/5k cycle. Average is the one that does
                        survive x12: it is annualSaving / 12, the same figure the proposal and
                        the twenty-year tables are built on, so the deck can never disagree
                        with the card. Highest and lowest bound it, and the lowest is the one
                        the customer rings about.
                        (user 2026-10-24: "we shall see lowest month saving and highest month
                         saving" / "3 more monthly saving which is average, highest, lowest") */}
                    {savings && (
                        <Box sx={{ mt: 0.7, pt: 0.55, borderTop: '1px solid rgba(74,222,128,0.3)', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 0.5 }}>
                            {[
                                [tr(lang, 'Average', '平均'), savings.avg, '#fff'],
                                [tr(lang, 'Highest', '最高'), savings.high, '#86EFAC'],
                                [tr(lang, 'Lowest', '最低'), savings.low, '#FCD34D'],
                            ].map(([t, v, c]) => (
                                <Box key={t} sx={{ textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: '0.52rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t}</Typography>
                                    <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.74rem', fontWeight: 800, color: c, lineHeight: 1.25 }}>{fmtRM(v)}</Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                    {savings && (
                        <Typography sx={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.55)', fontFamily: PP, mt: 0.35, fontStyle: 'italic' }}>
                            {tr(lang, 'Proposal, payback and WhatsApp all use the average',
                                      '建议书、回本期与 WhatsApp 均采用平均值')}
                        </Typography>
                    )}
                    {/* Two-part saving split */}
                    <Box sx={{ mt: 0.6, pt: 0.5, borderTop: '1px solid rgba(74,222,128,0.3)', textAlign: 'left' }}>
                        <LeaderRow dot="rgba(255,255,255,0.28)"
                            labelSx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.8)', fontFamily: PP, whiteSpace: 'nowrap' }}
                            valueSx={{ fontSize: '0.6rem', fontWeight: 700, color: '#fff', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}
                            label={tr(lang, 'Self-use saving', '自用省电')} value={fmtRM(selfUseSaving)} />
                        <LeaderRow dot="rgba(255,255,255,0.28)"
                            labelSx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.8)', fontFamily: PP, whiteSpace: 'nowrap' }}
                            valueSx={{ fontSize: '0.6rem', fontWeight: 700, color: card.mode === 'selco' ? '#FCA5A5' : '#86EFAC', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}
                            label={card.mode === 'selco' ? tr(lang, 'Export credit (curtailed)', '出口收益(弃电)') : tr(lang, 'Export credit (RM 0.20)', '出口收益 (RM 0.20)')}
                            value={card.mode === 'selco' ? tr(lang, 'no credit', '无收益') : fmtRM(exportSaving)} />
                    </Box>
                </Box>

                {/* Generation split — solar-hour self-use vs off-hour export (the
                    export estimate), then total gen + grid import still needed. */}
                {(() => {
                    const isSelco = card.mode === 'selco';
                    const gen = Math.round(card.generated || 0);
                    const self = Math.round(card.selfConsumed || 0);
                    const selfPct = card.generated > 0 ? Math.round((card.selfConsumed / card.generated) * 100) : 0;
                    const offKwh = Math.round((isSelco ? card.curtailed : card.exported) || 0);
                    const offPct = Math.round(((isSelco ? card.curtailedPct : card.exportPct) || 0) * 100);
                    const imp = Math.round(card.importAfterSolar || 0);
                    const gridExp = isSelco ? 0 : Math.round(card.exported || 0);   // kWh sold to grid (SELCo = 0)
                    const battCharge = Math.round(card.battMonthlyChargeKwh || 0);  // solar → battery
                    const battUsed = Math.round(card.battMonthlySelfKwh || 0);      // battery → load
                    const hasBatt = battCharge > 0;
                    const battWasted = hasBatt && battUsed < 0.5 * battCharge;      // charged but barely used
                    const rowL = { fontSize: '0.62rem', color: 'rgba(255,255,255,0.7)', fontFamily: PP, whiteSpace: 'nowrap' };
                    const rowV = { fontSize: '0.62rem', fontWeight: 700, color: '#fff', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' };
                    return (
                        <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 1.5 }}>
                            <LeaderRow labelSx={rowL} valueSx={rowV}
                                label={tr(lang, 'Solar-hour self-use', '日照时段自用')} value={`${self.toLocaleString()} kWh · ${selfPct}%`} />
                            <LeaderRow labelSx={rowL} valueSx={{ ...rowV, color: isSelco ? '#FCA5A5' : '#86EFAC' }}
                                label={isSelco ? tr(lang, 'Off-hour curtailed', '非时段(弃电)') : tr(lang, 'Off-hour export', '非时段出口')}
                                value={`${offKwh.toLocaleString()} kWh · ${offPct}%`} />
                            {hasBatt && (
                                <>
                                    <LeaderRow labelSx={rowL} valueSx={{ ...rowV, color: '#F9A8D4' }}
                                        label={tr(lang, 'Solar → battery (charged)', '太阳能→电池(充电)')} value={`${battCharge.toLocaleString()} kWh`} />
                                    <LeaderRow labelSx={rowL} valueSx={{ ...rowV, color: battWasted ? '#FCD34D' : '#F9A8D4' }}
                                        label={tr(lang, 'Battery → load (used)', '电池→用电(放电)')}
                                        value={`${battUsed.toLocaleString()} kWh${battWasted ? tr(lang, ' · oversized', ' · 过大') : ''}`} />
                                </>
                            )}
                            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.12)', mt: 0.3, pt: 0.4 }}>
                                <LeaderRow labelSx={rowL} valueSx={rowV}
                                    label={tr(lang, 'Total generation', '总发电量')} value={`${gen.toLocaleString()} kWh`} />
                                <LeaderRow labelSx={rowL} valueSx={rowV}
                                    label={tr(lang, 'Grid import', '仍需电网')} value={`${imp.toLocaleString()} kWh`} />
                                <LeaderRow labelSx={rowL} valueSx={{ ...rowV, color: isSelco ? '#FCA5A5' : '#86EFAC' }}
                                    label={tr(lang, 'Grid export', '出口电网')}
                                    value={isSelco ? tr(lang, '0 (curtailed)', '0(弃电)') : `${gridExp.toLocaleString()} kWh`} />
                            </Box>
                        </Box>
                    );
                })()}

                {/* In-card add-on package (RSC-style, on the gradient) */}
                <CardAddons
                    rows={addonRows}
                    catalog={catalog}
                    onAdd={onAddAddon}
                    onUpdate={onUpdateAddon}
                    onRemove={onRemoveAddon}
                    lang={lang}
                />

                {/* 价格明细 · CASH — panel − discount + battery + add-ons = final,
                    then the 80/20 + 6% SST breakdown that drives the commercial quotation. */}
                <Box sx={{ mt: 1.25, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, p: 1.25 }}>
                    <CardLabel sx={{ mb: 0.5 }}>{tr(lang, 'Price · CASH', '价格明细 · CASH')}</CardLabel>
                    <PriceRow label={tr(lang, 'System base', '基础价')} value={fmtRM(pricing.panel || 0)} />
                    {(() => {
                        const disc = pricing.discAmt || 0;
                        const ccRows = (
                            <>
                                <PriceRow label={tr(lang, '24M CC 0% Instalment', '24M CC 0% 分期')} value={fmtRM(pricing.cc24 || 0)} />
                                <PriceRow label={tr(lang, '36M CC 0% Instalment', '36M CC 0% 分期')} value={fmtRM(pricing.cc || 0)} />
                                <PriceRow label={tr(lang, '60M CC 0% Instalment', '60M CC 0% 分期')} value={fmtRM(pricing.cc60 || 0)} />
                            </>
                        );
                        if (pricing.deductOn) {
                            // Deduct-SST view: the typed discount deducts the SST first. Stays
                            // arithmetically clean — System + Battery + Add-ons + full SST − Discount.
                            const fullBase = (pricing.panel || 0) + (pricing.battery || 0) + (pricing.addons || 0);
                            return (
                                <>
                                    {pricing.battery > 0 && <PriceRow label={tr(lang, '+ Battery', '+ 电池')} value={`+ ${fmtRM(pricing.battery)}`} />}
                                    {pricing.addons !== 0 && <PriceRow label={tr(lang, 'Add-ons', '配件')} value={`${pricing.addons < 0 ? '− ' : '+ '}${fmtRM(Math.abs(pricing.addons))}`} discount={pricing.addons < 0} />}
                                    <PriceRow label={tr(lang, 'Subtotal (pre-SST)', '税前小计')} value={fmtRM(fullBase)} bold />
                                    <PriceRow label="SST 6% (on 20%)" value={`+ ${fmtRM2(pricing.fullSST || 0)}`} />
                                    {disc > 0 && <PriceRow label={tr(lang, '− Discount (deducts SST first)', '− 折扣（先扣 SST）')} value={`− ${fmtRM(disc)}`} discount />}
                                    <PriceRow label={tr(lang, 'Grand Total (SST deducted)', '含税总额（已扣 SST）')} value={fmtRM2(pricing.grandTotal || 0)} bold />
                                    <Typography sx={{ fontSize: '0.56rem', color: '#93C5FD', fontFamily: PP, fontWeight: 600, mt: 0.2, lineHeight: 1.3 }}>
                                        {disc <= 0
                                            ? tr(lang, 'Type a Discount above — it deducts the SST first, then price.', '在上方填折扣 — 会先扣 SST，再扣价格。')
                                            : ((pricing.sstPayable || 0) <= 0.5
                                                ? tr(lang, `SST fully waived by the ${fmtRM(disc)} discount → SST payable 0.`, `SST 已被 ${fmtRM(disc)} 折扣全额扣除 → 应付 SST 0。`)
                                                : tr(lang, `SST payable after − ${fmtRM(disc)}: ${fmtRM2(pricing.sstPayable || 0)}.`, `扣 ${fmtRM(disc)} 后应付 SST：${fmtRM2(pricing.sstPayable || 0)}。`))}
                                    </Typography>
                                    {ccRows}
                                </>
                            );
                        }
                        // Normal view: System − Discount + Battery + Add-ons = Final, then 80/20/SST.
                        const cash = pricing.cash || 0;
                        const p80 = Math.round(cash * 0.80);
                        const p20 = sstPortion(cash);
                        const sst = sstOn(cash);
                        return (
                            <>
                                {/* A mark-up is a NEGATIVE discount, so it has its own row —
                                    `disc > 0` alone hid it entirely, which is what made the
                                    old negative-number approach look like it did nothing. */}
                                {disc > 0 && <PriceRow label={tr(lang, `− Discount (${Math.round(pricing.discPct)}%)`, `− 折扣 (${Math.round(pricing.discPct)}%)`)} value={`− ${fmtRM(disc)}`} discount />}
                                {disc < 0 && <PriceRow label={tr(lang, `+ Mark-up (${Math.abs(Math.round(pricing.discPct))}%)`, `+ 加价 (${Math.abs(Math.round(pricing.discPct))}%)`)} value={`+ ${fmtRM(Math.abs(disc))}`} />}
                                {pricing.battery > 0 && <PriceRow label={tr(lang, '+ Battery', '+ 电池')} value={`+ ${fmtRM(pricing.battery)}`} />}
                                {pricing.addons !== 0 && <PriceRow label={tr(lang, 'Add-ons', '配件')} value={`${pricing.addons < 0 ? '− ' : '+ '}${fmtRM(Math.abs(pricing.addons))}`} discount={pricing.addons < 0} />}
                                <PriceRow label={tr(lang, 'Final (CASH)', '最终价 (CASH)')} value={fmtRM(cash)} bold />
                                <PriceRow label="80% / Part A" value={fmtRM(p80)} />
                                <PriceRow label="20% / Part B" value={fmtRM(p20)} />
                                <PriceRow label="SST 6% (on 20%)" value={fmtRM2(sst)} />
                                <PriceRow label={tr(lang, 'Grand Total (incl SST)', '含税总额')} value={fmtRM2(cash + sst)} bold />
                                {ccRows}
                            </>
                        );
                    })()}
                </Box>

                {/* ROI / CASH / 24M CC tiles */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mt: 1.25 }}>
                    <Tile label="ROI" value={card.roiYears != null ? `${card.roiYears} yrs` : '—'} />
                    <Tile label="CASH" value={fmtRM(pricing.cash || 0)} />
                    <Tile label="24M CC" value={fmtRM(pricing.cc24 || 0)} />
                </Box>
            </>
        );
    }

    return (
        <div
            style={{
                background: gradient,
                borderRadius: 14,
                padding: 20,
                color: '#fff',
                minHeight: card ? 300 : 'auto',
                border: selected ? '2px solid #fff' : '2px solid transparent',
                boxShadow: selected ? '0 6px 18px rgba(0,0,0,0.28)' : '0 1px 3px rgba(0,0,0,0.12)',
                transition: 'box-shadow 0.15s, border-color 0.15s',
            }}
        >
            {/* Header row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#fff', fontFamily: PP }}>{label}</Typography>
                {card && (
                    <Chip label={`${card.kwp} kWp`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 600, fontSize: '0.66rem', fontFamily: PP, height: 22, borderRadius: 1.5 }} />
                )}
            </Box>

            {/* Sizing-basis chooser — sizes this option to a coverage target (50% / 70%),
                plus a contextual 3rd: "MAX MD" when a Max Demand is entered (ATAP-capped),
                or "100%" coverage when there's no MD (the whole system is SELCo). */}
            {onBasisChange && (
            <Box sx={{ mb: 1.25 }}>
                <ToggleButtonGroup exclusive size="small" value={basis}
                    onChange={(_, v) => { if (v && onBasisChange) onBasisChange(v); }} fullWidth
                    sx={{ '& .MuiToggleButton-root': { flex: 1, fontFamily: PP, fontSize: '0.6rem', fontWeight: 700, textTransform: 'none', py: 0.3, px: 0.5, color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.3)', borderRadius: '6px !important' }, '& .Mui-selected': { background: 'linear-gradient(135deg,#1A8CFF,#0F62CC) !important', color: '#fff !important', borderColor: 'transparent !important' } }}>
                    <ToggleButton value="p50">~50%</ToggleButton>
                    <ToggleButton value="p70">~70%</ToggleButton>
                    {hasMd
                        ? <ToggleButton value="maxmd">MAX MD</ToggleButton>
                        : <ToggleButton value="p100">100%</ToggleButton>}
                </ToggleButtonGroup>
            </Box>
            )}

            {card ? body : (
                <Typography sx={{ fontStyle: 'italic', fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)', fontFamily: PP, py: 1 }}>
                    Enter a bill to size this option.
                </Typography>
            )}
        </div>
    );
}

export default function CscCombinedTab({ sharedPhaseMode = 'single', setSharedPhaseMode, lang = 'en' }) {
    const { user } = useUser();
    const navigate = useNavigate();
    const [submitBusy, setSubmitBusy] = useState(false);
    const [sendBotBusy, setSendBotBusy] = useState(false);
    const submitGuardRef = useRef(false);
    const [lastSubmittedSig, setLastSubmittedSig] = useState(null);
    const [clearDialogOpen, setClearDialogOpen] = useState(false);
    /* SAVED SETS - the last CSC_HISTORY_MAX working sets, kept in localStorage so a
       quote can be opened again days later. Distinct from the autosave draft, which is
       one slot in sessionStorage and gone when the tab closes.
       (user 2026-10-23: "can we have a record history of 5 ... so we can refr back?") */
    const [history, setHistory] = useState(() => loadCscHistory());
    const [historyAnchor, setHistoryAnchor] = useState(null);
    const [historyNote, setHistoryNote] = useState('');

    const saveToHistory = () => {
        const label = (customerName || '').trim() || 'Untitled';
        const kwp = Number(sel?.kwp) || 0;
        const bits = [kwp ? `${kwp} kWp` : '', bill ? `bill RM ${Number(bill).toLocaleString()}` : '']
            .filter(Boolean).join(' · ');
        const next = pushCscHistory({ label, subtitle: bits, data: buildCscSnapshot() });
        setHistory(next);
        setHistoryNote(next.length ? `Saved "${label}"` : 'Could not save - storage is full or blocked.');
    };

    const restoreFromHistory = (entry) => {
        setHistoryAnchor(null);
        try {
            applyCscSnapshot(entry.data);
            setHistoryNote(`Opened "${entry.label}"`);
        } catch {
            setHistoryNote('That saved set could not be opened.');
        }
    };

    const doClearData = () => { try { sessionStorage.removeItem(CSC_DRAFT_KEY); } catch {} window.location.reload(); };
    const [bill, setBill] = useState('');
    // Monthly usage (kWh) interlocked with the RM bill via the tariff: bill = kWh × tariff.
    // The RM `bill` stays the calc source of truth; billKwh is the convenience input so the
    // agent can type EITHER. Kept in sync on bill edit, kWh edit, and tariff change.
    const [billKwh, setBillKwh] = useState('');
    /* Bill <-> kWh, through the AFA-aware rate so the two agree with the Original box.
       Both take the RAW tariff and apply AFA inside, so every call site stays
       `_kwhFromBill(v, tariffRate)` and there is one place that knows about AFA.
       (user 2026-10-26: "can help me change the kwh accoridng to afa caluclation?") */
    const _kwhFromBill = (rm, t) => { const r = lvFlatSolarRate(t); return (Number(rm) > 0 && r > 0 ? String(Math.round(Number(rm) / r)) : ''); };
    /* kWh → the month's RM. On ToU this must be the REAL bill, not kWh × a rate.
       Roughly HALF an MV bill is capacity + network charged on the MD, which no per-kWh
       rate contains. Writing a month as kWh × 0.2753 and then reading it back with the
       proper bill formula is two different conversions, and the round trip loses the
       demand charges entirely: a 254,138 kWh month was written as RM 69,964, and
       inverting that left nothing for energy — the fixed charges alone are RM 89,346 —
       so every month came back 0 kWh and the Average / Highest / Lowest / Yearly cards
       all read RM 0. Same formula in both directions, and it round-trips exactly.
       MD is held, not scaled: a quieter month does not lower the peak that set it.
       (user 2026-10-31: "what is this actually 0") */
    const _billFromKwh = (kwh, t) => {
        const k = Number(kwh) || 0;
        if (!(k > 0)) return '';
        if (isTou && mvKwh > 0) {
            const f = k / mvKwh;                       // scale the entered peak/off split
            const m = { peakKwh: mvCtx.peakKwh * f, offKwh: mvCtx.offKwh * f, mdKw: mvCtx.mdKw };
            const b = isMv ? calcCommercialMvBill(m) : calcCommercialLvTouBill(m);
            return String(Math.round(b?.total || 0));
        }
        const r = lvFlatSolarRate(t);
        return (r > 0 ? String(Math.round(k * r)) : '');
    };
    const [tariffRate, setTariffRate] = useState(0.515);
    // AFA on/off (default OFF — No AFA, per user 2026-07-02, mirrors the RSC calc). The
    // module flag (getAfaOn) is a shared singleton read by calcCommercialLvBill; sync it to
    // this component's state on mount + on every toggle so the Original/After bills (and the
    // saving) include the AFA surcharge only when ON. OFF → Original = entered ex-AFA bill.
    const [afaOn, setAfaOnState] = useState(false);
    /* AFA IS OFTEN A REBATE, AND THEN IT IS THE ONLY WAY THE BILL TALLIES.
       General Settings carries ONE rate — this month's, positive. A commercial ToU bill
       carries its own, per billing period, and on this account every recent month is
       NEGATIVE: Feb −2.77 sen, Mar −2.15, Apr −0.47. ALL COSMOS February reads
       RM 155,154.14, and only −RM0.0277/kWh gets you there:
         energy + capacity + network + retail + KWTBB = 162,193.76
         AFA 254,138 × −0.0277                        =  −7,039.62  → 155,154.14
       With AFA off the app showed 162,194 and with the global +3.67 sen it showed
       171,521 — neither reachable from a toggle. So the rate itself is typed, straight
       off the bill, and negatives are expected rather than tolerated.
       (user 2026-10-31: "cause AFA is -7k. the bill like not tally if without AFA") */
    const [afaRateInput, setAfaRateInput] = useState('');
    // The General Settings rate, latched the first time it arrives non-zero, so clearing
    // the field restores it instead of pinning AFA to 0.
    const afaDefaultRef = useRef(null);
    if (afaDefaultRef.current === null && getAfaRate() !== 0) afaDefaultRef.current = getAfaRate();
    const afaRateTouched = useRef(false);
    useEffect(() => {
        const typed = parseFloat(afaRateInput);
        if (afaRateInput !== '' && Number.isFinite(typed)) setAfaRate(typed);
        else if (afaDefaultRef.current !== null) setAfaRate(afaDefaultRef.current);
        /* On a FLAT tariff the bill is the anchor and the kWh is derived from it, so a
           different AFA rate means a different consumption behind the same RM figure —
           exactly what the With/No AFA toggle already does. Skipped on the first run so
           mounting never rewrites a restored draft. ToU needs none of this: its kWh is
           typed off the meter. */
        if (!afaRateTouched.current) { afaRateTouched.current = true; return; }
        if (!isTou) setBillKwh(_kwhFromBill(bill, tariffRate));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [afaRateInput]);
    useEffect(() => { setAfaOn(afaOn); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    /* SEVERAL REAL BILLS instead of one typed figure. A proposal built on one month
       overstates the light months, because a system cannot offset power the site never
       used - so the saving curve bends and the average bill flatters the average saving.
       (user 2026-10-23: "we have 6month billls ... so the saving might be diff with
       proposal") */
    const [monthBills, setMonthBills] = useState(Array(12).fill(''));
    /* The kWh behind each month, when it is KNOWN rather than inferred. An interval file
       states it outright; a typed RM does not, and on MV the same RM means a different
       number of units every time MD or AFA changes. Storing it keeps the months stable
       against that — without it, months written at MD 870 quietly became nonsense at 904
       and inverted to 0 kWh, taking Average / Highest / Lowest / Yearly to RM 0 with
       them. null = infer from the RM, which is still right on a flat tariff.
       (user 2026-10-31: "still 0") */
    const [monthKwhs, setMonthKwhs] = useState(Array(12).fill(null));
    /* The bill can be ONE typical month or the real months, named. Named matters: "M4"
       means nothing to a customer checking their own statements, and a cycle that starts
       in a month other than January cannot be read off M1..M12 at all.
       (user 2026-10-23: "monthly bill can be put custom, then custom which month which
        month") */
    const [billMode, setBillMode] = useState('single');   // 'single' | 'custom'
    const setMonthBill = (i, v) => {
        setMonthBills(prev => prev.map((x, j) => (j === i ? v : x)));
        // Typed over by hand → the RM is now the only thing known about this month.
        setMonthKwhs(prev => prev.map((x, j) => (j === i ? null : x)));
    };
    /* Days per MONTH as an alternative to days per week. Same engine - it is converted
       back to a weekly figure - because two ways of SAYING the schedule must not become
       two ways of COMPUTING it. (user 2026-10-23: "can we do aso another onme which is
       how many days per month") */
    const [daysMode, setDaysMode] = useState('week');     // 'week' | 'month' | 'custom'
    /* CUSTOM WEEK - Mon-Fri, Saturday and Sunday each with their own hours, so a half-day
       Saturday stops being either a whole day or nothing.
       (user 2026-10-23: "can we have also mon-fri what time, then saturdayt what time.
        sunday what time. or half day") */
    const [weekPlan, setWeekPlan] = useState({
        wd: { on: true, start: 9, end: 17, days: 5 },
        sat: { on: false, start: 9, end: 13, days: 1 },
        sun: { on: false, start: 9, end: 13, days: 1 },
    });
    const setPlan = (k, patch) => setWeekPlan(p => ({ ...p, [k]: { ...p[k], ...patch } }));
    const [operatingDays, setOperatingDays] = useState(7);   // days per WEEK (default 7; × ~4.345 weeks = workdays/month)
    const [opStart, setOpStart] = useState(9);
    const [opEnd, setOpEnd] = useState(17);
    const [maxDemand, setMaxDemand] = useState('');
    // ── Medium Voltage: the bill is NOT bill ÷ rate ──────────────────────────────────────
    // An MV bill is energy (peak + off-peak kWh) PLUS capacity & network on the maximum
    // demand (RM/kW) — on a real 1,236 kW bill the MD part was 64% of the money. So for MV
    // the agent keys what the bill prints (peak kWh, off-peak kWh, MD) and the RM bill is
    // CALCULATED (`mvBillCalc`). Sizing and saving then run on an energy-only view:
    // `calcBill` = kWh × `calcRate` (the solar-weighted energy + KWTBB rate), so every
    // existing bill ÷ rate path recovers the TRUE kWh. The real bill (`realBill`) is what
    // the customer sees and what "% of bill" divides by. LV / Agriculture are unchanged:
    // calcBill = bill, calcRate = tariffRate. (user 2026-09-23)
    const [mvPeakKwh, setMvPeakKwh] = useState('');
    const [mvOffKwh, setMvOffKwh] = useState('');
    // ── BESS peak shaving (user 2026-09-25) ─────────────────────────────────────────────
    // Solar takes the ENERGY charge; only a battery can take the DEMAND charge, which is most
    // of an MV bill. It needs the raw 30-min readings — the shape decides how much a battery
    // can shave, so `lpRecords` is kept alongside the analysis. No load profile, no BESS.
    const [lpRecords, setLpRecords] = useState(null);
    const [bessOn, setBessOn] = useState(false);
    const [bessBrand, setBessBrand] = useState(BATT_BRAND_GOODWE);
    const [bessUnits, setBessUnits] = useState('');          // blank = use the best payback
    const [bessPrice, setBessPrice] = useState('');          // blank = the brand's list rate
    const [bessCredit, setBessCredit] = useState('90');      // % of shaved kW credited
    /* Every mode ends as ONE pair of numbers the engine already understands: days per
       week, and a window. Three ways to SAY a schedule, one way to compute it.

       The custom week expresses each group as an EQUIVALENT NUMBER OF FULL DAYS, weighted
       by its hours against the weekday window: a Saturday 09:00-13:00 beside a weekday
       09:00-17:00 is 4/8 = HALF a day, which is exactly what "half day" means to the
       person filling this in. Counting it as a whole day overstates the self-used share;
       counting it as nothing throws away a day of generation being consumed. The engine
       already accepts a fractional day count, so nothing downstream has to change. */
    const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    // The filled months, as numbers. Everything below keys off this one list.
    const filledMonthBills = monthBills
        .map(b => parseFloat(String(b ?? '').replace(/,/g, '')))
        .filter(b => Number.isFinite(b) && b > 0);
    const monthLabel = (i) => MONTH_NAMES[i % 12];
    const planHours = (g) => (g.on ? Math.max(0, g.end - g.start) : 0);
    const weekdayHours = Math.max(1, planHours(weekPlan.wd) || 8);
    const customDaysEq = ['wd', 'sat', 'sun'].reduce((acc, k) => {
        const g = weekPlan[k];
        return acc + (g.on ? g.days * (planHours(g) / weekdayHours) : 0);
    }, 0);

    /* HOW MUCH OF THE CUSTOM WEEK IS ACTUALLY IN THE SUN.
       (user 2026-10-23: "the method of calculation is per day calculation right? if
        saturday we half day")

       It is NOT a per-day simulation - the engine models one representative day and a day
       count - so a group whose hours sit outside 08:00-18:00 has to be handled here or it
       is silently treated as if it were midday. Saturday 09:00-22:00 is the case that
       exposes it: 13 open hours, but only 9 of them (09:00-18:00) can ever be served by
       solar. Counted on hours alone that evening load looks self-usable, which overstates
       the saving.

       So the week is weighted twice over: equivalent DAYS carry the volume (13h is 1.6
       weekdays of consumption), and this percentage carries how much of that volume falls
       under generation. The two together say what one representative day cannot. */
    const solarOverlapHours = (g) => (g.on ? Math.max(0, Math.min(g.end, 18) - Math.max(g.start, 8)) : 0);
    const customDayPct = (() => {
        let open = 0, sun = 0;
        ['wd', 'sat', 'sun'].forEach((k) => {
            const g = weekPlan[k];
            if (!g.on) return;
            open += g.days * planHours(g);
            sun += g.days * solarOverlapHours(g);
        });
        return open > 0 ? Math.max(0, Math.min(100, Math.round((sun / open) * 100))) : 100;
    })();

    const effectiveOperatingDays = daysMode === 'custom'
        ? Math.min(7, Math.max(0.1, customDaysEq || 0.1))
        : operatingDays;
    // The window follows the weekday pattern - it is where the bulk of the load sits, and
    // the shorter days are already accounted for in the equivalent-day count above.
    const effectiveOpStart = daysMode === 'custom' ? weekPlan.wd.start : opStart;
    const effectiveOpEnd = daysMode === 'custom' ? weekPlan.wd.end : opEnd;
    /* CUSTOM BILLS DRIVE THE SIZING. Without this the months were decoration: the
       package was still sized off whatever single figure was last typed, so filling in
       Jan-May changed the chart and nothing else.

       The AVERAGE is the right basis for sizing, because a system is sized once for the
       year - size it on the peak month and it is oversized for ten of them, size it on
       the trough and it never covers the business. The SAVING is a separate question,
       and the panel below answers it month by month rather than pretending the average
       month happens twelve times. (user 2026-10-23: "how do you plan to propose?") */
    useEffect(() => {
        if (billMode !== 'custom' || filledMonthBills.length === 0) return;
        const avg = filledMonthBills.reduce((a, b) => a + b, 0) / filledMonthBills.length;
        const rounded = String(Math.round(avg));
        setBill(prev => (prev === rounded ? prev : rounded));
        setBillKwh(_kwhFromBill(rounded, tariffRate));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billMode, JSON.stringify(filledMonthBills), tariffRate]);

    const isMv = isMvTariff(tariffRate);
    /* LV ToU is the OTHER time-of-use tariff. It shares MV's peak/off-peak inputs - both
       bills print the same two numbers - but almost nothing else: capacity and network are
       per kWh here, there is no demand charge, and there is a rebate line. `isTou` is only
       ever used to decide whether to ASK for peak/off-peak; what to DO with them stays
       split, so LV ToU can never pick up MV's maximum-demand charge.
       (user 2026-10-23: "u put into thje select and let us propose base on that") */
    const isLvTou = isLvTouTariff(tariffRate);
    const isTou = isMv || isLvTou;
    // MV: the bill is CALCULATED from the meter readings, so it must carry AFA like the real TNB
    // bill does — switch AFA on (current month's rate from General Settings) whenever MV is
    // chosen. The agent can still turn it off. LV keeps the No-AFA default. (user 2026-09-23)
    useEffect(() => {
        if (isTou) { setAfaOn(true); setAfaOnState(true); }
    }, [isTou]); // eslint-disable-line react-hooks/exhaustive-deps
    const _num = (v) => parseFloat(String(v ?? '').replace(/,/g, '')) || 0;
    const mvCtx = { peakKwh: _num(mvPeakKwh), offKwh: _num(mvOffKwh), mdKw: _num(maxDemand) };
    const mvKwh = mvCtx.peakKwh + mvCtx.offKwh;
    const mvBillCalc = isMv ? calcCommercialMvBill(mvCtx)
        : isLvTou ? calcCommercialLvTouBill(mvCtx) : null;
    /* All three branches now fold AFA in. The two ToU rates always did; the flat branch
       returned the bare tariff, which is what let `calcBill / calcRate` read the
       consumption behind an AFA-inclusive bill as if AFA were not on it. That single
       figure feeds sizing, both option cards and every saving, so the error was never
       confined to the kWh box. (user 2026-10-26) */
    const calcRate = isMv ? mvSolarRate() : isLvTou ? lvTouSolarRate() : lvFlatSolarRate(tariffRate);
    const calcBill = isTou ? (mvKwh > 0 ? String(Math.round(mvKwh * calcRate)) : '') : bill;
    const realBill = isTou ? (mvBillCalc?.total || 0) : (parseFloat(bill) || 0);
    /* `peakFrac` = the share of the REMOVED kWh that came off the peak line, worked out
       from this site's operating hours and days. Omit it and the tariff's own constant
       applies, which is the right fallback for callers with no schedule. (user 2026-10-31) */
    const billFn = (kwh, peakFrac) => (isMv ? calcCommercialMvBill(mvCtx, kwh, peakFrac ?? undefined)
        : isLvTou ? calcCommercialLvTouBill(mvCtx, kwh)
            : calcCommercialLvBill(kwh, tariffRate));
    // BESS: 261 kWh blocks (GoodWe) or 241 kWh (Huawei), both 125 kW per block. The battery is
    // charged off-peak / from surplus solar and discharged inside the 14:00–22:00 window to hold
    // the month's maximum demand down. Only offered on MV, and only with interval data.
    const bessCanOffer = isMv && Array.isArray(lpRecords) && lpRecords.length > 0;
    const bessUnitKwh = bessBrand === BATT_BRAND_HUAWEI ? 241 : 261;
    const bessUnitPrice = parseFloat(bessPrice) || (bessBrand === BATT_BRAND_HUAWEI ? 1350 : 1200);
    const bess = useMemo(() => {
        if (!bessOn || !bessCanOffer) return null;
        return analyseBess(lpRecords, {
            // Always show at least eight sizes; beyond that the ladder runs itself out
            // two units past the last one that pays back inside four years.
            minUnits: 8,
            capacityKwh: bessUnitKwh,
            creditFactor: Math.min(100, Math.max(1, parseFloat(bessCredit) || 90)) / 100,
            pricePerKwh: bessUnitPrice,
        });
    }, [bessOn, bessCanOffer, lpRecords, bessUnitKwh, bessUnitPrice, bessCredit]);
    // The chosen option: the agent's unit count, else the shortest payback.
    const bessPick = bess && bess.results.length
        ? (bess.results.find(r => r.units === (parseInt(bessUnits, 10) || 0)) || bess.best)
        : null;
    // Part B of the proposal (Part A when the battery is proposed on its own).
    const bessHtmlFor = (lang) => (bessPick ? buildBessSectionHtml(bessPick, {
        lang,
        partLetter: (Number(sel?.kwp) || 0) > 0 ? 'B' : 'A',
        customerName, dateIssued: todayDMY(),
        unitKwh: bessUnitKwh,
        demandRate: MV_DEMAND_RATE_PER_KW,
        creditPct: Math.round(parseFloat(bessCredit) || 90),
        brandLabel: bessBrand === BATT_BRAND_HUAWEI ? 'Huawei' : 'GoodWe',
        ladder: bess?.results || [],
        qualifyYears: bess?.qualifyYears || 4,
    }) : '');
    // For MV the typed-bill state follows the calculated bill, so every display / guard /
    // WhatsApp line that reads `bill` shows the real RM figure and the real kWh.
    useEffect(() => {
        if (!isTou) return;
        const b = realBill > 0 ? String(Math.round(realBill)) : '';
        const k = mvKwh > 0 ? String(Math.round(mvKwh)) : '';
        setBill(p => (p === b ? p : b));
        setBillKwh(p => (p === k ? p : k));
    }, [isTou, realBill, mvKwh]);
    // ── Imports: TNB e-bill (PDF) auto-fills usage/MD/tariff; load-profile (CSV/Excel) runs the
    // analysis and fills usage/MD (usage drives the calc via the kWh↔bill interlock). (user 2026-07-24)
    const [uploadMsg, setUploadMsg] = useState('');
    const [lpAnalysis, setLpAnalysis] = useState(null);
    /* SELF-HEAL THE MONTHS. Placed immediately AFTER lpAnalysis on purpose: a dependency
       array is evaluated DURING render, so referencing it from above throws "Cannot access
       'lpAnalysis' before initialization" and white-screens the whole tab — the same trap
       the operationWarranty effect below already carries a warning about. A month written before its kWh was stored — or by the old
       flat-rate formula — holds an RM that means nothing on a demand-charged tariff: RM
       84,357 for a 306,420 kWh month, against RM 89,346 of fixed charges alone, inverts
       to 0 kWh and takes Average / Highest / Lowest / Yearly to RM 0 with it.

       The interval file is still in memory and states each month's kWh outright, so the
       figures are recovered from it rather than demanding a re-upload. Only when NO month
       carries a kWh yet, so a hand-typed month is never overwritten.
       (user 2026-10-31: "why still 0 i really dont get it") */
    useEffect(() => {
        const whole = (lpAnalysis?.stats?.calendarMonths || []).filter((m) => m.days >= 28);
        if (whole.length < 2) return;
        if (monthKwhs.some((x) => Number(x) > 0)) return;
        setMonthKwhs((prev) => { const n = [...prev]; whole.forEach((m) => { n[m.month] = m.kwh; }); return n; });
        setMonthBills((prev) => { const n = [...prev]; whole.forEach((m) => { n[m.month] = _billFromKwh(m.kwh, tariffRate); }); return n; });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lpAnalysis, mvKwh, maxDemand, afaRateInput]);
    const handleTnbUpload = async (file) => {
        if (!file) return;
        setUploadMsg('Reading TNB bill…');
        try {
            const buf = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
            let text = '';
            for (let p = 1; p <= pdf.numPages; p++) { const pg = await pdf.getPage(p); const c = await pg.getTextContent(); text += ' ' + c.items.map(i => i.str).join(' '); }
            const r = parseTnbBill(text);
            const rate = r.tariffRate || tariffRate;
            if (r.tariffRate) setTariffRate(rate);
            if (isMvTariff(rate) && (r.peakKwh || r.offKwh)) {
                // MV: the bill is built from the meter's peak / off-peak kWh + MD, not bill ÷ rate.
                setMvPeakKwh(r.peakKwh ? String(Math.round(r.peakKwh)) : '');
                setMvOffKwh(r.offKwh ? String(Math.round(r.offKwh)) : '');
            } else if (r.monthlyUsage) { setBillKwh(String(Math.round(r.monthlyUsage))); setBill(_billFromKwh(r.monthlyUsage, rate)); }   // usage-driven (more accurate)
            if (r.md) setMaxDemand(String(Math.round(r.md)));
            /* The bill's OWN AFA, not this month's national one. Switch AFA on with it —
               a rebate the tool ignores leaves the calculated bill thousands above the
               customer's, and the sign is the part people get wrong by hand.
               (user 2026-10-31) */
            if (r.afaSen != null) { setAfaRateInput(String(r.afaSen)); setAfaOn(true); setAfaOnState(true); }
            setUploadMsg(r.filled
                ? `TNB bill read — ${r.voltage || ''} · usage ${r.monthlyUsage ? Math.round(r.monthlyUsage).toLocaleString() : '?'} kWh · MD ${r.md || '?'} kW${r.monthlyBill ? ` · avg bill RM ${Math.round(r.monthlyBill).toLocaleString()}` : ''}${r.afaSen != null ? ` · AFA ${r.afaSen} sen/kWh` : ''}. Check & adjust.`
                : 'Could not read this bill — enter manually.');
        } catch (e) { setUploadMsg('Could not read this PDF — enter the figures manually.'); }
    };
    const handleLoadProfileUpload = async (file) => {
        if (!file) return;
        setUploadMsg('Analysing load profile…');
        try {
            const buf = await file.arrayBuffer();
            const wb = XLSX.read(new Uint8Array(buf), { type: 'array' });
            const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: true });
            const recs = parseTnbRows(rows);
            if (!recs.length) { setUploadMsg('No 30-min interval data found — need a "kW Import" column.'); return; }
            const res = analyzeLoadProfile(recs, {});
            setLpAnalysis(res);
            setLpRecords(recs);   // the BESS simulation reads the raw interval readings
            const rate = tariffRate;
            setBillKwh(String(Math.round(res.stats.monthlyKwh)));
            setBill(_billFromKwh(res.stats.monthlyKwh, rate));
            if (res.stats.mdKw) setMaxDemand(String(Math.round(res.stats.mdKw)));

            /* TWO REAL MONTHS BEAT ONE AVERAGE. A 61-day export measures March and April
               separately; averaging them discards the only evidence in the file about how
               much this site moves month to month. Each COMPLETE month becomes its own
               custom bill, in its own calendar slot, and the tool reads them as a cycle.
               (user 2026-10-26: "from load profile is jst 2months" / "1. yes correct")

               A month needs 28 days of readings to count, so the partial month at either
               end of the range is left out - a 1-day stub of May is not a month, and
               entered as one it would read as a near-zero bill and drag the cycle down.

               One complete month is NOT enough: it would switch the tool to custom mode
               to say exactly what single mode already says, and lose the averaging that
               makes a part-month upload usable. */
            const COMPLETE_MONTH_DAYS = 28;
            const whole = (res.stats.calendarMonths || []).filter(m => m.days >= COMPLETE_MONTH_DAYS);
            let cycleNote = '';
            if (whole.length >= 2) {
                const next = Array(12).fill('');
                const nextKwh = Array(12).fill(null);
                whole.forEach((m) => { next[m.month] = _billFromKwh(m.kwh, rate); nextKwh[m.month] = m.kwh; });
                setMonthBills(next);
                setMonthKwhs(nextKwh);
                setBillMode('custom');
                cycleNote = ` · ${whole.length} whole months read as a cycle — `
                    + whole.map((m) => `${MONTH_NAMES[m.month]} ${Math.round(m.kwh).toLocaleString()} kWh`).join(', ');
            }
            setUploadMsg(`Load profile — ${res.stats.nDays} days · usage ~${Math.round(res.stats.monthlyKwh).toLocaleString()} kWh/mo · MD ${Math.round(res.stats.mdKw)} kW.${cycleNote}`);
        } catch (e) { setUploadMsg('Could not read this file — use a TNB 30-min interval CSV/Excel.'); }
    };
    // Mode AUTO from Max Demand: MD entered = ATAP (export, validated vs demand); blank =
    // SELCo (zero export). No checkbox (user 2026-06-23).
    const csMode = (parseFloat(maxDemand) || 0) > 0 ? 'atap' : 'selco';
    // (fullSelfUse flag removed — daytime % follows hours by default / manual override; the
    // 100%-self-use case is just daytime % = 100, see _fullSelf derivation below.)
    // Non-daytime usage % — the share of monthly kWh consumed OUTSIDE solar hours
    // (nights, weekends, non-operating days) that is ALWAYS grid-imported. Solar
    // can't cover it, so there's always a residual bill (export only offsets part).
    const [nonDayPct, setNonDayPct] = useState('20');   // legacy (kept for draft back-compat); calc uses _offHour
    // Daytime usage % — the share of consumption during solar hours. Default 100%
    // (typical commercial: all usage in the morning/daytime). Editable in manual mode;
    // when "follow operating hours" is on it auto-derives from the operating window.
    // (user 2026-06-23, replaces the inverted Non-daytime field)
    const [dayUsagePct, setDayUsagePct] = useState('');   // blank = follow operating hours; type to override
    const [brand, setBrand] = useState('goodwe');

    // Which option (A or B) the Section-2 Proposal + Section-3 Quotation/Forms/Create
    // Sales are generated for. Chosen via the chooser at the top of Section 2 (RSC
    // parity — choose the option, the package below follows it). Was hardcoded to 'a'
    // (user 2026-06-22). Each option keeps its own basis/battery/discount/add-ons.
    const [copied, setCopied] = useState(false);
    // Which part the WhatsApp message covers: 'a' = the solar system, 'b' = the BESS peak
    // shaving on its own, 'ab' = both with a combined total. Only offered once a BESS is
    // actually proposed; a solar-only site never sees the chooser. (user 2026-09-26)
    const [waPart, setWaPart] = useState('ab');
    // Option A retired (user 2026-06-29): only Option B (with battery) is shown + used.
    const [selectedOpt, setSelectedOpt] = useState('b');

    // ── Per-card add-on catalog (ported, simplified, from RSC AI BESS 360) ──
    // `addons` = shared catalog; `cscAddons` = per-option-key map of picked rows
    // ('a' / 'b'). Each card gets its own editable add-on table; rows fold into
    // that card's price + (for the SELECTED option) the 3 generated outputs.
    const [addons, setAddons] = useState([]);
    const [cscAddons, setCscAddons] = useState({});
    useEffect(() => {
        let c = false;
        getAddons().then(r => { if (!c) setAddons(r.data || []); }).catch(() => {});
        return () => { c = true; };
    }, []);

    // Pricing data (ATAP list + battery options). Placed ABOVE the add-on/sizing helpers so
    // atapPrices is defined before they run (no TDZ). batteryOptions drives the data-driven
    // CSC battery price. (moved + extended 2026-08-03)
    const { atapPrices, batteryOptions } = useRscPricing('Residential Pricing', sharedPhaseMode, 999);
    // Data-driven LV battery NET price = Battery Pricing gross (16 kWh cashEach, e.g. 8600) − RM 800
    // promo, so an admin price change flows into CSC with no code edit.
    useEffect(() => {
        const gross = batteryOptions?.find(b => Number(b.kwhEach) === 16)?.cashEach;
        // Promo comes from the RSC constant, not a literal: it moved 800 -> 1,200 and CSC
        // kept subtracting 800, so a 16kWh battery priced 7,800 in CSC while RSC showed
        // 7,400. Importing it means the next change flows through on its own.
        if (gross > 0) setLvBattUnitNet(gross - PER_BATTERY_PROMO);
    }, [batteryOptions]);

    // Residential band: the ATAP inverter is bumped to clear the 1.35 ratio; surface the
    // upgrade as ONE auto add-on row — a clean "Inverter Upgrade (NkW)" label (final kW, never
    // "3p5 to 3p8") with the chained catalog cost — so it appears in the ADD-ONS list AND the
    // "+ Add-ons" price-breakdown line. Computed, not stored (can't be edited/removed; always
    // tracks the current size). (user 2026-07-04)
    const autoInvUpgRows = (key) => {
        const card = key === 'a' ? optA : optB;
        if (!card) return [];
        const s = sysFor(key, card);
        const kwpN = Number(s.kwp) || 0;
        // ≤ 30 kWp = the ATAP-residential band. Tied to the same boundary as the pricing and
        // the Astern discount: the inverter upgrade and the brand adjustment below are both
        // corrections to a RESIDENTIAL list price, so they have to cover exactly the range
        // that uses one. (was <26, stale after the boundary moved to 30 on 2026-10-14)
        if (!(kwpN > 0 && kwpN <= 30)) return [];
        const rows = [];
        const upg = commercialInverterUpgrade(kwpN, Number(s.panels) || 0, { phaseMode: sharedPhaseMode, addonCatalog: addons, rows: atapPrices });
        if (upg.cost > 0) rows.push({ itemName: `Inverter Upgrade (${upg.kw}kW)`, price: String(upg.cost), rowId: `auto-inv-${key}`, _qty: 1, _autoInvUpg: true });
        // Residential-band brand STRING adjustment (Solis −2000, Huawei −500) — shown UNDER
        // the inverter (adds to total as a negative, not an add-on-column row). (user 2026-08-04)
        const _adj = BRAND_STRING_ADJ_SMALL[s.brand];
        if (_adj != null) rows.push({ itemName: `${s.brand === 'huawei' ? 'Huawei' : 'Solis'} Inverter`, price: String(_adj), rowId: `auto-brand-${key}`, _qty: 1, _autoInvUpg: true });
        return rows;
    };
    const addonsForKey = (key) => (cscAddons[key] || []).concat(autoInvUpgRows(key));
    const addAddonRow = (key, addonId) => {
        const cat = (addons || []).find(a => a.addonId === addonId);
        if (!cat) return;
        const rowId = `r-${addonId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        // A PerKwp add-on opens at THIS card's system size, so RM/kWp x kWp is right
        // the moment it is added; still editable. (user 2026-09-28)
        const _sysKwp = Number(sysFor(key, key === 'b' ? optB : optA)?.kwp) || 0;
        const row = { ...cat, rowId, _qty: defaultQtyFor(cat.unitType, { kwp: _sysKwp }) };
        setCscAddons(prev => ({ ...prev, [key]: [...(prev[key] || []), row] }));
    };
    const updateAddonRow = (key, rowId, patch) => setCscAddons(prev => ({ ...prev, [key]: (prev[key] || []).map(r => r.rowId === rowId ? { ...r, ...patch } : r) }));
    const removeAddonRow = (key, rowId) => setCscAddons(prev => ({ ...prev, [key]: (prev[key] || []).filter(r => r.rowId !== rowId) }));
    const addonTotalForKey = (key) => addonsForKey(key).reduce((sum, a) => {
        const qty = (isQtyBearing(a.unitType))
            ? (a._qty != null && a._qty !== '' && Number.isFinite(Number(a._qty)) ? Math.max(0, Number(a._qty)) : 1) : 1;
        return sum + computeAddonAmount(a, qty).total;
    }, 0);

    // ── Per-card battery / discount / O&M (RSC-style, each option carries its own) ──
    // Battery: free-type kWh, <100 snaps UP to nearest 16-kWh unit, ≥100 as typed
    // (same method as the old shared field). Discount: RM or % on the PANEL system
    // only (excludes battery + add-ons), mirroring RSC. O&M: annual maintenance fee.
    // Option B = Max MD: defaults to ONE 16 kWh battery (per user — the max-saving
    // option always proposes at least 1 battery to capture peak surplus). Editable;
    // clearing to 0 sticks. Option A (~50%) defaults to no battery.
    const [cscBattery, setCscBattery] = useState({ a: '', b: '' });   // no battery by default (manual stepper)

    /* THE CARD'S BATTERY IS OFF WHILE BESS IS ON, everywhere - priced, modelled and
       quoted - not merely greyed on screen. Panels are one solution and BESS peak
       shaving is another; the card's storage battery is a third thing, and an MV
       proposal that already carries a BESS should not quietly carry it too.
       (user 2026-10-26: "its diff, shd be two type ... for MV, battery is one
       solution, panels is one solution")

       DERIVED, not written back. A value typed before BESS was ticked is kept in
       state and comes back when BESS is unticked - and because every consumer reads
       through here, it cannot sit in cardPricing or in the SARE add-on cost as a
       charge the agent can no longer see. The snapshot and the restore keep storing
       the raw value for the same reason. */
    const _bessOwnsBattery = bessOn && bessCanOffer;
    const battKwhFor = (key) => (_bessOwnsBattery ? '' : cscBattery[key]);
    const [cscDisc, setCscDisc] = useState({});   // { a:{rm,pct}, b:{rm,pct} }
    // "Deduct SST" — per option: { on, amt }. on+blank amt = full SST offset; a typed
    // amt = partial. Folds into the system discount so it flows through FinalPrice →
    // commission (Astern + Agent bear it 50/50). Source of truth for create-sales.
    const [cscSstDeduct, setCscSstDeduct] = useState({});   // { b:{ on:false, amt:'' } }
    const [cscOm, setCscOm] = useState({ a: '', b: '' });
    const [cscOmPkg, setCscOmPkg] = useState({ a: 'premium', b: 'premium' });   // 'basic' | 'premium'
    // Battery BRAND per card (user 2026-09-21): 'goodwe' (default — every existing ladder and
    // price) or 'huawei' (241-kWh blocks @ RM1,350/kWh). Switching snaps the size onto the new
    // brand's ladder, so a card never carries a size the chosen brand doesn't sell.
    const [cscBattBrand, setCscBattBrand] = useState({ a: BATT_BRAND_GOODWE, b: BATT_BRAND_GOODWE });
    const battBrandFor = (key) => cscBattBrand[key] || BATT_BRAND_GOODWE;
    const setCardBattBrand = (key, brand) => {
        setCscBattBrand(prev => ({ ...prev, [key]: brand }));
        const snapped = snapBattToBrand(cscBattery[key], brand);
        setCardBattery(key, snapped > 0 ? String(snapped) : '', brand);
    };
    // Raw write while typing (no rounding) so digits don't jump mid-edit.
    const setCardBatteryRaw = (key, v) => setCscBattery(prev => ({ ...prev, [key]: v }));
    // Stepper commits a valid ladder value (no snap needed). Battery present → set the inverter
    // type by product: LV (<100 kWh) → GoodWe Hybrid (DC-coupled); HV (≥100 kWh) → String
    // (AC-coupled). Reprice for the resulting type. (user 2026-07-10)
    const setCardBattery = (key, v, battBrand = battBrandFor(key)) => {
        setCscBattery(prev => ({ ...prev, [key]: v }));
        const n = parseFloat(v) || 0;
        if (n > 0) {
            const s = sysFor(key, optA);
            const k = Number(s.kwp) || 0;
            // A Huawei battery is always an HV, AC-coupled product → String inverter.
            const type = (battBrand === BATT_BRAND_HUAWEI || isHvBattery(n)) ? 'string' : 'hybrid';
            const brand = type === 'hybrid' ? 'goodwe' : (s.brand || 'goodwe');
            const ppk = commercialPricePerKwp(k, brand, type, 0);
            if (ppk > 0 && k > 0) setCscSys(prev => ({ ...prev, [key]: { ...(prev[key] || {}), brand, type, pricePerKwp: String(ppk), systemCost: String(Math.round(ppk * k)) } }));
        }
    };
    const discFor = (key) => cscDisc[key] || {};
    const setCardDisc = (key, patch) => setCscDisc(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
    const sstDeductFor = (key) => cscSstDeduct[key] || {};
    const setCardSstDeduct = (key, patch) => setCscSstDeduct(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
    const setCardOm = (key, v) => setCscOm(prev => ({ ...prev, [key]: v }));
    // Switching package re-seeds O&M from that package's tier (clears manual override).
    const setCardOmPkg = (key, pkg, kwp) => {
        setCscOmPkg(prev => ({ ...prev, [key]: pkg }));
        setCscOm(prev => ({ ...prev, [key]: '' }));   // '' = follow the tier default for the new pkg
    };

    // Per-card system details. Each option owns its own; overrides default to the
    // option's auto sizing (card.kwp/panels/inverterKw/brand/cashPrice). O&M defaults
    // from the kWp tier table (omForKwp) until manually overridden.
    const [cscSys, setCscSys] = useState({ a: {}, b: {} });
    const setSysField = (key, field, value) => setCscSys(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));
    // Re-size on any sizing-input change: drop the manual panel / inverter / cost
    // overrides so a new bill (or operating-days / hours / daytime / MD) RE-PROPOSES the
    // system instead of sticking at the previously-edited panel count. Brand / panel-watt
    // / price-per-kWp are kept. (user 2026-06-24 — "after I edit panels, change bill, it stuck")
    const _firstSizeRef = useRef(true);
    // True for the duration of a draft restore — the restore re-sets bill/operating-days
    // etc., which would otherwise re-trigger this strip and wipe the restored manual
    // panel/inverter overrides (user 2026-06-29: "changed to 151 panels, refresh → gone").
    const _draftRestoring = useRef(false);
    useEffect(() => {
        if (_firstSizeRef.current) { _firstSizeRef.current = false; return; }   // keep restored draft on mount
        if (_draftRestoring.current) return;   // don't strip overrides while a saved draft is restoring
        setCscSys(prev => {
            // pricePerKwp goes with systemCost — keeping one and dropping the other is what let
            // PER KWP and PANEL TOTAL disagree after a bill change. (user 2026-09-25)
            const strip = (o) => { if (!o) return {}; const { panels, inverterKw, systemCost, pricePerKwp, kwp, ...rest } = o; return rest; };
            return { a: strip(prev.a), b: strip(prev.b) };
        });
        // Reset Option A's battery to its default so it recomputes with the new bill —
        // Option B's battery auto-recomputes via its own effect. (user 2026-06-24)
        // Small commercial bills (≤ RM1,500) suggest at least 1 battery unit (16 kWh):
        // a low-daytime-usage site has lots of daytime surplus, so a battery to shift it
        // to the night baseline is worthwhile. Larger bills stay battery-manual.
        // (user 2026-07-22)
        const _billA = realBill;
        const _defBattA = (_billA > 0 && _billA <= 1500) ? '16' : '';
        setCscBattery(prev => (prev.a === _defBattA ? prev : { ...prev, a: _defBattA }));
        // The 16-kWh default is a GoodWe unit — Huawei has no 16-kWh size.
        setCscBattBrand(prev => (prev.a === BATT_BRAND_GOODWE ? prev : { ...prev, a: BATT_BRAND_GOODWE }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bill, mvPeakKwh, mvOffKwh, tariffRate, effectiveOperatingDays, opStart, opEnd, dayUsagePct, maxDemand]);
    const sysFor = (key, card) => {
        const o = cscSys[key] || {};
        // Panels is the source of truth; kWp is ALWAYS derived = panels × (watt/1000).
        // Derive panels from the card's kWp (round UP to a whole panel), then snap kWp
        // back so it's an exact panel multiple. o.kwp is never stored / read.
        const panelWatt = o.panelWatt != null ? o.panelWatt : '650';
        const wpk = (Number(panelWatt) || 650) / 1000;
        const panelsNum = o.panels != null ? Number(o.panels)
            : (card?.kwp != null ? Math.ceil(Number(card.kwp) / wpk) : (card?.panels != null ? Number(card.panels) : 0));
        const panels = panelsNum ? String(panelsNum) : '';
        const kwp = panelsNum ? String(+(panelsNum * wpk).toFixed(2)) : (card?.kwp != null ? String(card.kwp) : '');
        let inverterKw = o.inverterKw != null ? o.inverterKw : (card?.inverterKw != null ? String(card.inverterKw) : '');
        // Brand↔type link (user 2026-08-04): GoodWe = Hybrid only (battery systems), Solis/Huawei
        // = String only. Default a no-battery system to Solis (String); battery → GoodWe (Hybrid).
        let brand = o.brand != null ? o.brand : 'goodwe';
        let type  = o.type  != null ? o.type  : (brand === 'goodwe' ? 'hybrid' : 'string');
        // A battery only ever runs on ITS OWN brand of inverter: GoodWe battery → GoodWe,
        // Huawei battery → Huawei (user 2026-09-26). The Huawei battery used to keep whatever
        // inverter brand the card carried, which could quote a GoodWe inverter beside a Huawei
        // pack. Huawei's is HV / AC-coupled, so it stays String; GoodWe's is DC-coupled Hybrid.
        if (Number(battKwhFor(key)) > 0) {
            if (battBrandFor(key) === BATT_BRAND_HUAWEI) { brand = 'huawei'; type = 'string'; }
            // GoodWe LV (<100 kWh) is DC-coupled Hybrid; HV (>=100 kWh) is AC-coupled String.
            // This pinned EVERY GoodWe battery to Hybrid, so a 261 kWh HV pack showed Hybrid
            // selected while the card was priced at the String rate, and the Hybrid button
            // was disabled underneath it — clicking String could not settle. (user 2026-09-26)
            else { brand = 'goodwe'; type = isHvBattery(battKwhFor(key)) ? 'string' : 'hybrid'; }
        }
        if (brand === 'huawei' || brand === 'solis') type = 'string';
        // GoodWe now does BOTH String and Hybrid (user 2026-08-19). Only the residential-band
        // row is still Hybrid-only — that band prices off the residential row and
        // carries the Solis/Huawei small-system adjustment, so it is left untouched.
        if (brand === 'goodwe' && (Number(kwp) || 0) <= 30) type = 'hybrid';
        // A >60 kW inverter can't be Hybrid (60 kW hybrid stack ceiling) → String, AC-coupled.
        if (exceedsHybridMax(Number(kwp) || 0, RATIO_CAP_NO_BATT)) type = 'string';
        // GoodWe String is a valid combination as of 2026-08-19, so a String system
        // no longer gets pushed onto Solis. In the residential band the old rule still applies.
        if (type === 'string' && brand === 'goodwe' && (Number(kwp) || 0) <= 30) brand = 'solis';
        // Auto price follows THIS CARD's brand / type, not the tab-level brand the sizing hook
        // priced with. Falling back to card.cashPrice quoted a Huawei card at the GoodWe rate:
        // a 104 kWp Huawei card showed PER KWP RM1,800 with PANEL TOTAL RM176,800 (= 104 ×
        // 1,700). It surfaced after a bill change, because the re-size strips systemCost but
        // keeps the brand — so the two fields drifted apart. Both now come from one figure.
        // (user 2026-09-25 — "same details, diff bill, come out diff package price")
        let systemCost = o.systemCost != null ? o.systemCost : (card?.cashPrice != null ? String(Math.round(card.cashPrice)) : '');
        let pricePerKwp = o.pricePerKwp != null ? o.pricePerKwp : ((card?.cashPrice && card?.kwp) ? String(Math.round(card.cashPrice / card.kwp)) : '');
        if (o.systemCost == null) {
            const _k = Number(kwp) || 0;
            // The residential band prices off the ATAP row (handled by the hook's cashPrice), so
            // only the commercial band is re-priced here.
            const _ppk = _k > 30 ? commercialPricePerKwp(_k, brand, type, 0) : 0;
            if (_ppk > 0) {
                pricePerKwp = String(_ppk);
                systemCost = String(Math.round(_ppk * _k));
            }
        }
        // Residential band: bump the ATAP inverter to clear the 1.35 commercial ratio — the inverter
        // field shows the final (upgraded) kW. systemCost stays the RAW ATAP price (so PANEL
        // TOTAL == price-card "System base"); the upgrade COST is surfaced as an auto add-on
        // row (see autoInvUpgRows) that appears in the ADD-ONS list and the "+ Add-ons"
        // breakdown line. (user 2026-07-04)
        const _kwpNum = Number(kwp) || 0;
        if (_kwpNum > 0 && _kwpNum <= 30) {   // residential-priced band — same boundary as the pricing
            inverterKw = String(commercialInverterUpgrade(_kwpNum, panelsNum, { phaseMode: sharedPhaseMode, addonCatalog: addons, rows: atapPrices }).kw);
        }
        const addOn = o.addOn != null ? o.addOn : '';
        // O&M defaults from the kWp tier for the chosen package (basic/premium);
        // a manual entry in cscOm overrides it.
        const omPkg = cscOmPkg[key] || 'premium';
        const om = (cscOm[key] != null && cscOm[key] !== '') ? cscOm[key] : String(omForKwp(kwp, omPkg));
        return { kwp, panels, panelWatt, inverterKw, brand, type, systemCost, pricePerKwp, addOn, om, omPkg };
    };

    // ---- Shared Project Info form state (mirrors cscProposalTab.jsx for Task 5 reuse) ----
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerAddress, setCustomerAddress] = useState('');
    const [siteState, setSiteState] = useState('');
    const [sitePhotos, setSitePhotos] = useState([]);
    const [includeOutright, setIncludeOutright] = useState(true);
    const [includeSARE, setIncludeSARE] = useState(true);
    // 10-year SARE term — independent toggle, defaults on alongside the 20-year.
    const [includeSARE10, setIncludeSARE10] = useState(true);
    /* A third term at +4 sen — half the premium of the 10-year for half the term
       reduction. Off by default: three SARE tables in one deck is a lot of paper, so
       it is opt-in rather than something a salesperson has to remember to untick.
       (user 2026-10-23: "now i wan 15years aso, +0.04 sare rate from 20years") */
    const [includeSARE15, setIncludeSARE15] = useState(false);
    const [includeLoan, setIncludeLoan] = useState(true);
    const [includeCA, setIncludeCA] = useState(true);
    const [includeITA, setIncludeITA] = useState(false);
    const [itaTouched, setItaTouched] = useState(false);
    const [sareTariffOverride, setSareTariffOverride] = useState('');
    /* 15 and 10 year rates are now their own overrides, not premiums off the 20-year.
       The rate card gives each term its own number per band, so moving the 20-year must
       no longer drag the others with it.
       (user 2026-10-23: "each 15,10,20years sare rate can you allow editable?") */
    const [sareTariffOverride15, setSareTariffOverride15] = useState('');
    const [sareTariffOverride10, setSareTariffOverride10] = useState('');
    const [sareTouched, setSareTouched] = useState(false);
    const [sareIncludeTouched, setSareIncludeTouched] = useState(false); // user manually toggled the SARE checkbox
    const [loanPrincipal, setLoanPrincipal] = useState('');
    const [loanRate, setLoanRate] = useState('5.0');
    const [loanTermMonths, setLoanTermMonths] = useState('84');
    // Loan TERMS for the proposal — 3 / 5 / 7 years, any combination, so they can be
    // compared in one deck. (user 2026-10-15: "can we select also 3,5,7 years loan either
    // one? or choose any two or 3 aso can as comparison")
    //
    // Each term keeps its OWN rate, because a longer tenure is usually priced higher —
    // the Astern hire-purchase sheet runs 3.6 / 3.6 / 4.0%. They all open at the single
    // Loan Rate below, so leaving them alone gives the shared-rate case.
    //
    // None ticked = the proposal behaves exactly as it does today, one loan on the single
    // Loan Term field. The feature is additive; nothing existing changes until it is used.
    const [loanTermPick, setLoanTermPick] = useState({ 36: false, 60: false, 84: false });
    // Pre-filled, not blank, so the rate is VISIBLE and can be adjusted rather than guessed
    // at. (user 2026-10-15: "the yearly rate can put by default an amount so we can adjust
    // aso?") A longer tenure is normally priced higher, which is why 7 years opens above
    // the other two — the Astern hire-purchase sheet runs 3.6 / 3.6 / 4.0 on the same shape.
    const LOAN_TERM_DEFAULT_RATE = { 36: '3.6', 60: '3.6', 84: '4.0' };
    const [loanTermRate, setLoanTermRate] = useState({ ...LOAN_TERM_DEFAULT_RATE });
    // Is the rate typed above a FLAT (hire-purchase) rate or an EFFECTIVE (reducing-balance)
    // one? Flat is converted to its effective twin inside the maths, so the 20-year table
    // keeps running on a single kind of rate. (user 2026-10-16)
    const [loanRateMode, setLoanRateMode] = useState('flat');

    /* Flipping Flat <-> Effective CONVERTS what is in the boxes; it does not relabel it.
       3.6 read as effective rather than flat is a different, much cheaper loan - RM 4,990
       a month against RM 5,232 on RM 170,000 over 3 years - so a toggle that left the
       numbers alone would silently reprice the deal. Rounded to 3 decimals, which holds
       the instalment to within a few sen and round-trips back to the number typed.
       (user 2026-10-16: "default effective rate shd be converted right") */
    const switchLoanRateMode = (next) => {
        if (next === loanRateMode) return;
        const convert = next === 'effective' ? effectiveRateFromFlat : flatRateFromEffective;
        setLoanTermRate(prev => {
            const out = { ...prev };
            for (const m of Object.keys(out)) {
                const pct = parseFloat(out[m]);
                if (!isFinite(pct) || pct <= 0) continue;      // blank stays blank
                out[m] = String(parseFloat((convert(pct / 100, Number(m) / 12) * 100).toFixed(3)));
            }
            return out;
        });
        setLoanRateMode(next);
    };
    const loanTermsSelected = useMemo(() => [36, 60, 84]
        .filter(m => loanTermPick[m])
        .map(m => ({
            months: m,
            rate: (parseFloat(loanTermRate[m]) || parseFloat(loanRate) || 5) / 100,
            flat: loanRateMode === 'flat',
        })), [loanTermPick, loanTermRate, loanRate, loanRateMode]);
    const [sales, setSales] = useState({ name: '', title: 'Solar Consultant', email: '', whatsapp: '', photoUrl: '' });

    // ── Commercial-form fields (feed ATAP Contract / Declaration / Insurance) ──
    // Company details
    const [refNo, setRefNo] = useState('');
    const [companyTin, setCompanyTin] = useState('');
    const [companyOffice, setCompanyOffice] = useState('');
    const [companyFax, setCompanyFax] = useState('');
    const [companyEmail, setCompanyEmail] = useState('');
    const [companyWebsite, setCompanyWebsite] = useState('');
    // PIC (person-in-charge)
    const [picSalutation, setPicSalutation] = useState('');
    const [picName, setPicName] = useState('');
    const [picMykad, setPicMykad] = useState('');
    const [picPosition, setPicPosition] = useState('');
    const [picCitizenship, setPicCitizenship] = useState('');
    const [picMobile, setPicMobile] = useState('');
    const [picEmail, setPicEmail] = useState('');
    // Authorised / alternate person
    const [altSalutation, setAltSalutation] = useState('');
    const [altName, setAltName] = useState('');
    const [altMykad, setAltMykad] = useState('');
    const [altPosition, setAltPosition] = useState('');
    const [altRelationship, setAltRelationship] = useState('');
    const [altCitizenship, setAltCitizenship] = useState('');
    const [altMobile, setAltMobile] = useState('');
    const [altEmail, setAltEmail] = useState('');
    // Warranty / terms
    const [operationWarranty, setOperationWarranty] = useState(DEFAULT_WARRANTY_TERM);
    // Set once the agent picks a Cleaning themselves; until then it simply follows
    // the system size. (user 2026-10-26: "more than 30kwp got free 1 time cleaning")
    const [cleaningTouched, setCleaningTouched] = useState(false);
    // The effect that keeps this honest across 30 kWp lives BELOW, next to `sel` — see
    // "Keep the warranty honest". It cannot sit here: its dependency array reads sel?.kwp,
    // dependency arrays are evaluated during render, and `sel` is not declared until much
    // further down. That is a temporal dead zone, which optional chaining does NOT protect
    // against — `sel?.kwp` throws just as hard as `sel.kwp` when `sel` is still in TDZ.
    const [defectWarranty, setDefectWarranty] = useState('1 set');
    const [insuranceAmount, setInsuranceAmount] = useState('');

    // ── Prepared-By agent picker (Group → Sales Agent Name → auto Position) ──
    // Defaults to the logged-in agent's group + the agent themselves, but is
    // editable. Picking an agent sets sales.name + sales.title (which the 3
    // generators already read). Referral-role agents are filtered out.
    const [agentGroups, setAgentGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [groupAgents, setGroupAgents] = useState([]);
    const [selectedAgentId, setSelectedAgentId] = useState('');
    const filterAgents = (list) => (list || []).filter(a => !(a.userRoles || []).some(ur => ur.roleName === 'Referral'));
    // Proposal / template path uses the legacy 'en'/'cn'/'my' codes (template files
    // are astern-proposal-csc-cn.html / -my.html). The header prop `lang` is
    // 'en'/'zh'/'ms', so map it once here. WhatsApp keeps the raw prop `lang`.
    const proposalLang = lang === 'zh' ? 'cn' : lang === 'ms' ? 'my' : 'en';

    // Effective daytime usage % → off-hour baseline + the 100%-self-use flag the saving
    // calc consumes. Manual mode (default): the typed daytime %. "Follow operating hours"
    // mode (!fullSelfUse): derive it from the operating window's overlap with the solar
    // window (08:00–18:00). 100% daytime in manual mode ⇒ full self-use. (user 2026-06-23)
    // Daytime usage % FOLLOWS the operating hours by default = operating-hours-within the
    // solar window (08:00–18:00) ÷ operating hours (9–17 = 8÷8 = 100%). A manually typed
    // dayUsagePct overrides it; clear the field to go back to following. No checkbox.
    // Operating DAYS aren't mixed into the % — weekends are full export, handled by the
    // saving calc's off-days (user 2026-06-23).
    const _computedDayPct = daysMode === 'custom'
        ? customDayPct
        : (() => { const oh = Math.max(0, opEnd - opStart); if (oh <= 0) return 100; const ov = Math.max(0, Math.min(opEnd, 18) - Math.max(opStart, 8)); return Math.max(0, Math.min(100, Math.round(ov / oh * 100))); })();
    /* A manual override is IGNORED in custom mode. It was typed against a single window
       that custom no longer uses, and leaving it applied would silently override a figure
       the agent can no longer see. (user 2026-10-23: "the day time usage i think u shd
       ignore it if custom") */
    const _effDayPct = (daysMode !== 'custom' && String(dayUsagePct).trim() !== '')
        ? Math.max(0, Math.min(100, parseFloat(dayUsagePct) || 0))
        : _computedDayPct;
    const _offHour = Math.max(0, Math.min(1, (100 - _effDayPct) / 100));
    // Always use the bell-curve SELCo model (battery-aware) instead of the optimistic
    // "100% self-use" override — so a battery genuinely raises the saving and the figure
    // reflects real midday curtailment. (user 2026-07-03)
    const _fullSelf = false;
    // When a manual daytime % is typed, nudge the operating window (opEnd) so the hours
    // roughly reflect it — a rough estimate is fine (user 2026-06-23). Empty = follow (no
    // reverse sync). ≥100% leaves the window (it's already all-daytime).
    // Suggest an operating window for a daytime %: daytime% = 10 solar-hours ÷ total hours.
    // Anchor on the solar core 08:00–18:00, extend the EVENING close first, then pull the
    // MORNING open earlier once evening hits midnight. Below ~42% the window is a full 24h
    // and the % just reflects a genuinely night-heavy load. (user 2026-07-25)
    const syncHoursFromDayPct = (raw) => {
        const s = String(raw).trim();
        if (s === '') return;
        const d = Math.max(1, Math.min(100, parseFloat(s) || 0));
        if (d >= 100) { setOpStart(8); setOpEnd(18); return; }
        const total = Math.min(24, Math.round(10 / (d / 100)));   // total operating hours for this %
        let start = 8, end = 8 + total;
        if (end > 24) { start = Math.max(0, 8 - (end - 24)); end = 24; }
        setOpStart(start); setOpEnd(end);
    };

    const { proposals, candidates, snap96, breakdown: sizingBreakdown } = useCscBillProposal({
        residentialRows: atapPrices,
        monthlyBill: parseFloat(calcBill) || 0,
        tariffRate: calcRate, operatingDays: effectiveOperatingDays, opStart: effectiveOpStart, opEnd: effectiveOpEnd,
        maxDemandKw: parseFloat(maxDemand) || 0,
        phaseMode: sharedPhaseMode, mode: csMode, brand,
        invType: (parseFloat(battKwhFor('a')) > 0) ? 'hybrid' : 'string',
        // Battery feeds the SELCo storage model (excess solar stored vs curtailed).
        battKwhTotal: parseFloat(battKwhFor('a')) || 0,
        // Non-daytime baseline (nights/weekends/non-operating days) is ALWAYS imported —
        // solar can't cover it, so the bill never reaches RM 0 unless "100% self-use" is on.
        offHourBaselinePct: _offHour,
        fullSelfUse: _fullSelf,
    });

    // Two fixed option cards (no toggle): A = ~50% bill saving, B = Max (MD-sized).
    const [optBasis, setOptBasis] = useState({ a: 'p50', b: 'maxmd' });
    const BASIS_LABEL = { p50: '~50% Bill Saving', p70: '~70% Bill Saving', p100: '100% Coverage', maxmd: 'Max', selco: 'SELCo (Zero Export)' };
    const BASIS_LABEL_ZH = { p50: '约省 50% 电费', p70: '约省 70% 电费', p100: '100% 覆盖', maxmd: '最大', selco: 'SELCo (零出口)' };
    const basisLabel = (b) => (lang === 'zh' ? (BASIS_LABEL_ZH[b] || BASIS_LABEL[b]) : BASIS_LABEL[b]);
    // The "Max" option uses the SAME sizing method in both modes (per user): the
    // covering + 72 kW-cap + fill-to-1.35 logic (maxMdCard). With no MD entered,
    // SELCo and ATAP size identically — e.g. a 6k bill → 96 kWp, a 7k bill → 134 kWp.
    // Normalise a stored basis to the currently-valid set so flipping the mode
    // never leaves a dangling selection.
    const isAtap = csMode === 'atap';
    const normBasis = (b) => isAtap
        ? (b === 'p100' || b === 'selco' ? 'maxmd' : b)
        : (b === 'selco' ? 'maxmd' : b);
    // Smallest NEM system whose monthly saving covers ≥ frac of the original TNB
    // bill; falls back to the candidate closest to the target. Returns the
    // SMALLEST system that hits the target — NO inverter-fill / ballooning.
    // (Per user: stop over-designing — a RM350 bill must not propose 20 kWp.)
    // pickPct / fillInverter / snapNear96 / targetSavingForBill / pickByGross /
    // billTargetCard now live in ../../utils/cscSizing (shared with cscMeterRow.jsx —
    // see import above). `sizingCtx` bundles the closed-over values they need.
    // SELCo-only daytime downsizing: shrink the recommended kWp toward the daytime self-use
    // when there's no battery (surplus would be curtailed). ATAP / battery keep full size.
    // (user 2026-08-02)
    const sizingCtx = { candidates, bill: calcBill, tariffRate: calcRate, mode: csMode, daytimeFrac: 1 - _offHour, hasBattery: (parseFloat(battKwhFor('a')) || 0) > 0 };

    // MAX MD caps at the SAVING PLATEAU — the smallest system that already reaches
    // peak monthly saving. Beyond it, extra panels just curtail / hit the export
    // cap (and start paying the ST-license fee), so net saving stops rising — no
    // point ballooning to the full Max-Demand size. proposals.maxSaving picks the
    // smallest INVERTER whose generation covers consumption (so the inverter isn't
    // oversized → no 20 kWp on a small bill). Then fill panels to the MOST that
    // inverter can carry at ratio <1.35 — per user, "max panels the kW can support".
    const maxMdCard = fillInverter(proposals?.maxSaving || null, candidates);

    // "Propose 96 kWp straight if near" — when a card lands at ~88–96 kWp and the MD
    // can carry a 72 kW inverter (snap96 exists), snap to the standard 96 kWp / 72 kW
    // ST-free package. EVERY basis, SELCo included — the rule and the reasoning live in
    // utils/cscSizing.js, which is where snapNear96 moved to. This comment used to say
    // "SELCo is left alone", which stopped being true when the helper moved.
    const cardForBasis = (basis) => {
        const base = basis === 'maxmd' ? maxMdCard
            : basis === 'selco' ? (proposals?.selco || null)
            : basis === 'p100' ? pickPct(1.00, sizingCtx)
            : basis === 'p70' ? pickPct(0.70, sizingCtx)
            : pickPct(0.50, sizingCtx);
        return snapNear96(base, basis, snap96);
    };

    // ── Option A sizing: bill → TARGET monthly saving lookup (user 2026-06-22) ──
    // The agent's manual method: read a target monthly saving off the bill, then size
    // the SMALLEST system whose generation × tariff (grossMonthlySaving) reaches it.
    // 640 Wp panels, capped at the inverter's 1.35 ratio (the candidate list already
    // respects that cap). Bills under RM 1.4k cover ~the full bill ("Max"). The 5,000
    // target lands at ~96 kWp @ 0.515 — matches the hand-quote. Option A's default
    // "~50%" basis now uses this table; picking 70%/Max MD/SELCo still overrides it.
    const basisA = normBasis(optBasis.a);
    const optA = basisA === 'p50' ? billTargetCard(sizingCtx) : cardForBasis(basisA);
    const basisB = normBasis(optBasis.b);
    // Option B = the SAME system as Option A, just WITH battery. A is the no-battery
    // option; B is the with-battery comparison (user 2026-06-23). B's battery count is
    // auto-set by the saving rule in the effect below.
    const optB = optA;
    // No auto-battery (user 2026-07-10): the battery is never suggested by default — the agent
    // steps it in manually (16-kWh LV up to 96, then HV 261-kWh units: 261/522/783…). Both options start
    // battery-free; adding a battery sets the inverter type (LV → Hybrid, HV → String).
    const selectedCard = selectedOpt === 'b' ? optB : optA;

    // Single source of price truth for a card: panel − discount + battery + add-ons.
    // Discount applies to the PANEL system ONLY (excludes battery + add-ons). Each
    // card reads its OWN per-card system state (sysFor): editable System Price +
    // flat Add-on Price. 36M CC = CASH ÷ 0.915 (RSC factor).
    const cardPricing = (key, card) => {
        const s = sysFor(key, card);
        const panel = Number(s.systemCost) || 0;   // already folds the residential-band inverter upgrade (see sysFor)
        const battery = batteryCostFor(battKwhFor(key), battBrandFor(key));
        const addons = addonTotalForKey(key) + (Number(s.addOn) || 0);
        const d = cscDisc[key] || {};
        // SIGNED: positive = discount (comes off), negative = mark-up (goes on). The agent
        // always types a POSITIVE number; the Discount/Mark-up switch supplies the sign.
        // A mark-up is NOT capped at the panel price — only a discount is, because taking
        // more off than the system costs would price it below zero.
        const isMarkup = d.mode === 'markup';
        let discAmt = 0;
        if (Number(d.rm) > 0) discAmt = isMarkup ? Number(d.rm) : Math.min(panel, Number(d.rm));
        else if (Number(d.pct) > 0) discAmt = Math.round(panel * Number(d.pct) / 100);
        if (isMarkup) discAmt = -discAmt;
        // Mark-up lands here as a negative, so `panel − discAmt` ADDS it. The Math.max(0)
        // only ever bites a discount.
        const panelNet = Math.max(0, panel - discAmt);   // commission basis (system after discount / mark-up)
        const cash = panelNet + battery + addons;         // Final CASH / commission-basis all-in
        // "Deduct SST" (mirrors create-sales): the typed discount DEDUCTS the SST first —
        // SST payable = max(0, fullSST − discount); any discount beyond the SST comes off the
        // price. The customer keeps the full system price otherwise; commission still follows
        // panelNet (unchanged). Tick with no discount = no effect. (user 2026-08-10)
        // Deduct-SST needs a real discount to offset the SST with. On a mark-up `discAmt`
        // is negative, and `fullSST − discAmt` would INCREASE the SST payable — charging
        // the customer extra tax for being marked up. Never on in mark-up mode.
        const deductOn = !isMarkup && !!(cscSstDeduct[key] && cscSstDeduct[key].on);
        const fullBase = panel + battery + addons;        // pre-discount all-in = SST base in deduct mode
        const fullSST = sstOn(fullBase);
        const sstPayable = deductOn ? Math.max(0, fullSST - discAmt) : sstOn(cash);
        const excess = deductOn ? Math.max(0, discAmt - fullSST) : 0;   // discount beyond SST → price
        const custPreSst = deductOn ? (fullBase - excess) : cash;       // customer total before SST
        const grandTotal = custPreSst + sstPayable;                     // customer-paying grand total
        // Instalments finance what the customer actually owes, which INCLUDES SST
        // (user 2026-08-21). They were converted from the pre-SST cash figure, so the SST
        // was left outside the financing - on a 91,000 job the 24M line read 97,326 when
        // the customer owes 92,092, not 91,000.
        //
        // ccFromCash carries the shared CC_FACTORS and the roundUp10 rule, so CSC stops
        // keeping its own copy of the rates and matches RSC rounding. Deduct-SST mode is
        // handled for free: grandTotal already reflects it.
        const cc24 = ccFromCash(grandTotal, 24);
        const cc = ccFromCash(grandTotal, 36);
        const cc60 = ccFromCash(grandTotal, 60);
        // Effective discount % off the panel/system (derived — works whether the agent
        // typed an RM amount or a %, and stays correct after the min-cap).
        const discPct = panel > 0 ? (discAmt / panel) * 100 : 0;
        return { panel, panelNet, battery, addons, discAmt, discPct, cash, cc24, cc, cc60, deductOn, fullSST, sstPayable, custPreSst, grandTotal };
    };

    // Correct SYSTEM (panel-only) cash price for an arbitrary panel count, mirroring the
    // hook's buildCandidate pricing: ≤30 kWp → raw ATAP residential list (user: "≤30kWp
    // must follow ATAP"); >30 kWp → commercial tier × kWp. Used by the editable System
    // Details so changing panels re-prices on the RIGHT basis (was wrongly using the
    // commercial tier in the residential band). Returns 0 when ATAP has no row → caller keeps the
    // prior price.
    const priceForPanels = (panels, kwp, brand = 'goodwe', type = 'string') => {
        const n = Number(panels) || 0;
        if (n <= 0 || !(kwp > 0)) return 0;
        let residentialCash = 0;
        if (kwp <= 30 && Array.isArray(atapPrices)) {
            const row = atapPrices.find(r => Number(r.panels) === n);
            if (row) residentialCash = sharedPhaseMode === '3phase' ? row.cash3p : row.cash;
        }
        return getCommercialPrice({ kwp, brand, type, residentialCash, is3p: sharedPhaseMode === '3phase' });
    };

    // One-button "Astern Discount" for ATAP-residential-priced systems — mirrors RSC's
    // "with backupbox" package discount (SuRIA Home Bonus Promo, kept rate). Commercial
    // keeps the backup-box ALWAYS (no box-removal discount, unlike RSC), so it's the
    // with-backupbox amount, flat regardless of phase/battery: RM 2,200. Follows the RSC
    // value (was 1P 1,800 / 3P 3,000; aligned to the RM2,200 with-backupbox rate 2026-08-07).
    //
    // The gate is ≤ 30 kWp and must STAY equal to the residential-pricing boundary in
    // commercialPricePerKwp. It read ≥26 after that boundary moved to 30 on 2026-10-14,
    // which left 26–30 kWp priced off the residential row but unable to take the
    // residential discount — the price basis and the discount have to agree.
    const asternDiscFor = (key) => {
        const card = key === 'b' ? optB : optA;
        const k = parseFloat(sysFor(key, card)?.kwp) || 0;
        if (k <= 0 || k > 30) return 0;
        return 2200;
    };
    // Toggle the Astern discount (checkbox): on → set the flat amount; off → clear it.
    const applyAstern = (key) => {
        const amt = asternDiscFor(key);
        if (amt <= 0) return;
        const isOn = (cscDisc[key] || {}).kind === 'astern';
        setCardDisc(key, isOn ? { rm: '', pct: '', kind: '' } : { rm: String(amt), pct: '', kind: 'astern' });
    };

    // Effective per-card system snapshot for the SELECTED option (auto + override
    // merged). Drives the 3 generators, SARE tier, GITA auto-tick, and math hook.
    const sel = sysFor(selectedOpt, selectedCard);
    /* Costed AFTER `sel` exists - it needs the chosen system's kWp. Sitting above the
       declaration it threw "Cannot access 'cscSys' before initialization" on every
       render, because a const in the temporal dead zone is a ReferenceError, not
       undefined. */
    const billsAnalysis = (() => {
        /* CUSTOM MODE ONLY. monthBills survives a switch back to `single` - deliberately,
           so flipping between the two does not throw away what was typed - but nothing
           used to CHECK the mode here. Months left over from an earlier session then
           drove the year chart, the card's average/highest/lowest, year1NetSaving (and
           through it the outright and loan tables, the payback and the ROI tile) and the
           SARE self-use mix, while the Monthly Bill box showed a single figure that had
           nothing to do with any of them. The panel below has always TOLD the agent to
           "set Monthly Bill to custom"; this is what makes that true.

           One gate, because every one of those consumers reads this single value.
           (user 2026-10-26: "from load profile is jst 2months, how u noe the 6months
           cycle or?" - the cycle was 5 stale months, and the mode said single) */
        if (billMode !== 'custom') return null;
        const kwp = parseFloat(sel?.kwp) || 0;
        if (!kwp) return null;
        return estimateSavingFromBills({
            /* calcRate, NOT the bare tariff. These are real bills just like the single
               one above, so they convert at the same AFA-aware rate - and on a ToU
               tariff the bare rate was wrong here even before AFA entered it. */
            bills: monthBills.map((rm, i) => ({ bill: rm, kwh: monthKwhs[i] })), tariffRate: calcRate, kwp, systemCost: parseFloat(sel?.systemCost) || 0,
            operatingDays: effectiveOperatingDays, opStart: effectiveOpStart, opEnd: effectiveOpEnd,
            /* MV: a bill CANNOT be divided by a rate to get kWh — roughly half of it is
               capacity + network, charged per kW of demand and untouched by consumption.
               On the ALL COSMOS bill the true average is 55.82 sen while calcRate (the
               value of a solar kWh) is 32.51, so dividing invented 72% more electricity
               than the site used. That phantom load then became the baseline the bill
               comparison subtracted solar from, and the card's saving collapsed from
               RM 42,109 to RM 20,643 against a quotation nobody could reconcile.
               Inverting the bill round-trips exactly. (user 2026-10-30, sale screenshots) */
            kwhFor: isMv ? ((rm) => mvKwhFromBill(mvCtx, rm)) : null,
        });
    })();

    // Keep the warranty honest when the system size moves across 30 kWp. Declared HERE,
    // after `sel`, and it must stay after it: the dependency array below is evaluated during
    // render, so placing this above `sel` throws "Cannot access 'sel' before initialization"
    // and white-screens the whole CSC tool. It did exactly that on prod on 2026-10-15.
    useEffect(() => {
        const k = parseFloat(sel?.kwp) || 0;
        if (!(k > 0)) return;
        setOperationWarranty((prev) => warrantyForKwp(prev, k, cleaningTouched));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sel?.kwp, cleaningTouched]);
    // SARE is only for ≥ 42 kWp — auto-untick it (and keep it out of the proposal) when
    // the selected system drops below 42 kWp. (user 2026-08-01)
    // SARE default follows kWp: ON for ≥42 (and before a system is computed), OFF for a real
    // system <42 kWp. Skipped once the user manually toggles the checkbox. (user 2026-08-01)
    // Battery does NOT auto-untick SARE: the user prices that case with their own
    // formula and wants the choice left alone. (user 2026-10-14: "SARE auto unticked by
    // battery i think this ignore. i have the formula already")
    useEffect(() => { if (sareIncludeTouched) return; const _k = parseFloat(sel?.kwp) || 0; setIncludeSARE(_k === 0 || _k >= 42); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [sel?.kwp, sareIncludeTouched]);

    // What the SARE rate has to recover ON TOP of the panel system: the battery, the
    // catalogue add-on rows, and the flat Add-on Price field. Everything that is not the
    // panel system itself. (user 2026-10-14: "any addon including battery")
    const _sareAddOnCost = batteryCostFor(battKwhFor(selectedOpt), battBrandFor(selectedOpt))
        + addonTotalForKey(selectedOpt)
        + (Number(sel.addOn) || 0);

    /* A MARK-UP IS MONEY SARE HAS TO RECOVER TOO.
       (user 2026-11-02: "i markup the price, but the thing is the sare rate doesnt change
        for 20years one")

       The rate card is per kWp band, so nothing about the price moved it — an agent could
       add RM20,000 of margin to a SARE deal and the customer's tariff would not change by
       a sen, which means the margin is never collected. On a zero-capex product the tariff
       IS the price; it is the only place margin can live.

       Recovered exactly the way an add-on is, through sareAddOnUplift: cost per kWp, every
       RM100 of that adds RM0.01/kWh, grossed up by the 0.8 recovery factor.

       A DISCOUNT is deliberately NOT mirrored here. Taking money off a cash price is the
       agent's to give; cutting the published SARE rate below the card is Astern's decision,
       not an arithmetic consequence. It stays at the band rate until somebody says otherwise.

       20-YEAR ONLY, like the add-on uplift, because the 15- and 10-year recovery factors
       still have not been worked out. The short terms therefore quote the bare band rate
       and do NOT recover the mark-up — flagged to the user on the day this went in. */
    const _sareMarkup = (() => {
        const d = cscDisc[selectedOpt] || {};
        if (d.mode !== 'markup') return 0;
        const panel = Number(sel.systemCost) || 0;
        if (Number(d.rm) > 0) return Number(d.rm);
        if (Number(d.pct) > 0) return Math.round(panel * Number(d.pct) / 100);
        return 0;
    })();

    // What the 20-year rate must recover: the add-ons AND the mark-up. Kept separate from
    // _sareAddOnCost because that one also decides whether the short terms are withheld,
    // and a mark-up is not a reason to hide them.
    const _sareRecoverCost = _sareAddOnCost + _sareMarkup;

    /* AN ADD-ON WITHDRAWS THE SHORT TERMS. The uplift that recovers an add-on is worked
       out for the 20-year term only, so defaultSareTariffByKwp hands the 15- and 10-year
       terms their bare band rate - a rate that recovers nothing for the battery or the
       add-on rows. Printing it would be quoting a price we know to be short, on the two
       terms that have the least time to make it back.

       So they are withheld, not mispriced. (user 2026-10-24: "15 years and 10years
       disable first if got add on")

       DERIVED, never written back into the checkbox state: the agent's choice of terms
       has to survive them clearing the add-on again. The saved set and the autosave keep
       storing the raw ticks for the same reason; only what is RENDERED and what reaches
       the proposal use the effective pair. */
    /* A MARK-UP WITHHOLDS THEM TOO. The uplift that recovers extra cost is worked out for
       the 20-year term only, so the short terms would quote a rate that recovers the
       add-ons AND now the mark-up not at all — a price we know to be short, on the two
       terms with the least time to make it back. Greyed out, like an add-on does.
       (user 2026-11-02: "for 15years and 10years, can grey out like add on") */
    const _sareShortTermsBlocked = _sareRecoverCost > 0;
    const includeSARE10Eff = includeSARE10 && !_sareShortTermsBlocked;
    const includeSARE15Eff = includeSARE15 && !_sareShortTermsBlocked;

    // Auto-default SARE tariff from the kWp tier (40-150→0.36, ≤300→0.32,
    // ≤600→0.30, >600→0.28) PLUS the add-on uplift, until the user overrides it.
    // Written as a plain String, not toFixed(2): an uplift of RM0.015 is a real rate of
    // 0.375 and rounding it to 0.38 here would quietly overcharge by half a sen.
    useEffect(() => {
        if (sareTouched) return;
        const k = parseFloat(sel.kwp) || 0;
        if (k < 40) { setSareTariffOverride(''); return; }
        setSareTariffOverride(String(defaultSareTariffByKwp(k, 20, _sareRecoverCost)));
    }, [sel.kwp, _sareRecoverCost, sareTouched]);

    // GITA (state var includeITA) is available ONLY for systems ≥ 40 kWp. Below that it is
    // forced OFF and greyed out. At/above 40 kWp it defaults ON, until the user manually
    // toggles the checkbox (itaTouched), after which their choice sticks. (user 2026-07-25)
    useEffect(() => {
        const k = parseFloat(sel.kwp) || 0;
        if (k < 40) { setIncludeITA(false); return; }   // ineligible below 40 kWp
        if (itaTouched) return;
        setIncludeITA(true);                             // default ON at/above 40 kWp
    }, [sel.kwp, itaTouched]);

    // Prepared-By picker bootstrap. Load all groups; default group + agent to the
    // logged-in user (still editable). Seed sales.name from the profile so it's
    // populated before the title fetch resolves.
    useEffect(() => {
        let c = false;
        getAllAgentGroups().then(r => { if (!c) setAgentGroups(r.data || []); }).catch(() => {});
        return () => { c = true; };
    }, []);
    useEffect(() => {
        if (!user?.userId) return;
        let c = false;
        setSales(s => ({ ...s, name: handleAgentName(user.firstName, user.lastName, user.alias), photoUrl: absPhoto(user.profilePhotoUrl) }));
        getAgentDetailsByAgentId(user.userId)
            .then(res => {
                const gid = res?.data?.groupId;
                if (c || !gid) return;
                setSelectedGroupId(gid);
                return getAgentsByGroupId(gid).then(ar => {
                    if (c) return;
                    setGroupAgents(filterAgents(ar.data));
                    setSelectedAgentId(user.userId);
                });
            })
            .catch(() => {});
        getSalesTitle(user.userId)
            .then(res => { const t = res?.data?.title; if (!c && t) setSales(s => ({ ...s, title: t })); })
            .catch(() => {});
        return () => { c = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const onPickGroup = (gid) => {
        setSelectedGroupId(gid);
        setSelectedAgentId('');
        getAgentsByGroupId(gid).then(r => setGroupAgents(filterAgents(r.data))).catch(() => setGroupAgents([]));
    };
    const onPickAgent = (uid) => {
        setSelectedAgentId(uid);
        const a = groupAgents.find(x => String(x.userId) === String(uid));
        if (a) {
            const photo = a.profilePhotoUrl || (String(uid) === String(user?.userId) ? user?.profilePhotoUrl : '');
            setSales(s => ({ ...s, name: handleAgentName(a.firstName, a.lastName, a.alias), photoUrl: absPhoto(photo) }));
        }
        getSalesTitle(uid).then(r => { const t = r?.data?.title; setSales(s => ({ ...s, title: t || s.title })); }).catch(() => {});
    };

    // ── Proposal math (same call shape as cscProposalTab.jsx) ──
    // Loan/financing must finance the SAME price the outright shows = the selected card's
    // panelNet + battery (the proposal's docSystemCost). sel.systemCost came through empty
    // on auto-sized cards, so the loan principal fell back to kWp × RM1,800 instead of the
    // real price (user 2026-06-23).
    // Full installed price the customer pays = panel (after discount) + battery + add-ons
    // (= P.cash). EVERY financing option (Outright/SARE/Loan/CA) must finance this, so
    // both the battery and the add-ons are counted in for all (user 2026-06-23).
    const _selCash = cardPricing(selectedOpt, selectedCard);
    const _sysCostForMath = Math.round(_selCash.cash || 0);
    // Proposal math is called BELOW, after dispSavingMo, so it can be fed the year-1 NET saving.
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    // Monthly saving = REAL TNB bill reduction (Original bill − After-Solar bill, net of
    // export credit) for the displayed system — "Option B" (user 2026-06-08). The SAME
    // figure feeds the breakdown card, WhatsApp, and the proposal summary so all three
    // tally. It auto-respects the 100% self-use toggle (which changes optA's self/export
    // fields). The 20-year TABLE keeps its own generation-revenue model (it's correct).

    // ── Mode follows the explicit ATAP/SELCo master toggle ──────────────────────
    // ATAP → NEM (export credited; MD optional, caps the system when entered).
    // SELCo → zero export (self-consumption only). No MD-derivation any more.
    // Use the system that's ACTUALLY shown/edited (the single card = `sel`, option B) — the
    // old code read a phantom 'a' slot the user never sees, which mis-flagged SELCo. (user 2026-08-07)
    const _effKwp = Number(sel?.kwp) || 0;
    const _md = parseFloat(maxDemand) || 0;
    const _neededInv = _effKwp > 0 ? inverterFromKwp(_effKwp, RATIO_CAP_NO_BATT, sel?.type || 'string', sel?.brand) : 0;
    // ATAP (export allowed) ONLY when MD is keyed AND the inverter kW fits within the MD.
    // SELCo (zero export) when MD is blank OR the ACTUAL inverter kW exceeds MD — you can
    // only export up to your demand, so an oversized system is self-consumption only.
    // (user 2026-07-24; use the shown inverter not a kWp-derived one, 2026-08-07)
    const _invOverMd = _md > 0 && (Number(sel?.inverterKw) || _neededInv) > _md;
    const _csMode = (csMode === 'selco' || _invOverMd) ? 'selco' : 'atap';
    const _mdWarn = null;

    // When the user manually edits Panels / System kWp, recompute energy at the
    // effective kWp (derived from sysFor) instead of freezing at the auto-sized optA.
    // kwpEdited is true iff panels were overridden and the resulting kWp differs from
    // the auto-pick by more than 0.01 kWp. When NOT edited, energyForDisplay === optA
    // (zero change in behavior). Either way, the MD-derived _csMode governs the mode.
    const _autoKwp = Number(optA?.kwp) || 0;
    const _kwpEdited = !!optA && _effKwp > 0 && Math.abs(_effKwp - _autoKwp) > 0.01;
    let energyForDisplay = optA;
    if (_kwpEdited) {
        const _monthlyKwh = calcRate > 0 ? (parseFloat(calcBill) || 0) / calcRate : 0;
        const _battA = parseFloat(battKwhFor('a')) || 0;
        let _e = _csMode === 'selco'
            ? estimateMonthlySavingSELCo({ kwp: _effKwp, tariffRate: calcRate, monthlyKwh: _monthlyKwh, operatingDays: effectiveOperatingDays, opStart: effectiveOpStart, opEnd: effectiveOpEnd, battKwhTotal: _battA, offHourBaselinePct: _offHour })
            : estimateMonthlySaving({ kwp: _effKwp, tariffRate: calcRate, monthlyKwh: _monthlyKwh, operatingDays: effectiveOperatingDays, opStart: effectiveOpStart, opEnd: effectiveOpEnd, battKwhTotal: _battA, offHourBaselinePct: _offHour });
        if (_fullSelf) {
            const _selfFull = Math.min(_e.generated * Math.min(1, (operatingDays || 7) / 7), _monthlyKwh);
            _e = { ..._e, selfConsumed: _selfFull, exported: 0, importAfterSolar: Math.max(0, _monthlyKwh - _selfFull), monthlySaving: _selfFull * calcRate };
        }
        energyForDisplay = { ...optA, kwp: _effKwp, mode: _csMode, ..._e };
    } else if (optA && _md > 0 && _csMode !== optA.mode) {
        // Not panel-edited, but MD rule forces a different mode than the hook's auto-pick.
        // Recompute energy at _autoKwp with the MD-derived mode so saving is consistent.
        const _monthlyKwh = calcRate > 0 ? (parseFloat(calcBill) || 0) / calcRate : 0;
        const _battA = parseFloat(battKwhFor('a')) || 0;
        let _e = _csMode === 'selco'
            ? estimateMonthlySavingSELCo({ kwp: _autoKwp, tariffRate: calcRate, monthlyKwh: _monthlyKwh, operatingDays: effectiveOperatingDays, opStart: effectiveOpStart, opEnd: effectiveOpEnd, battKwhTotal: _battA, offHourBaselinePct: _offHour })
            : estimateMonthlySaving({ kwp: _autoKwp, tariffRate: calcRate, monthlyKwh: _monthlyKwh, operatingDays: effectiveOperatingDays, opStart: effectiveOpStart, opEnd: effectiveOpEnd, battKwhTotal: _battA, offHourBaselinePct: _offHour });
        if (_fullSelf) {
            const _selfFull = Math.min(_e.generated * Math.min(1, (operatingDays || 7) / 7), _monthlyKwh);
            _e = { ..._e, selfConsumed: _selfFull, exported: 0, importAfterSolar: Math.max(0, _monthlyKwh - _selfFull), monthlySaving: _selfFull * calcRate };
        }
        energyForDisplay = { ...optA, mode: _csMode, ..._e };
    }

    const _consumptionKwh = Math.round((energyForDisplay?.selfConsumed || 0) + (energyForDisplay?.importAfterSolar || 0));
    const _origBill = energyForDisplay ? billFn(_consumptionKwh) : { total: 0 };
    const _newImportKwh = Math.round(energyForDisplay?.importAfterSolar || 0);
    const _newBill = energyForDisplay ? billFn(_newImportKwh) : { total: 0 };
    const _exportCredit = (energyForDisplay && energyForDisplay.mode !== 'selco') ? Math.round(energyForDisplay.exported || 0) * 0.20 : 0;
    const _newTotalAfterCredit = Math.max(0, (_newBill.total || 0) - _exportCredit);
    // Hard ceiling: the saving can NEVER exceed what the solar physically generates × the tariff
    // (you can't offset more than you produce). calcCommercialLvBill over-credits MD/capacity for
    // big systems, so clamp to gen×tariff. (user 2026-07-14)
    const _grossCeilMo = Math.round((energyForDisplay?.generated || 0) * calcRate);
    const dispSavingMo = energyForDisplay ? Math.min(_grossCeilMo, Math.max(0, Math.round((_origBill.total || 0) - _newTotalAfterCredit))) : 0;
    const dispCapital = cardPricing(selectedOpt, selectedCard).cash;   // the shown system, not the phantom 'a' slot (user 2026-08-07)
    const dispRoi = (dispSavingMo * 12) > 0 ? Math.round((dispCapital / (dispSavingMo * 12)) * 100) / 100 : null;
    const dispSavingPct = (realBill > 0 && dispSavingMo) ? dispSavingMo / realBill : (energyForDisplay?.savingPct ?? null);
    const optADisplay = energyForDisplay ? { ...energyForDisplay, monthlySaving: dispSavingMo, roiYears: dispRoi, savingPct: dispSavingPct } : energyForDisplay;

    // Card B (Max MD) — same display/saving math as card A above, computed for optB.
    const optBDisplay = (() => {
        if (!optB) return optB;
        /* WHICH MONTH THE CARD DRAWS. The system is SIZED on the average bill and that does
           not change here - _kwpB comes from optB, which was computed upstream from the
           average. (user 2026-10-24: "system size is based on the bill average")

           What changes is the month this panel ILLUSTRATES. With real months entered it is
           the HEAVIEST one, because the average is a month that never appears on any bill:
           quoting 11,667 to a customer whose bills read 20,000 / 10,000 / 5,000 shows them
           a month they have never had. The heavy month is real, and it is the one where the
           array is fully absorbed. (user 2026-10-24: "this shd pick the highest month")

           The AVERAGE still drives every downstream figure - see selSavingMo below - so
           nothing here inflates WhatsApp, the summary or the twenty-year tables. */
        const peak = (billsAnalysis && billsAnalysis.count >= 2) ? billsAnalysis.peakMonth : null;
        /* ON ToU THE METER LINES WIN. Peak and off-peak kWh are typed straight off the
           bill, so the month is KNOWN — there is nothing to reconstruct and nothing a
           reconstruction could improve. Deriving it from a custom month's RM instead put
           a figure here that disagreed with the very bill printed beside it: a RM 92,845
           month inverted to 23,903 kWh against entered meter lines totalling 254,138, and
           the panel then "removed" 230,235 kWh of solar — wiping the entire 61,092 kWh
           peak line to 0.00 and leaving an Original bill of RM 92,606 that matched neither
           the inputs nor the customer's bill.
           (user 2026-10-31: "why peak and off peak in table before is wrong") */
        const monthlyKwh = (isTou && mvKwh > 0)
            ? mvKwh
            : peak
                ? peak.kwh
                : (calcRate > 0 ? (parseFloat(calcBill) || 0) / calcRate : 0);
        const battB = parseFloat(battKwhFor('b')) || 0;
        const effKwpB = Number(sysFor('b', optB)?.kwp) || 0;
        const autoKwpB = Number(optB?.kwp) || 0;
        const recomputeB = (kwp) => {
            let e = _csMode === 'selco'
                ? estimateMonthlySavingSELCo({ kwp, tariffRate: calcRate, monthlyKwh, operatingDays: effectiveOperatingDays, opStart: effectiveOpStart, opEnd: effectiveOpEnd, battKwhTotal: battB, offHourBaselinePct: _offHour })
                : estimateMonthlySaving({ kwp, tariffRate: calcRate, monthlyKwh, operatingDays: effectiveOperatingDays, opStart: effectiveOpStart, opEnd: effectiveOpEnd, battKwhTotal: battB, offHourBaselinePct: _offHour });
            if (_fullSelf) {
                const selfFull = Math.min(e.generated * Math.min(1, (operatingDays || 7) / 7), monthlyKwh);
                e = { ...e, selfConsumed: selfFull, exported: 0, importAfterSolar: Math.max(0, monthlyKwh - selfFull), monthlySaving: selfFull * calcRate };
            }
            return e;
        };
        // Always recompute with Option B's battery (battB) — B = A's system + battery, so
        // its energy/saving + battery fill % must reflect that battery. (user 2026-06-23)
        const _kwpB = effKwpB > 0 ? effKwpB : autoKwpB;
        let energyB = { ...optB, kwp: _kwpB, mode: _csMode, ...recomputeB(_kwpB) };
        const consB = Math.round((energyB?.selfConsumed || 0) + (energyB?.importAfterSolar || 0));
        const origB = billFn(consB);
        const newB = billFn(Math.round(energyB?.importAfterSolar || 0), energyB?.selfPeakFrac);
        const credB = (energyB.mode !== 'selco') ? Math.round(energyB.exported || 0) * 0.20 : 0;
        const _grossCeilB = Math.round((energyB?.generated || 0) * calcRate);   // saving can't exceed gen×tariff
        const savingMoB = Math.min(_grossCeilB, Math.max(0, Math.round((origB.total || 0) - Math.max(0, (newB.total || 0) - credB))));
        const capitalB = cardPricing('b', optB).cash;
        const roiB = (savingMoB * 12) > 0 ? Math.round((capitalB / (savingMoB * 12)) * 100) / 100 : null;
        const savingPctB = (realBill > 0 && savingMoB) ? savingMoB / realBill : (energyB?.savingPct ?? null);
        return { ...energyB, monthlySaving: savingMoB, roiYears: roiB, savingPct: savingPctB };
    })();

    // ONE option only (Option A retired 2026-06-29): the whole app proposes a single
    // battery-aware system = optBDisplay. Card, proposal summary, 20-yr math and WhatsApp
    // ALL read this one saving so they can never diverge. The old no-battery dispSavingMo
    // artifact drives nothing. (user 2026-07-14)
    /* THE FIGURE EVERY OTHER OUTPUT USES, and it is the AVERAGE, never the peak.
       optBDisplay.monthlySaving is now the heaviest month (it has to be, or the saving
       would contradict the bill boxes printed directly above it), and multiplying a peak
       month by twelve would overstate the year by the whole spread - on 20k/10k/5k that
       is the difference between a real year and the best month repeated.

       So with real months this is the mean of the twelve monthly savings, which is exactly
       annualSaving / 12. That makes WhatsApp, the proposal summary and the twenty-year
       tables agree to the ringgit, because year1NetSaving below is that same annualSaving.
       Without real months it stays what it always was: the single bill's saving. */
    const selSavingMo = (billsAnalysis && billsAnalysis.count >= 2)
        ? billsAnalysis.avgSaving
        : (optBDisplay?.monthlySaving || 0);

    // Proposal math — driven by the SAME year-1 NET saving as the selected card + WhatsApp,
    // so the 20-yr table / average / total / ROI all tally. Net degrades on the generation curve.
    const math = useCscProposalMath({
        kwp: parseFloat(sel.kwp) || 0,
        systemCost: _sysCostForMath,
        tariffRate: calcRate,
        /* THE YEAR, BUILT FROM THE MONTHS. Everything downstream - the outright and loan
           tables, payback, the comparison summary - derives from this one annual figure,
           so this is where the fluctuation has to enter or it enters nowhere.

           With custom bills it is the SUM of each month's own saving. Without them it is
           the selected card's monthly figure x 12, exactly as before.

           The two differ, and always in the same direction: a saving computed on the
           AVERAGE bill beats the average of the monthly savings, because saving is capped
           by consumption and the cap bites in the quiet months. On a 20,000/9,000/5,000
           cycle that gap was RM5,501 a year - the deck quoting 118,520 on the outright
           page while the year page said 113,019, for one system.
           (user 2026-10-24: "so meaning sare table for yearly saving for sare and
            outright will based on fluctuated month to calclate?") */
        year1NetSaving: (billsAnalysis && billsAnalysis.count >= 2)
            ? billsAnalysis.annualSaving
            : selSavingMo * 12,
        omAnnual: parseFloat(sel.om) || 0,
        includeCA,
        includeITA,
        includeOutright,
        includeSARE,
        includeLoan,
        sareTariffRate: parseFloat(sareTariffOverride) || undefined,
        sareTariffRate15: parseFloat(sareTariffOverride15) || undefined,
        sareTariffRate10: parseFloat(sareTariffOverride10) || undefined,
        /* The measured self-use mix, when real months exist. Undefined otherwise, which
           keeps a single-bill quote byte-identical to before. */
        sareSelfUseFraction: billsAnalysis?.selfUseFraction ?? null,
        // Only used if the tariff field is somehow blank — the field above is already
        // uplifted by the same figure, so the hook never applies it twice in practice.
        // Add-ons AND the mark-up — everything the 20-year rate has to earn back.
        sareAddOnCost: _sareRecoverCost,
        // Blank = finance the WHOLE thing, INCLUDING SST — the grand total the customer
        // actually owes, not the pre-SST cash. Left to the hook's own fallback it used
        // systemCost, which is pre-SST, so every loan was quoted short by the tax. The CC
        // instalments already finance grandTotal for exactly this reason (2026-08-21: "on a
        // 91,000 job the 24M line read 97,326 when the customer owes 92,092"); a bank loan
        // is the same argument. (user 2026-10-16: "principal we donnid key in right? since
        // the system cost etc after SST is auto")
        loanPrincipal: parseFloat(loanPrincipal) || Math.round(_selCash.grandTotal || 0) || undefined,
        loanRate: parseFloat(loanRate) / 100 || 0.07,
        loanTermMonths: parseInt(loanTermMonths, 10) || 120,
        loanTerms: loanTermsSelected,
    });

    // Single source of the proposal variable map — used by BOTH the Download PDF
    // path (genProposal) and the Print Preview path (printPreview) so the two
    // outputs are guaranteed identical content.
    // Single price source for the SELECTED option: panel (after discount) +
    // battery folded into systemCost; add-ons (table + flat) passed separately.
    // grand total = systemCost + addOn = P.cash (no double-count).
    const buildProposalMap = () => {
        const P = cardPricing(selectedOpt, selectedCard);
        const docSystemCost = P.panelNet + P.battery;
        return buildCscVarMap({
            customer: { name: customerName, phone: customerPhone, email: customerEmail, address: customerAddress },
            system: { kwp: sel.kwp, systemCost: docSystemCost, tariffRate: calcRate, tariffKey: tariffRate, omAnnual: Number(sel.om) || 0, inverterKw: Number(sel.inverterKw), inverterBrand: sel.brand, inverterBrands: [sel.brand], inverterPairs: [{ brand: sel.brand, type: sel.type }], panelWatt: Number(sel.panelWatt) || 650, atapPrices, phaseMode: sharedPhaseMode, operationWarranty },
            math, sales, lang: proposalLang,
            // The real bills, when the agent gave any. The page is dropped without them.
            yearBills: billsAnalysis,
            dates: { issued: todayDMY(), valid: plus30DMY() },
            financing: { loanPrincipal: parseFloat(loanPrincipal) || undefined, includeSARE, includeSARE10, includeSARE15, includeOutright, includeLoan },
            addOn: P.addons,
            site: siteState, photos: sitePhotos,
            maxDemand: parseFloat(maxDemand) || 0,
            batteryKwh: parseFloat(battKwhFor(selectedOpt)) || 0,
            batteryBrand: battBrandFor(selectedOpt),
            summarySavingMo: selSavingMo,   // selected option's real bill reduction (A or B) — matches WhatsApp
            // Catalogue add-ons (optimizers, etc.) → extra BoM rows on the
            // System Components page. Selected option's rows only.
            addons: (addonsForKey(selectedOpt) || []).map(a => ({ name: (a.itemName || a.name || '').trim(), qty: Number(a._qty || a.qty) || 1 })).filter(a => a.name),
        });
    };

    const genProposal = async () => {
        if (!customerName.trim()) { setErr('Customer name is required.'); return; }
        if (!sel.kwp || !sel.systemCost) { setErr('System kWp and cost are required.'); return; }
        setErr(''); setBusy(true);
        const map = buildProposalMap();
        const r = await generateCscProposalPdf(map, { lang: proposalLang, includeSARE, includeSARE10: includeSARE10Eff, includeSARE15: includeSARE15Eff, includeOutright, includeLoan, includePhotos: sitePhotos.length > 0, hasYearBills: !!(billsAnalysis && billsAnalysis.count >= 2), customerName, kwp: sel.kwp, systemKwp: Number(sel.kwp) || 0, bessHtml: bessHtmlFor(proposalLang), bessLang: proposalLang, loanTerms: loanTermsSelected });
        if (!r.ok) setErr(r.error);
        setBusy(false);
    };

    // Company Profile = the SAME proposal template with the customer/pricing pages stripped
    // (see postProcessCscProposal companyProfileOnly). Generic brand deck — no customer name
    // needed; reuses the current calculator context only to resolve the map. (user 2026-08-05)
    const [profBusy, setProfBusy] = useState(false);
    const genCompanyProfile = async () => {
        setErr(''); setProfBusy(true);
        try {
            // Downloadable ANYTIME — no proposal/system needed (all system + pricing pages
            // are stripped by companyProfileOnly). Use the current calculator context if a
            // system is sized; otherwise build a minimal-but-COMPLETE map so no literal
            // {{placeholders}} leak on the generic pages (cscSubstitute leaves unknown keys
            // as-is). (user 2026-08-06)
            let map = null;
            if (sel.systemCost) { try { map = buildProposalMap(); } catch { map = null; } }
            if (!map) {
                map = buildCscVarMap({
                    customer: { name: '', phone: '', email: '', address: '' },
                    system: { kwp: 0, systemCost: 0, tariffRate: 0.515, omAnnual: 0, inverterKw: 0, inverterBrand: 'goodwe', inverterBrands: ['goodwe'], panelWatt: 650, atapPrices, phaseMode: sharedPhaseMode, operationWarranty },
                    math, sales, lang: proposalLang,
                    dates: { issued: todayDMY(), valid: plus30DMY() },
                    financing: {}, addOn: [], site: siteState, photos: [],
                    maxDemand: 0, batteryKwh: 0, summarySavingMo: 0, addons: [],
                });
            }
            const r = await generateCscProposalPdf(map, { lang: proposalLang, companyProfileOnly: true, customerName: '', kwp: 0, systemKwp: 0 });
            if (!r.ok) setErr(r.error);
        } catch (e) {
            setErr(`Company profile failed: ${e.message || e}`);
        } finally {
            setProfBusy(false);
        }
    };

    // ── Print preview (vector-quality PDF via browser Save As PDF) ──
    // Ported from cscProposalTab.jsx; uses setErr + buildProposalMap + the
    // Combined tab's `lang` state. Same template / post-processing / FAB / title
    // injection so the vector output matches the rasterized Download path.
    const printPreview = async () => {
        if (!customerName.trim()) { setErr('Customer name is required.'); return; }
        if (!sel.kwp || !sel.systemCost) { setErr('System kWp and price are required.'); return; }
        setErr('');
        try {
            const cacheBust = `?v=${Date.now()}`;
            const tplUrl = (proposalLang === 'cn' ? '/templates/astern-proposal-csc-cn.html'
                          : proposalLang === 'my' ? '/templates/astern-proposal-csc-my.html'
                          : '/templates/astern-proposal-csc.html') + cacheBust;
            let res = await fetch(tplUrl);
            let tpl = res.ok ? await res.text() : '';
            // Same fallback as generatePdf: also fall back when nginx serves
            // the SPA index.html for a missing localized template (HTTP 200 but no .page divs).
            const looksLikeTemplate = (s) => /class\s*=\s*["']page(\s|["'])/.test(s);
            if (proposalLang !== 'en' && (!res.ok || !looksLikeTemplate(tpl))) {
                res = await fetch(`/templates/astern-proposal-csc.html?v=${Date.now()}`);
                tpl = res.ok ? await res.text() : '';
            }
            if (!res.ok || !looksLikeTemplate(tpl)) {
                throw new Error(`Failed to fetch template (HTTP ${res.status})`);
            }
            const origin = window.location.origin;
            tpl = tpl
                .split("src='./").join(`src='${origin}/templates/`)
                .split('src="./').join(`src="${origin}/templates/`)
                .split("url('./").join(`url('${origin}/templates/`)
                .split('url("./').join(`url("${origin}/templates/`)
                .split('url(./').join(`url(${origin}/templates/`);
            const map = buildProposalMap();
            let populated = postProcessCscProposal(cscSubstitute(expandLoanTerms(tpl, loanTermsSelected, proposalLang), map), { includeSARE, includeSARE10: includeSARE10Eff, includeSARE15: includeSARE15Eff, includeOutright, includeLoan, includePhotos: sitePhotos.length > 0, hasYearBills: !!(billsAnalysis && billsAnalysis.count >= 2), bessHtml: bessHtmlFor(proposalLang), bessLang: proposalLang });
            // Inject floating Print button + auto-hide on print
            const printUi = `
<style>
  .csc-print-fab {
    position: fixed; bottom: 24px; right: 24px; z-index: 9999;
    background: linear-gradient(135deg, #1A8CFF, #0F62CC); color: #fff;
    border: none; border-radius: 999px; padding: 12px 22px;
    font: 600 14px/1 'Poppins', -apple-system, sans-serif; letter-spacing: 0.02em;
    box-shadow: 0 8px 20px rgba(15, 98, 204, 0.35); cursor: pointer;
    display: inline-flex; align-items: center; gap: 8px;
  }
  .csc-print-fab:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(15, 98, 204, 0.45); }
  .csc-print-fab:active { transform: translateY(0); }
  .csc-print-fab:disabled { opacity: 0.55; cursor: progress; }
  .csc-print-fab svg { width: 18px; height: 18px; fill: currentColor; }
  @media print { .csc-print-fab { display: none !important; } }
</style>
<button class="csc-print-fab" onclick="window.print()" title="Print or Save as PDF">
  <svg viewBox="0 0 24 24"><path d="M19 8h-1V3H6v5H5a3 3 0 0 0-3 3v6h4v4h12v-4h4v-6a3 3 0 0 0-3-3zM8 5h8v3H8V5zm8 14H8v-5h8v5zm3-7a1 1 0 1 1 1-1 1 1 0 0 1-1 1z"/></svg>
  Print / Save PDF
</button>`;
            populated = populated.replace('</body>', `${printUi}\n</body>`);
            // Browser's Save-as-PDF defaults the filename to the <title>.
            const safeName = (customerName || 'Customer').replace(/[\/\\:*?"<>|]/g, '').trim();
            const kwpLabel = `${Number(sel.kwp) || 0}`;
            const pdfTitle = `${safeName} - ${kwpLabel}KWP Astern Commercial Proposal`;
            populated = populated.replace(/<title>[\s\S]*?<\/title>/, `<title>${pdfTitle.replace(/</g, '&lt;')}</title>`);
            const blob = new Blob([populated], { type: 'text/html;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const win = window.open(url, '_blank');
            if (!win) { setErr('Pop-up blocked — allow pop-ups for this site, then click again.'); return; }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            console.error(e);
            setErr(`Preview failed: ${e.message || e}`);
        }
    };

    // ── WhatsApp output (RSC-style export chooser + preview) ──────────
    // Mirrors residentialSolarCalculatorV2's export UI: Opt A / Opt B / A+B
    // chooser + bilingual lang toggle + a WhatsApp preview, all fed by CSC's
    // OWN numbers (no RSC builder/pricing imports — only the LAYOUT is shared).
    // (Language comes from the header `lang` prop; single option so selectedOpt = 'a'.)

    // Build the `r`-shape the detailed commercial WhatsApp builder consumes, from
    // this tab's CSC state. Uses the bill-CAPPED card.monthlySaving (not a gross
    // kWp×3.4×30×rate), so the saving can never exceed the bill. Capital cost +
    // add-ons + battery − discount come from cardPricing; commercial tax relief
    // (capital allowance) uses the 24% / 38.8% tier like the System Size tab.
    const buildWaR = (key) => {
        const card = key === 'b' ? optB : optA;
        if (!card) return null;
        const s = sysFor(key, card);
        const P = cardPricing(key, card);
        const kwpN = parseFloat(s.kwp) || 0;
        const rate = Number(tariffRate) || 0;
        const capitalCost = P.cash;
        const capitalCostRaw = P.cash + P.discAmt;
        // Monthly saving = REAL bill reduction for the ONE proposed system (battery-aware),
        // taken as the AVERAGE month — the same value the proposal summary and 20-yr math
        // use, so all outputs tally. Never the card's headline, which is the peak month and
        // would overstate savingYr by the whole spread. 10/20-yr use the table's degraded
        // totals so long-term figures agree.
        const savingMo = selSavingMo;
        const savingYr = savingMo * 12;
        const saving10 = Math.round(math?.total10yrOutright || savingYr * 10);
        const saving20 = Math.round(math?.total20yrOutright || savingYr * 20);
        // ROI matches the PDF exactly — same CA/GITA cashflow model (escalation + tax
        // incentives), not a flat capital-relief shortcut. Falls back to simple payback
        // only if the math hook hasn't produced the figures. (user 2026-07-03)
        const roi = (math?.outrightBreakevenExactNoIncentives != null) ? math.outrightBreakevenExactNoIncentives : (savingYr > 0 ? capitalCost / savingYr : 0);
        const yieldRate = roi > 0 ? (1 / roi) * 100 : 0;
        const taxPct = kwpN < 60 ? 24 : 38.8;
        const relief = Math.round(capitalCost * (kwpN < 60 ? 0.24 : 0.388));
        const afterTaxCost = capitalCost - relief;
        const roiAfterTax = (math?.outrightBreakevenExact != null) ? math.outrightBreakevenExact : (savingYr > 0 ? afterTaxCost / savingYr : 0);
        const yieldAfterTax = roiAfterTax > 0 ? (1 / roiAfterTax) * 100 : 0;
        const billN = realBill;
        const billSavingPct = billN > 0 ? Math.min((savingMo / billN) * 100, 100) : null;
        const tariffLabel = (TARIFFS.find(t => t.rate === rate) || {}).label || '';
        return {
            kwp: kwpN, panels: parseFloat(s.panels) || 0, kw: parseFloat(s.inverterKw) || 0, battKwh: parseFloat(battKwhFor(key)) || 0,
            capitalCost, capitalCostRaw, discount: P.discAmt,
            maintenance: parseFloat(s.om) || 0,
            // Systems < 96 kWp are serviced once every 3 years (per system).
            maintenancePeriodYears: kwpN < 96 ? 3 : 1,
            relief, afterTaxCost, taxPct,
            ppk: 0, battCost: P.battery, rate, tariffLabel, bill: billN, billSavingPct,
            company: (customerName || '').trim().toUpperCase(),
            genMode: 'normal', sareRate: 0,
            // Free O&M warranty years for the WhatsApp "N-year Maintenance Warranty" line —
            // follows the Operation Warranty dropdown ("1 Year"/"3 Years …"). (user 2026-08-02)
            omWarrantyYears: warrantyYears(operationWarranty),
            // Whether the clean is included travels WITH the years - WhatsApp used to
            // assume it always was. (user 2026-10-26)
            omCleaning: warrantyHasCleaning(operationWarranty),
            // Year-1 SARE (Zero-Capex) monthly saving — shown under the "With CA" block on
            // the Outright message so the customer sees the SARE alternative. (user 2026-07-22)
            // Suppressed entirely when SARE is not included (e.g. <42 kWp, or unticked) so the
            // WhatsApp message never mentions SARE when it isn't offered. (user 2026-08-01)
            sareSavingMo: (includeSARE && math?.years?.[0]?.sare?.annualCF > 0) ? Math.round((math.years[0].sare.annualNetCF != null ? math.years[0].sare.annualNetCF : math.years[0].sare.annualCF) / 12) : 0,   // NETT (export-adjusted) — user 2026-08-04
            // Lowest and highest NETT monthly saving across the 20-year term, so the message
            // can quote a range instead of year 1 alone. The saving climbs because the TNB
            // tariff escalates ~8% every 3 years while the SARE rate is fixed for the contract.
            // (user 2026-08-21)
            // YEAR 1's Min ~ Max, matching sare_monthly_saving_range in cscProposalEngine.
            // These two implement the SAME range and must agree: the proposal card and the
            // WhatsApp message are read side by side, and until now they disagreed badly —
            // the proposal quoted year 1 nett-to-gross (746 ~ 1,663) while the message
            // quoted lowest-to-highest NETT across all 20 years (735 ~ 12,551), the top end
            // being year 20 after two decades of tariff escalation. (bug 2026-08-25)
            //   Min = NETT (surplus exported at the RM0.20 SMP rate)
            //   Max = gross (every kWh self-consumed at the full TNB tariff)
            ...(() => {
                if (!includeSARE) return {};
                const y1 = math?.years?.[0]?.sare;
                if (!y1) return {};
                const lo = Math.round((y1.annualNetCF != null ? y1.annualNetCF : y1.annualCF) / 12);
                const hi = Math.round((y1.annualCF || 0) / 12);
                return lo > 0 ? { sareSavingMoMin: lo, sareSavingMoMax: hi } : {};
            })(),
            // The SAME year-1 range as an ANNUAL figure — the message now leads with the
            // yearly saving (user 2026-08-28). Derived from the annual cash flow directly,
            // NOT monthly x 12: rounding the monthly first and multiplying gives 9,360
            // where the proposal card, which rounds the annual, prints 9,361. A one-ringgit
            // disagreement between two documents read side by side is exactly the class of
            // bug called out above.
            sareSavingYr: (includeSARE && math?.years?.[0]?.sare?.annualCF > 0)
                ? Math.round(math.years[0].sare.annualNetCF != null ? math.years[0].sare.annualNetCF : math.years[0].sare.annualCF)
                : 0,
            ...(() => {
                if (!includeSARE) return {};
                const y1 = math?.years?.[0]?.sare;
                if (!y1) return {};
                const lo = Math.round(y1.annualNetCF != null ? y1.annualNetCF : y1.annualCF);
                const hi = Math.round(y1.annualCF || 0);
                return lo > 0 ? { sareSavingYrMin: lo, sareSavingYrMax: hi } : {};
            })(),
            // Same three figures for the 10-YEAR term, so the message can quote both leases
            // side by side (user 2026-08-25). Gated on its own checkbox: tick only the 20-year
            // and the message is exactly as before. The 10-year block exists on years 1-10
            // only, so the range never quotes a year-20 figure against a 10-year lease.
            sare10SavingMo: (includeSARE10Eff && math?.years?.[0]?.sare10?.annualCF > 0)
                ? Math.round((math.years[0].sare10.annualNetCF != null ? math.years[0].sare10.annualNetCF : math.years[0].sare10.annualCF) / 12)
                : 0,
            // Year 1 Min ~ Max, same definition as the 20-year above and as the proposal's
            // sare10_monthly_saving_range.
            ...(() => {
                if (!includeSARE10Eff) return {};
                const y1 = math?.years?.[0]?.sare10;
                if (!y1) return {};
                const lo = Math.round((y1.annualNetCF != null ? y1.annualNetCF : y1.annualCF) / 12);
                const hi = Math.round((y1.annualCF || 0) / 12);
                return lo > 0 ? { sare10SavingMoMin: lo, sare10SavingMoMax: hi } : {};
            })(),
            // Annual equivalents for the 10-year lease, same reasoning as the 20-year above.
            sare10SavingYr: (includeSARE10Eff && math?.years?.[0]?.sare10?.annualCF > 0)
                ? Math.round(math.years[0].sare10.annualNetCF != null ? math.years[0].sare10.annualNetCF : math.years[0].sare10.annualCF)
                : 0,
            ...(() => {
                if (!includeSARE10Eff) return {};
                const y1 = math?.years?.[0]?.sare10;
                if (!y1) return {};
                const lo = Math.round(y1.annualNetCF != null ? y1.annualNetCF : y1.annualCF);
                const hi = Math.round(y1.annualCF || 0);
                return lo > 0 ? { sare10SavingYrMin: lo, sare10SavingYrMax: hi } : {};
            })(),
            savingMo, savingYr, saving10, saving20,
            roi, roiAfterTax, yieldRate, yieldAfterTax,
            // Catalogue add-ons → shown in the WhatsApp "Package Includes".
            addonItems: (addonsForKey(key) || []).map(a => ({ name: (a.itemName || a.name || '').trim(), qty: Number(a._qty || a.qty) || 1 })).filter(a => a.name),
            // Loan figures for the WhatsApp block - the same numbers the proposal
            // prints, so the chat and the PDF cannot disagree. (user 2026-09-29)
            includeLoan,
            loanTermMonths: math?.loanTermMonths || 0,
            loanMonthlyPmt: math?.loanMonthlyPmt || 0,
        };
    };

    // A BESS message needs the same figures the Part A card shows.
    const bessWaOpts = {
        unitKwh: bessUnitKwh,
        brandLabel: bessBrand === BATT_BRAND_HUAWEI ? 'Huawei' : 'GoodWe',
        company: customerName || '',
    };
    const waPartEff = !bessPick ? 'a' : waPart;
    const generatedMsg = (() => {
        // Follow the Option A/B chooser (selectedOpt) — was hardcoded to 'a', so
        // selecting Option B never changed the WhatsApp message (user 2026-06-25).
        const r = buildWaR(selectedOpt);
        const solar = r ? buildCscWhatsAppMsg(r, lang) : '';
        if (waPartEff === 'b') return buildCscBessMsg(bessPick, bessWaOpts, lang);
        if (waPartEff === 'ab' && bessPick) {
            const P = cardPricing(selectedOpt, selectedOpt === 'b' ? optB : optA);
            return buildCscSolarPlusBessMsg(solar, bessPick, {
                ...bessWaOpts,
                solarTotalInclSst: P?.grandTotal || 0,
                solarMonthlySaving: selSavingMo,
            }, lang);
        }
        return solar;
    })();

    const copyText = async (txt) => {
        if (!txt) return;
        try {
            await navigator.clipboard.writeText(txt);
        } catch {
            const ta = document.createElement('textarea');
            ta.value = txt; ta.style.cssText = 'position:fixed;opacity:0';
            document.body.appendChild(ta); ta.select();
            document.execCommand('copy'); document.body.removeChild(ta);
        }
    };
    const onCopyMsg = async () => {
        if (!generatedMsg) return;
        await copyText(generatedMsg);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    // Copy + share, mirroring RSC's openWAText: mobile → native share sheet (straight to
    // WhatsApp with the text prefilled) / wa.me fallback; desktop → copy + open WhatsApp Web.
    const onOpenWA = async () => {
        if (!generatedMsg) return;
        const mob = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (mob) {
            if (navigator.share) { try { await navigator.share({ text: generatedMsg }); } catch { /* user cancelled */ } }
            else window.open(`https://wa.me/?text=${encodeURIComponent(generatedMsg)}`, '_blank');
        } else {
            await copyText(generatedMsg);
            window.open('https://web.whatsapp.com/', '_blank');
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    // ── Commercial Quotation output (backend PDF via formGeneratorApi) ──
    // Mirrors formGenerator.jsx commercial getFormData block: 80/20 split with
    // 6% SST applied to the 20% portion only. Combined tab pays cash, so the
    // selected option's price (+ add-on) drives the split. SST via utils/sst.js.
    // ── Payment method (drives the Commercial Quotation) ──
    // CC converts the price (36M = cash/0.925, 60M = cash/0.9) → that converted
    // amount flows to the final columns. Cash = as-is. Loan marks UP by 1/LTV —
    // they print a referral note (the financing party sets the effective rate).
    const [payMethod, setPayMethod] = useState('cash');   // 'cash' | 'cc' | 'loan'
    /* ── Loan (financed): the markup that gets the customer to 100% ───────────────────
       A bank lends a PERCENTAGE of the invoice. If it lends 90% and the invoice is the
       real price, the customer is 10% short. So the invoice is marked up to real / LTV,
       the bank's 90% then lands on the real price, and the markup is cancelled by credit
       note once the loan is fully disbursed. At 100% there is no markup and no agreement
       page - the quotation is the ordinary one.
       (user 2026-10-17: "some loan can get 90% some can get 100%") */
    const [loanLtv, setLoanLtv] = useState(90);          // % of the invoice the bank lends
    const [depositRefundable, setDepositRefundable] = useState(true); // prints the refund clause
    const [loanLtvCustom, setLoanLtvCustom] = useState(false); // the % box is asked for, not always there
    const [payTerm, setPayTerm] = useState(24);
    const CC_FACTORS = { 24: 0.935, 36: 0.925, 60: 0.9 };
    // A LOAN carries no term on the quotation - the tenure is between the customer and
    // their bank, and printing one implies we set it. (user 2026-10-17: "if loan we
    // dont have a term") CC is the only method that still quotes a term.
    const payTermsFor = (m) => m === 'cc' ? [24, 36, 60] : [];
    // Leasing was removed as a separate method: it IS a loan, and having both invited
    // the wrong one to be picked. (user 2026-10-17: "Leasing is actually loan so can
    // remove leasing.") A saved draft that still says 'leasing' is read as 'loan' below.
    const payFinanceNote = (m) => m === 'loan' ? 'Refer to the effective interest rate charged by the loan party.' : '';
    const payAdjustedPrice = (cash, m, t) => m === 'cc' ? Math.round((Number(cash) || 0) / (CC_FACTORS[t] || 0.925)) : Math.round(Number(cash) || 0);
    const onPayMethod = (m) => { setPayMethod(m); const terms = payTermsFor(m); if (terms.length && !terms.includes(payTerm)) setPayTerm(terms[0]); };
    const [quoteBusy, setQuoteBusy] = useState(false);
    const [formBusy, setFormBusy] = useState('');   // holds the key of the running form

    // Shared commercial payload used by EVERY commercial form (Quotation, ATAP,
    // Declaration, Insurance, All-ZIP). They all consume the same getFormData
    // shape, so one builder feeds them all. Single price source for the SELECTED
    // option (panel − discount + battery + add-ons) → matches proposal + WhatsApp.
    /** Every number the loan structure needs, from the real split and one LTV. */
    const buildLoanFigures = ({ c80, c20, cSst, ltv, active }) => {
        const round2 = (n) => Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;
        const pct = Math.min(100, Math.max(1, Number(ltv) || 100));
        const realTotal = round2(c80 + c20 + cSst);
        if (!active || pct >= 100) {
            return { active: false, ltv: pct, markup: 0, contractTotal: realTotal, realTotal,
                l80: c80, l20: c20, lSst: cSst, loanAmount: realTotal, deposit: 0, creditNote: 0 };
        }
        /* SST is marked up WITH the price, not left behind. Financing only the pre-tax
           amount leaves the bank's share short by its own cut of the tax - a 90% loan
           left the customer RM 1,948 to find on a RM 1.6m job, which is not "100%
           financed" by any reading. Marking the whole total up means LTV x contract is
           the real total to the sen, and the customer pays nothing.
           (user 2026-10-17: "Sst yes. Inclusive in loan") */
        const f = 100 / pct;
        const l80 = round2(c80 * f);
        const l20 = round2(c20 * f);
        const lSst = round2(cSst * f);          // stays exactly 6% of the marked-up service line
        const contractTotal = round2(l80 + l20 + lSst);
        const markup = round2(contractTotal - realTotal);
        // The bank lends its percentage of the invoice - that IS the arrangement, and a
        // second box asking for the approved amount only invited the two to disagree.
        // (user 2026-10-17: "Stil got this input i dunwan the approved one.")
        const loanAmount = round2(contractTotal * pct / 100);
        return {
            active: true, ltv: pct, l80, l20, lSst,
            realTotal, contractTotal, markup,
            loanAmount,
            deposit: Math.max(0, round2(realTotal - loanAmount)),
            creditNote: markup,
        };
    };

    const buildCommercialPayload = () => {
        const P = cardPricing(selectedOpt, selectedCard);
        // Quotation is CASH-based (80/20 + 6% SST on the 20%). When CC is chosen, the
        // converted CC figures ride along as an extra "FINAL CC PRICE" reference row.
        // Deduct-SST: the customer's pre-SST base = custPreSst; the SST shown is the full 6%
        // on it, and a "Less: Discount (deducts SST)" line brings the grand total down to
        // P.grandTotal. Normal: cash-based split. (user 2026-08-10)
        const cashPrice = P.deductOn ? (P.custPreSst || 0) : P.cash;
        const c80 = Math.round(cashPrice * 0.80);
        const c20 = sstPortion(cashPrice);
        const cSst = sstOn(cashPrice);
        const cSstDiscount = P.deductOn ? Math.max(0, cSst - (P.sstPayable || 0)) : 0;
        const cGrandTotal = P.deductOn ? (P.grandTotal || 0) : (c80 + c20 + cSst);
        const isCc = payMethod === 'cc';
        const ccPrice = isCc ? payAdjustedPrice(cashPrice, 'cc', payTerm) : 0;
        const cc80 = isCc ? Math.round(ccPrice * 0.80) : 0;
        const cc20 = isCc ? sstPortion(ccPrice) : 0;
        const ccSst = isCc ? sstOn(ccPrice) : 0;
        // Loan: the same two price lines, marked up by 1/LTV. SST is NOT marked up - it
        // stays 6% of the REAL service portion, because the markup is a financing device
        // and nobody should pay tax on money that is credit-noted away.
        const loanCalc = buildLoanFigures({ c80, c20, cSst, ltv: loanLtv, active: payMethod === 'loan' });
        // Catalogue add-ons → quotation scope-of-work line items (same {id,name,desc,qty,
        // price} shape the residential PDF uses). Price is per-unit; the backend lists the
        // item without a per-line price (customer reads the FINAL TOTAL). (user 2026-06-29)
        const addOnItems = (addonsForKey(selectedOpt) || []).map((r) => {
            const qtyBearing = isQtyBearing(r.unitType);
            const rawQty = qtyBearing ? (Number(r._qty) || 1) : 1;
            const name = (r.itemName || r.name || '').trim();
            if (!name) return null;
            const line = computeAddonAmount(r, rawQty);

            /* QuotationAddOnItem.Qty is an INT. A PerKwp add-on's quantity is the kWp
               itself and is fractional on purpose — defaultQtyFor says so: "a 12.9 kWp
               system is charged for 12.9, not 12 or 13". Sent as-is it cannot convert to
               Int32, the model binder gives up on the WHOLE body, and the quotation fails
               with "the req field is required" as well, which points nowhere near the
               cause. Every CSC system with a fractional kWp hit this.

               A fractional quantity becomes ONE line at the full amount, which is exactly
               what the DTO asks for — "a PerKwp add-on is quoted as one job rather than
               12.9 of something" — and the customer's total is unchanged either way.
               (user 2026-10-20: "cant do quotation can check what error?") */
            const wholeQty = Number.isInteger(rawQty);
            const qty = wholeQty ? rawQty : 1;
            const price = wholeQty && rawQty > 0
                ? Math.round((line.total / rawQty) * 100) / 100
                : line.total;

            return { id: String(r.addonId || r.id || ''), name, desc: (r.description || '').toString(), qty, price };
        }).filter(Boolean);
        return {
            addOnItems,
            companyName: (customerName || '').toUpperCase(),
            address: (customerAddress || '').toUpperCase(),
            date: todayDMY(),
            refNo,
            program: 'ATAP',
            kwp: Number(sel.kwp) || 0,
            // Inverter kWac follows the EDITED inverter on the SELECTED card (user
            // 2026-06-29): when the agent types an Inverter kW, the quotation must print
            // that exact value (e.g. 72kWac), not a re-derived auto size. sel.inverterKw
            // already resolves to the per-card override (cscSys[selectedOpt].inverterKw)
            // when present, else the card's candidate value. Only when BOTH are blank do
            // we fall back to the auto-sized ST-capped inverterFromKwp(kWp) (= `_neededInv`).
            kwac: (Number(sel.inverterKw) > 0)
                ? Number(sel.inverterKw)
                : (_neededInv > 0 ? _neededInv : 0),
            panelQty: Number(sel.panels) || 0,
            panelWatt: String(Number(sel.panelWatt) || 650),
            batteryKwh: Number(battKwhFor(selectedOpt)) || 0,
            batteryBrand: battBrandFor(selectedOpt),
            // Battery qty = physical units, matching the sale line item: GoodWe LV = 16 kWh
            // modules (64 kWh = "4 Units", user 2026-06-21), GoodWe HV = 1; Huawei = 241-kWh
            // blocks (482 kWh = "2 Units").
            batteryQty: (() => {
                const bk = Number(battKwhFor(selectedOpt)) || 0;
                if (bk <= 0) return 0;
                if (battBrandFor(selectedOpt) === BATT_BRAND_HUAWEI) return Math.max(1, Math.round(bk / HUAWEI_BATT_BLOCK_KWH));
                return bk < 100 ? Math.ceil(bk / 16) : 1;
            })(),
            // PIC (person-in-charge) — falls back to the customer contact when blank.
            picSalutation,
            picName: (picName || customerName || '').toUpperCase(),
            picMykad,
            picPosition: (picPosition || '').toUpperCase(),
            picCitizenship,
            picMobile: picMobile || customerPhone || '',
            picEmail: (picEmail || customerEmail || '').toLowerCase(),
            // Authorised / alternate person
            altSalutation,
            altName: (altName || '').toUpperCase(),
            altMykad,
            altPosition: (altPosition || '').toUpperCase(),
            altRelationship,
            altCitizenship,
            altMobile,
            altEmail: (altEmail || '').toLowerCase(),
            // Company details
            companyTin,
            companyOffice,
            companyFax,
            companyEmail: (companyEmail || '').toLowerCase(),
            companyWebsite,
            // Prepared-By (Astern side) signatory — FIXED to the authorising director for
            // all commercial quotations/forms, not the individual salesperson (user 2026-06-23).
            agentName: 'Leng Rong Chen',
            agentPosition: 'Director',
            operationWarrantyYears: operationWarranty || DEFAULT_WARRANTY_TERM,
            defectWarrantyYears: defectWarranty || '1 set',
            insuranceAmount: parseInt(insuranceAmount, 10) || 10000,   // backend expects int — blank → default 10,000
            // Inverter brand → quotation scope line (Huawei prints "Huawei", else the
            // generic "Solis / Goodwe" string-inverter label). (user 2026-08-04)
            commercialInverterBrand: sel.brand || 'goodwe',
            commercialInverterType: sel.type || undefined,   // real choice — not guessed from brand (user 2026-09-22)
            // Commercial pricing 80/20/SST:
            // Only a method that HAS a term gets one appended. A loan no longer does, and
            // sending "loan24" matched no case on the server, so the quotation printed
            // "PAYMENT METHOD: Cash" on a loan quote while the prices said LOAN.
            // (user 2026-10-17: "Payment method still cash after i choose loan")
            commercialPaymentMethod: payMethod === 'cash' ? 'cash'
                : (payTermsFor(payMethod).length > 0 ? `${payMethod}${payTerm}` : payMethod),   // cash | cc24 | cc36 | cc60 | loan
            commercialPrice: cashPrice,
            commercialCashPrice: cashPrice,
            commercialPart80: c80,
            commercialPart20: c20,
            commercialSst: cSst,
            commercialSstDiscount: cSstDiscount,
            commercialGrandTotal: cGrandTotal,
            // CC option (converted) — extra "FINAL CC PRICE" reference row when CC chosen.
            commercialCcTerm: isCc ? payTerm : 0,
            commercialCc80: cc80,
            commercialCc20: cc20,
            commercialCcSst: ccSst,
            commercialFinanceNote: payFinanceNote(payMethod),
            // Loan (financed): the invoice is marked up so the bank's LTV lands on the
            // real price. All of it derives from ONE place, so no page can drift from
            // another. (user 2026-10-17)
            commercialLoanLtv: loanCalc.ltv,
            commercialLoan80: loanCalc.l80,
            commercialLoan20: loanCalc.l20,
            commercialLoanSst: loanCalc.lSst,
            commercialContractValue: loanCalc.contractTotal,
            commercialRealTotal: loanCalc.realTotal,
            commercialLoanMarkup: loanCalc.markup,
            commercialLoanAmount: loanCalc.loanAmount,
            commercialLoanDeposit: loanCalc.deposit,
            commercialDepositRefundable: depositRefundable,
        };
    };

    // CSC → create-sales payload. Battery captured as a flagged line item
    // (qty + total kWh); its cost stays folded into FinalPrice (CSC model).
    const buildSalePayload = () => {
        const p = buildCommercialPayload();
        // Same add-on model as RSC: battery is a flagged line, every catalog add-on
        // is its own line, and the per-card flat "Add-on Price" (if any) is a misc
        // line. Price is SPLIT like RSC so add-ons stay traceable: FinalPrice =
        // system-only (panel after discount), AddOnPrice = battery + add-ons. The
        // sale page sums FinalPrice + AddOnPrice = the all-in commercial cash, so
        // nothing double-counts and the battery/incentive tracing works.
        const P = cardPricing(selectedOpt, selectedCard);
        const s = sysFor(selectedOpt, selectedCard);
        const items = [];

        // Battery — LV < 100 kWh: 16-kWh modules @ RM7800. HV ≥ 100 kWh: commercial battery,
        // per-kWh @ RM1,200 (single line, not 16-kWh packs). (user 2026-07-10)
        const battKwh = Number(battKwhFor(selectedOpt)) || 0;
        const _battBrand = battBrandFor(selectedOpt);
        const _battHuawei = _battBrand === BATT_BRAND_HUAWEI;
        const _battHV = _battHuawei || battKwh >= 100;
        if (battKwh > 0) {
            const battCost = batteryCostFor(battKwh, _battBrand);
            const qty = _battHuawei ? Math.max(1, Math.round(battKwh / HUAWEI_BATT_BLOCK_KWH))
                : _battHV ? 1 : Math.ceil(battKwh / 16);
            const unit = qty > 0 ? Math.round(battCost / qty) : battCost;
            items.push({
                // Category stays 'Battery' so battery incentives still find it; the brand is in
                // the name only for Huawei, so every existing GoodWe line keeps its exact name.
                ItemName: `Battery ${battKwh}kWh${_battHV ? ' HV' : ''}${_battHuawei ? ' (Huawei)' : ''}`,
                Category: 'Battery',
                IsBattery: true,
                Qty: qty,
                BatteryKwh: battKwh,
                UnitPrice: unit,
                LineTotal: battCost,
                SortOrder: 0,
            });
        }

        // Catalog add-ons (same picker as RSC AI BESS 360; base+rate via computeAddonAmount).
        (addonsForKey(selectedOpt) || []).forEach((r, i) => {
            const qtyBearing = isQtyBearing(r.unitType);
            const qty = qtyBearing ? (Number(r._qty) || 1) : 1;
            const total = computeAddonAmount(r, qty).total;
            if (total <= 0 && !r.name) return;
            items.push({
                ItemName: r.name || 'Add-on',
                Category: r.category || null,
                IsBattery: false,
                Qty: qty,
                BatteryKwh: null,
                UnitPrice: qty > 0 ? Math.round((total / qty) * 100) / 100 : total,
                LineTotal: total,
                SortOrder: i + 1,
            });
        });

        // Per-card flat "Add-on Price" field (lumpsum), if the agent typed one.
        const flat = Number(s.addOn) || 0;
        if (flat > 0) {
            items.push({
                ItemName: 'Add-on', Category: null, IsBattery: false,
                Qty: 1, BatteryKwh: null, UnitPrice: flat, LineTotal: flat,
                SortOrder: items.length,
            });
        }

        const addOnSum = items.reduce((sum, it) => sum + (Number(it.LineTotal) || 0), 0);

        return {
            PanelQty: p.panelQty,
            Energy: p.kwp,
            // Match the sale form's exact values (COMMERCIAL; CASH / creditCard36|60).
            // Loan/leasing have no sale-form equivalent → fall back to CASH.
            ProductCategory: 'COMMERCIAL',
            Phase: sharedPhaseMode === '3phase' ? '3phase' : 'single',
            OriginalPrice: P.panel,                       // system gross (excl battery/add-ons)
            FinalPrice: P.panelNet,                       // system after discount (excl add-ons)
            AddOnPrice: addOnSum,                          // battery + add-ons = sum of line items
            // "Deduct SST": persist the SST-deducted customer total as the payment denominator
            // (grand total = system + full SST − discount). Normal sales leave it null (backend
            // fallback) as before. (user 2026-08-10)
            ...(P.deductOn ? { QuotationTotal: P.grandTotal } : {}),
            // The one-button RSC-style discount tags itself kind:'astern' → routes to
            // DiscountByAstern; manually-typed RM/% stays Agent discount. (user 2026-06-25)
            // "Deduct SST" does NOT change the split — the typed discount deducts the SST on
            // the sale page; FinalPrice (= P.panelNet) already reflects the discount, and the
            // deductSst flag rides in FormPayloadJson so the edit page shows it. (user 2026-08-10)
            // A MARK-UP ARRIVES HERE AS A NEGATIVE AGENT DISCOUNT — which is exactly the
            // convention the sale page, the quotation and the commission already use, so
            // nothing downstream needs teaching. It can never land in DiscountByAstern:
            // the Astern tick is hidden in mark-up mode and cleared when the switch moves.
            DiscountByAstern: (cscDisc[selectedOpt]?.kind === 'astern') ? (P.discAmt || 0) : 0,
            DiscountByAgent: (cscDisc[selectedOpt]?.kind === 'astern') ? 0 : (P.discAmt || 0),
            PaymentMethod: payMethod === 'cc' ? `creditCard${payTerm}` : 'CASH',
            DepositOn: null,
            LineItems: items,
            // Snapshot the form payload so the sale can regenerate its forms
            // anywhere (signing link, sale page) without the live calculator. Carry the
            // "Deduct SST" flag so the edit page reconstructs the SST-deducted total.
            FormPayloadJson: JSON.stringify({ ...p, ...(P.deductOn ? { deductSst: true } : {}) }),
            // Carry the company/customer details already typed on the CSC form.
            createCustomerRequest: {
                CustomerName: customerName || '',
                PhoneNumber: customerPhone || '',
                Address: customerAddress || '',
                Email: customerEmail || '',
            },
        };
    };

    // "Send to Sales Bot": stash the exact commercial payload, deep-link Telegram.
    // Customer is keyed in Telegram (replaces any form name); nothing is created until Confirm there.
    const sendToSalesBot = async () => {
        if (sendBotBusy) return;
        const payload = buildSalePayload();
        if (!payload) { setErr('Pick a system option first.'); return; }
        setSendBotBusy(true);
        try {
            const res = await stashBotPayload(payload);
            const code = res?.data?.code;
            const botUser = res?.data?.botUsername || 'Nexwavesalesbot';
            if (!code) { setErr('Could not prepare the sale code — try again.'); return; }
            window.open(`https://t.me/${botUser}?start=NEXSALE_${code}`, '_blank');
        } catch (e) {
            setErr('Send to Sales Bot failed: ' + (e?.response?.data?.message || e?.message || e));
        } finally {
            setSendBotBusy(false);
        }
    };

    // Create the Pending commercial sale straight from the form — no dialog.
    // createAnother=true stays on CSC; false navigates to the Sales list.
    const doSubmitSale = async (createAnother) => {
        if (submitGuardRef.current) return;
        if (!customerName.trim()) { setErr('Company / customer name is required.'); return; }
        const base = buildSalePayload();
        const sig = JSON.stringify(base);
        if (sig === lastSubmittedSig) {
            if (!window.confirm('You have already created a sale with identical data. Create another duplicate anyway?')) return;
        }
        submitGuardRef.current = true;
        setSubmitBusy(true); setErr('');
        try {
            const res = await createSales({ ...base, AgentId: user?.userId, CreatedBy: user?.username });
            setLastSubmittedSig(sig);
            const newId = res?.data?.salesId;
            // Sale created — drop the saved draft so the calculator starts fresh next time.
            try { sessionStorage.removeItem(CSC_DRAFT_KEY); } catch {}
            const canViewDetail = user?.roleRights?.includes('AllSalesList');
            if (createAnother) alert('Pending sale created. You can create another.');
            else navigate(newId && canViewDetail ? `/accounting/sales/${newId}` : '/sales');
        } catch (e) {
            setErr('Failed to create sale: ' + (e?.response?.data?.message || e?.response?.data || e.message));
        } finally {
            setSubmitBusy(false);
            submitGuardRef.current = false;
        }
    };

    const genQuotation = async () => {
        if (!customerName.trim()) { setErr('Company / customer name is required.'); return; }
        if (!sel.systemCost) { setErr('System cost is required.'); return; }
        setErr(''); setQuoteBusy(true);
        try {
            await generateCommercialQuotation2(buildCommercialPayload());
        } catch (e) {
            setErr(`Quotation failed: ${e.message || e}`);
        } finally {
            setQuoteBusy(false);
        }
    };

    // Generic runner for the other commercial forms — per-button busy state keyed
    // by `key`; all share buildCommercialPayload().
    const runCommercialForm = async (key, fn) => {
        if (!customerName.trim()) { setErr('Company / customer name is required.'); return; }
        if (!sel.systemCost) { setErr('System cost is required.'); return; }
        setErr(''); setFormBusy(key);
        try { await fn(buildCommercialPayload()); }
        catch (e) { setErr(`Form failed: ${e.response?.data || e.message || e}`); }
        finally { setFormBusy(''); }
    };

    // ── Draft persistence: keep filled data across page navigation ──
    // Restore once on mount, then save on every input change. Cleared on tab close
    // (sessionStorage) or via the Clear Data button. draftRestoredRef gates the save
    // effect so the initial empty state doesn't overwrite the restored draft.
    const draftRestoredRef = useRef(false);
    /* Apply a saved working set. Used by BOTH the autosaved draft on mount and the Saved
       Sets menu, so a restored history entry can never drift from a restored draft - one
       definition of what "load this back" means. */
    const applyCscSnapshot = (d) => {
        _draftRestoring.current = true;   // gate the re-size strip until restore settles
        const S = (v, set) => { if (v !== undefined) set(v); };
        S(d.bill, setBill); S(d.tariffRate, setTariffRate); S(d.mvPeakKwh, setMvPeakKwh); S(d.mvOffKwh, setMvOffKwh);
        /* AFA IS RESTORED WITH THE REST, and it has to be set BEFORE the usage below.
           This supersedes the 2026-07-03 "CSC always loads No AFA" default, which was
           decided when AFA moved only the Original box. It now sets the rate a bill
           converts at, so forgetting it reopened a saved quote with its usage re-derived
           at the bare tariff - a different system size from the one the customer was
           given. (user 2026-10-26: "AFA changes system size yes")

           Both setters, in this order: _kwhFromBill reads getAfaOn() through
           lvFlatSolarRate, which is MODULE state shared with RSC and not a React value,
           so setAfaOnState alone would leave the line below computing on the old flag. */
        if (d.afaOn !== undefined) { setAfaOn(d.afaOn); setAfaOnState(d.afaOn); }
        setBillKwh(_kwhFromBill(d.bill, d.tariffRate != null ? d.tariffRate : 0.515));
        // AFA is NOT restored from the draft — CSC always defaults to No AFA on
        // load (user 2026-07-03). The agent toggles it on deliberately if needed.
        S(d.operatingDays, setOperatingDays);
        S(d.monthBills, setMonthBills); S(d.monthKwhs, setMonthKwhs); S(d.daysMode, setDaysMode); S(d.weekPlan, setWeekPlan); S(d.billMode, setBillMode); S(d.opStart, setOpStart); S(d.opEnd, setOpEnd);
        S(d.maxDemand, setMaxDemand); S(d.brand, setBrand); S(d.nonDayPct, setNonDayPct); S(d.dayUsagePct, setDayUsagePct);
        S(d.cscAddons, setCscAddons); S(d.cscBattery, setCscBattery); S(d.cscBattBrand, setCscBattBrand);
        S(d.cscDisc, setCscDisc); S(d.cscSstDeduct, setCscSstDeduct); S(d.cscOm, setCscOm); S(d.cscOmPkg, setCscOmPkg);
        S(d.cscSys, setCscSys); S(d.optBasis, setOptBasis);
        S(d.customerName, setCustomerName); S(d.customerPhone, setCustomerPhone);
        S(d.customerEmail, setCustomerEmail); S(d.customerAddress, setCustomerAddress);
        S(d.siteState, setSiteState);
        S(d.includeOutright, setIncludeOutright); S(d.includeSARE, setIncludeSARE); S(d.includeSARE10, setIncludeSARE10); S(d.includeSARE15, setIncludeSARE15);
        S(d.includeLoan, setIncludeLoan); S(d.includeCA, setIncludeCA);
        S(d.includeITA, setIncludeITA); S(d.itaTouched, setItaTouched);
        S(d.sareTariffOverride, setSareTariffOverride); S(d.sareTouched, setSareTouched);
        if (d.payMethod === 'leasing') setPayMethod('loan');   // retired method, read as loan
        S(d.loanPrincipal, setLoanPrincipal); S(d.loanRate, setLoanRate);
        S(d.loanTermMonths, setLoanTermMonths); S(d.loanTermPick, setLoanTermPick); S(d.loanTermRate, setLoanTermRate);
        S(d.refNo, setRefNo); S(d.companyTin, setCompanyTin);
        S(d.companyOffice, setCompanyOffice); S(d.companyFax, setCompanyFax);
        S(d.companyEmail, setCompanyEmail); S(d.companyWebsite, setCompanyWebsite);
        S(d.picSalutation, setPicSalutation); S(d.picName, setPicName);
        S(d.picMykad, setPicMykad); S(d.picPosition, setPicPosition);
        S(d.picCitizenship, setPicCitizenship); S(d.picMobile, setPicMobile);
        S(d.picEmail, setPicEmail);
        S(d.altSalutation, setAltSalutation); S(d.altName, setAltName);
        S(d.altMykad, setAltMykad); S(d.altPosition, setAltPosition);
        S(d.altRelationship, setAltRelationship); S(d.altCitizenship, setAltCitizenship);
        S(d.altMobile, setAltMobile); S(d.altEmail, setAltEmail);
        S(d.operationWarranty, setOperationWarranty); S(d.cleaningTouched, setCleaningTouched); S(d.defectWarranty, setDefectWarranty);
        S(d.insuranceAmount, setInsuranceAmount);
    };
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem(CSC_DRAFT_KEY);
            if (raw) applyCscSnapshot(JSON.parse(raw));
        } catch { /* ignore corrupt draft */ }
        // Enable saving only AFTER the restored state has been committed, so the
        // save effect's first (mount) run with stale-empty values doesn't overwrite
        // the saved draft.
        setTimeout(() => { draftRestoredRef.current = true; _draftRestoring.current = false; }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Everything that makes up a CSC working set. One definition, used by the autosave
       draft and by a Saved Set alike. */
    const buildCscSnapshot = () => ({
            bill, tariffRate, mvPeakKwh, mvOffKwh, afaOn, operatingDays, opStart, opEnd,
            monthBills, monthKwhs, daysMode, weekPlan, billMode,
            maxDemand, nonDayPct, dayUsagePct, brand,
            cscAddons, cscBattery, cscBattBrand, cscDisc, cscSstDeduct, cscOm, cscOmPkg, cscSys, optBasis,
            customerName, customerPhone, customerEmail, customerAddress, siteState,
            includeOutright, includeSARE, includeSARE10, includeSARE15, includeLoan, includeCA,
            includeITA, itaTouched, sareTariffOverride, sareTouched,
            loanPrincipal, loanRate, loanTermMonths, loanTermPick, loanTermRate,
            refNo, companyTin, companyOffice, companyFax, companyEmail, companyWebsite,
            picSalutation, picName, picMykad, picPosition, picCitizenship, picMobile, picEmail,
            altSalutation, altName, altMykad, altPosition, altRelationship,
            altCitizenship, altMobile, altEmail,
            operationWarranty, cleaningTouched, defectWarranty, insuranceAmount,
    });

    useEffect(() => {
        if (!draftRestoredRef.current) return;
        try {
            sessionStorage.setItem(CSC_DRAFT_KEY, JSON.stringify(buildCscSnapshot()));
        } catch { /* quota / serialize errors are non-fatal */ }

        /* AUTO-SAVE A SET, one per customer. (user 2026-10-23: "can auto save set for
           each?")

           I argued against autosaving and the objection no longer holds: pushCscHistory
           REPLACES an entry with the same name rather than taking another slot, so a
           quote being edited keeps overwriting its own set instead of filling all ten
           with one customer.

           Two guards and a delay are what keep it useful rather than noisy:
             - a CUSTOMER NAME, so an untitled half-form never becomes a saved set;
             - a SYSTEM CHOSEN, so it is a real quote and not an opening bill figure;
             - 4 seconds after typing STOPS, because saving on each keystroke would
               rewrite the store a hundred times a minute for no benefit.
           Silent on purpose: the toast belongs to the Save Set button, which is now for
           forcing a save rather than the only way to get one. */
        const name = (customerName || '').trim();
        const hasSystem = (parseFloat(sel?.kwp) || 0) > 0;
        if (!name || !hasSystem) return undefined;
        const t = setTimeout(() => {
            try {
                const kwp = parseFloat(sel?.kwp) || 0;
                const bits = [kwp ? `${kwp} kWp` : '', bill ? `bill RM ${Number(bill).toLocaleString()}` : '']
                    .filter(Boolean).join(' · ');
                setHistory(pushCscHistory({ label: name, subtitle: bits, data: buildCscSnapshot() }));
            } catch { /* a full or blocked store must not break the tab */ }
        }, 4000);
        return () => clearTimeout(t);
    }, [bill, tariffRate, mvPeakKwh, mvOffKwh, afaOn, operatingDays, opStart, opEnd,
        maxDemand, nonDayPct, dayUsagePct, brand,
        cscAddons, cscBattery, cscBattBrand, cscDisc, cscOm, cscOmPkg, cscSys, optBasis,
        customerName, customerPhone, customerEmail, customerAddress, siteState,
        includeOutright, includeSARE, includeSARE10, includeLoan, includeCA,
        includeITA, itaTouched, sareTariffOverride, sareTouched,
        loanPrincipal, loanRate, loanTermMonths, loanTermPick, loanTermRate,
        refNo, companyTin, companyOffice, companyFax, companyEmail, companyWebsite,
        picSalutation, picName, picMykad, picPosition, picCitizenship, picMobile, picEmail,
        altSalutation, altName, altMykad, altPosition, altRelationship,
        altCitizenship, altMobile, altEmail,
        operationWarranty, defectWarranty, insuranceAmount]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, bgcolor: '#F8FAFC', fontFamily: PP }}>
            {/* Saved-set feedback. A toast rather than an alert: saving is a side errand,
                not something to interrupt the quote for. */}
            <Snackbar open={!!historyNote} autoHideDuration={2600}
                onClose={() => setHistoryNote('')} message={historyNote}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />

            {/* Clear-data confirm — remind that no sale was created yet; offer to create first. */}
            <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700 }}>Clear all data?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontFamily: PP, fontSize: '0.9rem', color: '#475569' }}>
                        You haven't created a sale yet. Clearing will discard all entered data. Create the sale first?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
                    <Button onClick={() => setClearDialogOpen(false)} sx={{ fontFamily: PP, textTransform: 'none', color: '#64748B' }}>
                        Cancel
                    </Button>
                    <Button onClick={() => { setClearDialogOpen(false); doClearData(); }} variant="outlined"
                        sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 700, borderColor: '#EF4444', color: '#EF4444' }}>
                        Clear anyway
                    </Button>
                    <Button onClick={() => { setClearDialogOpen(false); doSubmitSale(false); }} variant="contained"
                        sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 700, boxShadow: 'none', background: 'linear-gradient(135deg,#16A34A,#15803D)' }}>
                        Create Sale
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Top-level controls — laid out one row per control with aligned labels.
                ATAP/SELCo is the MASTER mode switch: ATAP = NEM (MD optional, caps export,
                sizing 50/70/MAX MD); SELCo = zero export (no MD, sizing 50/70/100). */}
            {(() => {
                const LBL = { minWidth: 56, fontSize: '0.72rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, flexShrink: 0 };
                const TG = { '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.74rem', fontWeight: 700, textTransform: 'none', py: 0.4, px: 1.6, color: COLORS.sub, borderColor: COLORS.border, borderRadius: '8px !important' } };
                const SEL_BLUE = { '& .Mui-selected': { background: 'linear-gradient(135deg,#1A8CFF,#0F62CC) !important', color: '#fff !important', borderColor: 'transparent !important' } };
                const SEL_GREEN = { '& .Mui-selected': { background: 'linear-gradient(135deg,#16A34A,#15803D) !important', color: '#fff !important', borderColor: 'transparent !important' } };
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.75 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Button size="small" variant="outlined" onClick={() => setClearDialogOpen(true)}
                                sx={{ fontFamily: PP, fontSize: '0.76rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', borderColor: '#CBD5E1', color: '#64748B', '&:hover': { borderColor: '#EF4444', color: '#EF4444', bgcolor: 'rgba(239,68,68,0.06)' } }}>
                                Clear Data
                            </Button>
                            {/* Saving is deliberate, not automatic: an autosave of every keystroke
                                would fill all ten slots with one half-typed quote. */}
                            {/* Still here, now as a way to FORCE a save rather than the only
                                way to get one - auto-save waits 4s after typing stops, and
                                this does it immediately with a confirmation. */}
                            <Button size="small" variant="outlined" onClick={saveToHistory}
                                sx={{ fontFamily: PP, fontSize: '0.76rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', borderColor: '#CBD5E1', color: COLORS.primary, '&:hover': { borderColor: COLORS.primary, bgcolor: 'rgba(26,140,255,0.06)' } }}>
                                Save Now
                            </Button>
                            <Button size="small" variant="outlined" disabled={history.length === 0}
                                onClick={(e) => setHistoryAnchor(e.currentTarget)}
                                sx={{ fontFamily: PP, fontSize: '0.76rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', borderColor: '#CBD5E1', color: '#64748B' }}>
                                Saved Sets ({history.length}/{CSC_HISTORY_MAX})
                            </Button>
                            <Menu anchorEl={historyAnchor} open={!!historyAnchor} onClose={() => setHistoryAnchor(null)}>
                                {history.map((h) => (
                                    <MenuItem key={h.id} onClick={() => restoreFromHistory(h)}
                                        sx={{ fontFamily: PP, fontSize: '0.8rem', display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', fontWeight: 700, color: COLORS.text }}>
                                                {h.label}
                                            </Typography>
                                            <Typography sx={{ fontFamily: PP, fontSize: '0.68rem', color: COLORS.sub }}>
                                                {[h.subtitle, formatSavedAt(h.savedAt)].filter(Boolean).join(' · ')}
                                            </Typography>
                                        </Box>
                                        <Box component="span" role="button" tabIndex={0}
                                            onClick={(e) => { e.stopPropagation(); setHistory(removeCscHistory(h.id)); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setHistory(removeCscHistory(h.id)); } }}
                                            sx={{ fontSize: '0.7rem', color: COLORS.sub, cursor: 'pointer', '&:hover': { color: '#EF4444' } }}>
                                            Remove
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <Typography sx={LBL}>Phase</Typography>
                            <ToggleButtonGroup exclusive size="small" value={sharedPhaseMode}
                                onChange={(_, v) => { if (v && setSharedPhaseMode) setSharedPhaseMode(v); }}
                                sx={{ ...TG, ...SEL_BLUE }}>
                                <ToggleButton value="single">Single Phase</ToggleButton>
                                <ToggleButton value="3phase">3 Phase</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        {sharedPhaseMode !== '3phase' && sizingBreakdown?.single1pCapped && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <Box sx={{ minWidth: 56, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#B45309', fontFamily: PP }}>
                                    Bill needs &gt;5 kW &mdash; single phase caps at 5 kW. Use 3 Phase.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                );
            })()}

            {/* ───────── Section 1 — CSC Calculator ───────── */}
            <SectionHeader title="Section 1 — CSC Calculator" />

            {/* Full-width LEFT (inputs) / RIGHT (system card + outputs) — RSC-style. */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(300px, 1fr) minmax(0, 2.4fr)' }, gap: 2, alignItems: 'start' }}>

            {/* LEFT column — shared inputs */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            {/* White shared-input card — inputs stack one per row in the narrow left
                column so wide fields (Tariff, Operating Hours) aren't truncated. */}
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 20 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                    {/* Company Name up top — same field as the Project Info company name (user 2026-06-23) */}
                    <Box>
                        <Label>Company Name</Label>
                        <TextField fullWidth size="small" value={customerName} onChange={(e) => setCustomerName(e.target.value)}
                            placeholder="customer / company"
                            InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                    </Box>
                    {/* Import (optional) — auto-fill from a TNB e-bill (PDF) or a 30-min load-profile
                        (CSV/Excel). Usage (kWh) drives the calc — more accurate than bill ÷ tariff.
                        Leave blank to key the numbers manually below. (user 2026-07-24) */}
                    <Box sx={{ p: 1.25, bgcolor: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: 2 }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: PP, mb: 0.75 }}>Import (optional)</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button component="label" size="small" variant="outlined" sx={{ fontFamily: PP, fontSize: '0.74rem', textTransform: 'none', borderRadius: '8px' }}>
                                Upload TNB Bill (PDF)
                                <input hidden type="file" accept="application/pdf,.pdf" onChange={(e) => { handleTnbUpload(e.target.files?.[0]); e.target.value = ''; }} />
                            </Button>
                            <Button component="label" size="small" variant="outlined" sx={{ fontFamily: PP, fontSize: '0.74rem', textTransform: 'none', borderRadius: '8px' }}>
                                Upload Load Profile (CSV/Excel)
                                <input hidden type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { handleLoadProfileUpload(e.target.files?.[0]); e.target.value = ''; }} />
                            </Button>
                        </Box>
                        {uploadMsg && <Typography sx={{ fontSize: '0.68rem', color: '#0F766E', fontFamily: PP, mt: 0.75 }}>{uploadMsg}</Typography>}
                    </Box>
                    {isTou ? (
                        <>
                            {/* Time of Use: key what the bill prints — peak kWh, off-peak kWh and
                                the Maximum Demand below. The RM bill is CALCULATED from them (energy +
                                capacity/network on MD + retail + KWTBB), never bill ÷ rate. (user 2026-09-23) */}
                            <Box>
                                <Label>Peak Usage (kWh) <span style={{ color: COLORS.sub, fontWeight: 400 }}>· Penggunaan Puncak</span></Label>
                                <TextField fullWidth size="small" type="text" inputMode="numeric"
                                    value={mvPeakKwh === '' ? '' : Number(String(mvPeakKwh).replace(/,/g, '')).toLocaleString('en-US')}
                                    onChange={(e) => setMvPeakKwh(e.target.value.replace(/[^\d.]/g, ''))}
                                    placeholder="e.g. 86,258"
                                    InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                            </Box>
                            <Box>
                                <Label>Off-Peak Usage (kWh) <span style={{ color: COLORS.sub, fontWeight: 400 }}>· Luar Puncak</span></Label>
                                <TextField fullWidth size="small" type="text" inputMode="numeric"
                                    value={mvOffKwh === '' ? '' : Number(String(mvOffKwh).replace(/,/g, '')).toLocaleString('en-US')}
                                    onChange={(e) => setMvOffKwh(e.target.value.replace(/[^\d.]/g, ''))}
                                    placeholder="e.g. 118,565"
                                    InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                            </Box>
                            <Box>
                                <Label>Monthly Bill (RM) <span style={{ color: COLORS.sub, fontWeight: 400 }}>· calculated</span></Label>
                                <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: '8px', px: 1.5, py: 0.9, bgcolor: '#F8FAFC' }}>
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.9rem', fontWeight: 700, color: COLORS.dark }}>
                                        {realBill > 0 ? fmtRM(realBill) : '—'}
                                    </Typography>
                                    {mvBillCalc && realBill > 0 && (
                                        <Typography sx={{ fontSize: '0.64rem', color: COLORS.sub, fontFamily: PP, mt: 0.3, lineHeight: 1.5 }}>
                                            Energy {fmtRM(mvBillCalc.energy)} · Capacity + Network {fmtRM(mvBillCalc.capacity + mvBillCalc.network)} ({Math.round(mvBillCalc.mdKw).toLocaleString('en-US')} kW MD)
                                            {' · '}Retail {fmtRM(mvBillCalc.retail)}{mvBillCalc.afa ? ` · AFA ${fmtRM(mvBillCalc.afa)}` : ''} · KWTBB {fmtRM(mvBillCalc.kwtbb)}
                                        </Typography>
                                    )}
                                    {!(mvCtx.mdKw > 0) && (
                                        <Typography sx={{ fontSize: '0.64rem', color: '#B45309', fontFamily: PP, mt: 0.3 }}>
                                            Enter the Maximum Demand (kW P) from the bill — capacity and network are charged on it.
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.25, flexWrap: 'wrap' }}>
                                    <Label>Monthly Bill (RM)</Label>
                                    <ToggleButtonGroup exclusive size="small" value={billMode}
                                        onChange={(_, v) => { if (v) setBillMode(v); }}
                                        sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.64rem', fontWeight: 700, textTransform: 'none', py: 0, px: 0.9, borderRadius: '6px !important' } }}>
                                        <ToggleButton value="single">single</ToggleButton>
                                        <ToggleButton value="custom">custom</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                                {billMode === 'single' ? (
                                    <TextField fullWidth size="small" type="text" inputMode="numeric"
                                        value={bill === '' ? '' : Number(String(bill).replace(/,/g, '')).toLocaleString('en-US')}
                                        onChange={(e) => { const v = e.target.value.replace(/[^\d.]/g, ''); setBill(v); setBillKwh(_kwhFromBill(v, tariffRate)); }}
                                        placeholder="e.g. 8,500"
                                        InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                                ) : (
                                    <Box>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
                                            {monthBills.map((b, i) => (
                                                <Box key={i}>
                                                    <Typography sx={{ fontSize: '0.6rem', color: COLORS.sub, fontFamily: PP, mb: 0.15 }}>
                                                        {monthLabel(i)}
                                                    </Typography>
                                                    <TextField fullWidth size="small" type="text" inputMode="numeric"
                                                        value={b === '' ? '' : Number(String(b).replace(/,/g, '')).toLocaleString('en-US')}
                                                        onChange={(e) => setMonthBill(i, e.target.value.replace(/[^\d.]/g, ''))}
                                                        InputProps={{ sx: { fontFamily: PP, fontSize: '0.78rem', borderRadius: '8px' } }}
                                                        inputProps={{ style: { padding: '6px 8px' } }} />
                                                </Box>
                                            ))}
                                        </Box>
                                        <Typography sx={{ fontSize: '0.62rem', color: COLORS.sub, fontFamily: PP, mt: 0.5 }}>
                                            Fill only the months you have. The rest of the year repeats them.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            <Box>
                                <Label>Monthly Usage (kWh)</Label>
                                <TextField fullWidth size="small" type="text" inputMode="numeric"
                                    value={billKwh === '' ? '' : Number(String(billKwh).replace(/,/g, '')).toLocaleString('en-US')}
                                    onChange={(e) => { const v = e.target.value.replace(/[^\d.]/g, ''); setBillKwh(v); setBill(_billFromKwh(v, tariffRate)); }}
                                    placeholder="e.g. 16,500"
                                    InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                            </Box>
                        </>
                    )}
                    <Box>
                        <Label>Tariff Type</Label>
                        <Select fullWidth size="small" value={tariffRate} onChange={(e) => { const r = Number(e.target.value); setTariffRate(r); setBillKwh(_kwhFromBill(bill, r)); }}
                            sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                            {TARIFFS.map(t => (
                                <MenuItem key={t.rate} value={t.rate} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                                    {(isMvTariff(t.rate) || isLvTouTariff(t.rate)) ? t.label : `${t.label} — RM ${t.rate}/kWh`}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                    {/* ── BESS peak shaving — Medium Voltage only (user 2026-09-25) ──────────────
                        Solar cannot touch the capacity + network charges, which are most of an MV
                        bill; a battery can, by holding the maximum demand down inside the peak
                        window. It needs the 30-min load profile: the SHAPE decides how much can be
                        shaved, so the tick stays disabled until one is uploaded. */}
                    {isMv && (
                        <Box>
                            <FormControlLabel
                                disabled={!bessCanOffer}
                                control={<Checkbox size="small" checked={bessOn && bessCanOffer} onChange={(e) => setBessOn(e.target.checked)} />}
                                label={<Typography sx={{ fontSize: '0.85rem', fontFamily: PP, fontWeight: 600, color: bessCanOffer ? COLORS.text : COLORS.sub }}>
                                    Add BESS — peak shaving
                                </Typography>}
                            />
                            <Typography sx={{ fontSize: '0.66rem', color: bessCanOffer ? COLORS.sub : '#B45309', fontFamily: PP, mt: -0.5, mb: bessOn ? 1 : 0 }}>
                                {bessCanOffer
                                    ? `Shaves the maximum demand — worth RM ${MV_DEMAND_RATE_PER_KW.toFixed(2)}/kW a month (capacity + network).`
                                    : 'Upload the TNB 30-min Load Profile above to size a battery — a bill alone cannot show the peak shape.'}
                            </Typography>
                            {bessOn && bessCanOffer && (
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 0.5 }}>
                                    <Box sx={{ gridColumn: '1 / -1' }}>
                                        <Label>Battery</Label>
                                        <ToggleButtonGroup fullWidth size="small" exclusive value={bessBrand}
                                            onChange={(_, v) => { if (v) setBessBrand(v); }}
                                            sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', py: 0.4, borderRadius: '8px !important' }, '& .Mui-selected': { bgcolor: `${COLORS.primary} !important`, color: '#fff !important' } }}>
                                            <ToggleButton value={BATT_BRAND_GOODWE}>GoodWe 261 kWh</ToggleButton>
                                            <ToggleButton value={BATT_BRAND_HUAWEI}>Huawei 241 kWh</ToggleButton>
                                        </ToggleButtonGroup>
                                        <Typography sx={{ fontSize: '0.64rem', color: COLORS.sub, fontFamily: PP, mt: 0.3 }}>
                                            125 kW per block · charged off-peak, discharged 2pm–10pm weekdays
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Label>Units <span style={{ color: COLORS.sub, fontWeight: 400 }}>· blank = best</span></Label>
                                        <TextField fullWidth size="small" type="number" value={bessUnits}
                                            onChange={(e) => setBessUnits(e.target.value.replace(/[^\d]/g, ''))}
                                            placeholder={bess?.best ? String(bess.best.units) : 'auto'}
                                            InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                                    </Box>
                                    <Box>
                                        <Label>Price (RM/kWh)</Label>
                                        <TextField fullWidth size="small" type="number" value={bessPrice}
                                            onChange={(e) => setBessPrice(e.target.value.replace(/[^\d.]/g, ''))}
                                            placeholder={String(bessBrand === BATT_BRAND_HUAWEI ? 1350 : 1200)}
                                            InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                                    </Box>
                                    <Box>
                                        <Label>MD credit (%)</Label>
                                        <TextField fullWidth size="small" type="number" value={bessCredit}
                                            onChange={(e) => setBessCredit(e.target.value.replace(/[^\d]/g, ''))}
                                            InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                                        <Typography sx={{ fontSize: '0.62rem', color: COLORS.sub, fontFamily: PP, mt: 0.3 }}>
                                            Safety margin on the shaved kW
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                    {/* AFA toggle (mirrors RSC) — OFF (default) treats the entered Monthly Bill as
                        the ex-AFA figure, so the Original breakdown reconciles to it. ON adds the
                        AFA surcharge line (rate from General Settings) on top. */}
                    <Box>
                        <Label>AFA Surcharge</Label>
                        <ToggleButtonGroup size="small" exclusive value={afaOn}
                            /* Re-derive the kWh from the bill the agent typed. The BILL is
                               the anchor - it is what the customer handed them - so
                               flipping AFA moves the consumption behind it, not the bill.
                               setAfaOn() first: _kwhFromBill reads the module flag through
                               lvFlatSolarRate, so the state setter alone would compute the
                               new kWh with the OLD rate. (user 2026-10-26) */
                            onChange={(_, v) => {
                                if (v === null) return;
                                setAfaOn(v); setAfaOnState(v);
                                if (!isTou) setBillKwh(_kwhFromBill(bill, tariffRate));
                            }}
                            sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', px: 1.5, py: 0.5, borderRadius: '8px' }, '& .Mui-selected': { bgcolor: `${COLORS.primary} !important`, color: '#fff !important' } }}>
                            <ToggleButton value={true}>With AFA</ToggleButton>
                            <ToggleButton value={false}>No AFA</ToggleButton>
                        </ToggleButtonGroup>
                        {/* The rate itself, for ToU. A commercial bill prints its own AFA per
                            billing period and it is usually a REBATE — type it with the minus
                            sign exactly as the bill shows it. Blank = the General Settings
                            rate. (user 2026-10-31) */}
                        {afaOn && (
                            <TextField fullWidth size="small" type="number" sx={{ mt: 0.75 }}
                                value={afaRateInput}
                                onChange={(e) => setAfaRateInput(e.target.value)}
                                placeholder={`${afaDefaultRef.current ?? getAfaRate()} (from settings)`}
                                label="AFA rate (sen/kWh) · negative on a rebate month"
                                InputLabelProps={{ shrink: true, sx: { fontFamily: PP, fontSize: '0.8rem' } }}
                                inputProps={{ step: 0.01 }}
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                        )}
                        <Typography sx={{ fontSize: '0.66rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>
                            {isMv
                                ? (afaOn
                                    ? `AFA ${getAfaRate()} sen/kWh ${getAfaRate() < 0 ? 'REBATED' : 'added'} — the bill should match TNB's Caj Semasa. Your bill prints the rate for its own period; copy it here, minus sign and all.`
                                    : 'No AFA — the bill shown is before AFA. If your bill has an AFA line, it will NOT tally until you switch this on and enter the rate.')
                                : (afaOn
                                    ? `AFA ${getAfaRate()} sen/kWh is inside the bill you typed — usage is ${(tariffRate + getAfaRate() / 100).toFixed(4)}/kWh.`
                                    : 'No AFA — usage is the bill ÷ tariff. Original matches the bill you typed.')}
                        </Typography>
                    </Box>
                    {/* No SELCo checkbox — the mode AUTO-decides from Max Demand below:
                        blank = SELCo (zero export), entered = ATAP. (user 2026-06-23) */}
                    <Box>
                        <Label>Maximum Demand (kW) <span style={{ color: COLORS.sub, fontWeight: 400 }}>{isMv ? '· required for MV — the peak kW (kW P)' : '· blank = SELCo'}</span></Label>
                        <TextField fullWidth size="small" type="number" value={maxDemand} onChange={(e) => setMaxDemand(e.target.value)}
                            placeholder="blank = SELCo (zero export)"
                            InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                        <Typography sx={{ fontSize: '0.66rem', color: sharedPhaseMode === '3phase' ? COLORS.sub : '#B45309', fontFamily: PP, mt: 0.4 }}>
                            {(parseFloat(maxDemand) || 0) > 0
                                ? 'ATAP — export capped to your Max Demand.'
                                : sharedPhaseMode === '3phase'
                                    ? 'Blank = SELCo (zero export), sized from the bill. Enter MD for ATAP (export to demand).'
                                    : 'Not used on single phase (capped at 5 kW) — switch to 3 Phase to apply.'}
                        </Typography>
                    </Box>
                    {/* No "Follow operating hours" checkbox — operating days/hours are always
                        considered, and daytime % follows them by default. (user 2026-06-23) */}
                    <Box>
                        {/* Two ways to SAY the same schedule, one way to COMPUTE it. Days per
                            month is converted straight back to a weekly figure (÷4.345, the
                            same 52/12 the engine uses the other way), so switching the input
                            can never change the method behind it.
                            (user 2026-10-23: "can we do aso another onme which is how many
                             days per month") */}
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.25 }}>
                            <Label>Operating Days</Label>
                            <ToggleButtonGroup exclusive size="small" value={daysMode}
                                onChange={(_, v) => { if (v) setDaysMode(v); }}
                                sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.64rem', fontWeight: 700, textTransform: 'none', py: 0, px: 0.9, borderRadius: '6px !important' } }}>
                                <ToggleButton value="week">/ week</ToggleButton>
                                <ToggleButton value="custom">custom</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        {daysMode === 'custom' ? (
                            <Box>
                                {[['wd', 'Mon \u2013 Fri'], ['sat', 'Saturday'], ['sun', 'Sunday']].map(([k, label]) => {
                                    const g = weekPlan[k];
                                    return (
                                        <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.6 }}>
                                            <Checkbox size="small" checked={g.on} sx={{ p: 0.25 }}
                                                onChange={(e) => setPlan(k, { on: e.target.checked })} />
                                            <Typography sx={{ fontSize: '0.72rem', fontFamily: PP, color: g.on ? COLORS.text : COLORS.sub, minWidth: 64 }}>
                                                {label}
                                            </Typography>
                                            <TextField size="small" type="number" disabled={!g.on} value={g.start}
                                                onChange={(e) => setPlan(k, { start: Math.max(0, Math.min(23, parseInt(e.target.value, 10) || 0)) })}
                                                inputProps={{ min: 0, max: 23, style: { padding: '4px 6px', width: 34, fontSize: '0.74rem', textAlign: 'center' } }} />
                                            <Typography sx={{ fontSize: '0.72rem', color: COLORS.sub }}>–</Typography>
                                            <TextField size="small" type="number" disabled={!g.on} value={g.end}
                                                onChange={(e) => setPlan(k, { end: Math.max(1, Math.min(24, parseInt(e.target.value, 10) || 0)) })}
                                                inputProps={{ min: 1, max: 24, style: { padding: '4px 6px', width: 34, fontSize: '0.74rem', textAlign: 'center' } }} />
                                            <Typography sx={{ fontSize: '0.64rem', color: COLORS.sub, fontFamily: PP, whiteSpace: 'nowrap' }}>
                                                {g.on ? `${Math.max(0, g.end - g.start)}h` : 'closed'}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                                <Typography sx={{ fontSize: '0.64rem', color: COLORS.sub, fontFamily: PP, mt: 0.3 }}>
                                    = {effectiveOperatingDays.toFixed(1)} full days / week.
                                    {weekPlan.sat.on && Math.max(0, weekPlan.sat.end - weekPlan.sat.start) < Math.max(1, weekPlan.wd.end - weekPlan.wd.start)
                                        ? ' A short Saturday counts as the fraction of a weekday it actually runs.'
                                        : ''}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'stretch', height: 40, border: '1px solid #C4CDD5', borderRadius: '8px', overflow: 'hidden' }}>
                                <Button onClick={() => setOperatingDays(d => Math.max(1, (Number(d) || 7) - 1))}
                                    sx={{ minWidth: 46, borderRadius: 0, fontSize: '1.3rem', lineHeight: 1, color: '#334155', borderRight: '1px solid #E2E8F0' }}>−</Button>
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: PP, fontSize: '0.95rem', fontWeight: 600, color: '#0F172A' }}>
                                    {operatingDays}<Typography component="span" sx={{ ml: 0.5, fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP }}>/ week</Typography>
                                </Box>
                                <Button onClick={() => setOperatingDays(d => Math.min(7, (Number(d) || 7) + 1))}
                                    sx={{ minWidth: 46, borderRadius: 0, fontSize: '1.3rem', lineHeight: 1, color: '#334155', borderLeft: '1px solid #E2E8F0' }}>+</Button>
                            </Box>
                        )}
                    </Box>
                    {daysMode !== 'custom' && (
                    <Box>
                        <Label>Operating Hours <span style={{ color: COLORS.sub, fontWeight: 400 }}>· start – end</span></Label>
                        {/* Steppers (±1h) instead of number fields — easier on mobile. Solar window
                            is 08:00–18:00; the daytime % follows these hours. (user 2026-07-24) */}
                        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                            {[[opStart, setOpStart], [opEnd, setOpEnd]].map(([val, setter], i) => (
                                <Box key={i} sx={{ display: 'contents' }}>
                                    {i === 1 && <Typography sx={{ color: COLORS.sub, fontFamily: PP }}>–</Typography>}
                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'stretch', height: 40, border: '1px solid #C4CDD5', borderRadius: '8px', overflow: 'hidden' }}>
                                        {/* start stays below end, end stays above start — never start > end. */}
                                        <Button onClick={() => { setter(v => Math.max(i === 0 ? 0 : opStart + 1, (Number(v) || 0) - 1)); setDayUsagePct(''); }}
                                            sx={{ minWidth: 36, borderRadius: 0, fontSize: '1.2rem', lineHeight: 1, color: '#334155', borderRight: '1px solid #E2E8F0' }}>−</Button>
                                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: PP, fontSize: '0.9rem', fontWeight: 600, color: '#0F172A' }}>
                                            {/* End can cross midnight for 24h/late ops: 30 → "06:00 (+1)". */}
                                            {val > 24 ? val - 24 : val}<Typography component="span" sx={{ ml: 0.2, fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP }}>:00{val > 24 ? ' (+1)' : ''}</Typography>
                                        </Box>
                                        {/* End may step past midnight up to start+24 (a full 24h day). */}
                                        <Button onClick={() => { setter(v => Math.min(i === 0 ? opEnd - 1 : opStart + 24, (Number(v) || 0) + 1)); setDayUsagePct(''); }}
                                            sx={{ minWidth: 36, borderRadius: 0, fontSize: '1.2rem', lineHeight: 1, color: '#334155', borderLeft: '1px solid #E2E8F0' }}>+</Button>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    )}
                    {daysMode === 'custom' ? (
                        /* Custom works the figure out from the whole week - hours in the sun
                           divided by hours open, across Mon-Fri, Saturday and Sunday. There is
                           nothing to set, so there is no stepper: a control that cannot change
                           the answer is worse than no control. */
                        <Box>
                            <Label>Daytime usage (%) <span style={{ color: COLORS.sub, fontWeight: 400 }}>· from your week</span></Label>
                            <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: '8px', px: 1.5, py: 0.9, bgcolor: '#F8FAFC' }}>
                                <Typography sx={{ fontFamily: PP, fontSize: '0.95rem', fontWeight: 700, color: COLORS.dark }}>
                                    {_effDayPct}%
                                </Typography>
                                <Typography sx={{ fontSize: '0.64rem', color: COLORS.sub, fontFamily: PP, mt: 0.3 }}>
                                    Hours inside 08:00–18:00 ÷ hours open, weighted across the week.
                                    {_effDayPct < 100 ? ' Hours outside the solar window cannot be served by solar.' : ''}
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                    <Box>
                        <Label>Daytime usage (%) <span style={{ color: COLORS.sub, fontWeight: 400 }}>· used during solar hours</span></Label>
                        {/* Stepper (±5%) instead of a number field — mirrors Operating Days, reliable
                            on mobile. Blank = auto (follows operating hours); tapping ± sets a manual
                            override + re-syncs the hours. (user 2026-07-24) */}
                        <Box sx={{ display: 'flex', alignItems: 'stretch', height: 40, border: '1px solid #C4CDD5', borderRadius: '8px', overflow: 'hidden' }}>
                            <Button onClick={() => { const v = Math.max(42, _effDayPct - 5); setDayUsagePct(String(v)); syncHoursFromDayPct(String(v)); }}
                                sx={{ minWidth: 46, borderRadius: 0, fontSize: '1.3rem', lineHeight: 1, color: '#334155', borderRight: '1px solid #E2E8F0' }}>−</Button>
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: PP, fontSize: '0.95rem', fontWeight: 600, color: '#0F172A' }}>
                                {_effDayPct}<Typography component="span" sx={{ ml: 0.5, fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP }}>%</Typography>
                            </Box>
                            <Button onClick={() => { const v = Math.min(100, _effDayPct + 5); setDayUsagePct(String(v)); syncHoursFromDayPct(String(v)); }}
                                sx={{ minWidth: 46, borderRadius: 0, fontSize: '1.3rem', lineHeight: 1, color: '#334155', borderLeft: '1px solid #E2E8F0' }}>+</Button>
                        </Box>
                        <Typography sx={{ fontSize: '0.66rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>
                            {String(dayUsagePct).trim() !== ''
                                ? <>Manual {_effDayPct}% — <Box component="span" onClick={() => setDayUsagePct('')} sx={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 600 }}>reset to auto</Box> ({_computedDayPct}%).</>
                                : `Auto: ${_computedDayPct}% daytime (${Math.max(0, Math.min(opEnd, 18) - Math.max(opStart, 8))}h in sun ÷ ${Math.max(0, opEnd - opStart)}h open). Tap ± to override.`}
                        </Typography>
                    </Box>
                    )}
                </Box>
                {/* Solar window explainer: how the export judgement works */}
                <Typography sx={{ fontSize: '0.66rem', color: COLORS.sub, fontFamily: PP, mt: 1.25 }}>
                    Solar generates ~08:00–18:00. kWh produced DURING your operating hours is self-used; kWh produced OUTSIDE them (and on non-operating days) is exported. Operating days/week and hours drive how much is self-used vs exported.
                </Typography>
            </div>
            </Box>{/* /LEFT column */}

            {/* RIGHT column — system card (basis chooser inside) + outputs */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', '& > *': { flex: '1 1 280px', minWidth: 0 } }}>
                {/* Option A retired (user 2026-06-29) — only Option B (with battery) shows. */}
                <OptionCard
                    card={optBDisplay}
                    /* The spread, when real months were entered. The card then names the
                       month it is drawing and prints average / highest / lowest beneath it,
                       so nobody reads one month as every month.
                       (user 2026-10-24: "then u have 3 more monthly saving which is
                        average, highest, lowest") */
                    savings={(billsAnalysis && billsAnalysis.count >= 2) ? {
                        avg: billsAnalysis.avgSaving,
                        high: billsAnalysis.maxSaving,
                        low: billsAnalysis.minSaving,
                        peakBill: billsAnalysis.peakMonth?.bill || 0,
                        count: billsAnalysis.count,
                    } : null}
                    label={`${tr(lang, 'Recommended Package', '推荐配套')}${_csMode === 'selco' ? ' (SELCo)' : ''}`}
                    lang={lang}
                    basis={basisB}
                    hasMd={isAtap}
                    mdKw={parseFloat(maxDemand) || 0}
                    tariffRate={calcRate}
                    billFn={billFn}
                    selected={false}
                    gradient="linear-gradient(135deg,#064e3b,#065f46)"
                    sys={sysFor('b', optB)}
                    onSysChange={(f, v) => setSysField('b', f, v)}
                    priceForPanels={priceForPanels}
                    battery={battKwhFor('b')}
                    batteryOff={_bessOwnsBattery}
                    bessLabel={bessPick ? `${bessPick.units} × ${bessUnitKwh} kWh = ${(bessPick.units * bessUnitKwh).toLocaleString()} kWh` : ''}
                    onBatteryChange={(v) => setCardBatteryRaw('b', v)}
                    onBatteryBlur={(v) => setCardBattery('b', v)}
                    battBrand={battBrandFor('b')}
                    onBattBrandChange={(v) => setCardBattBrand('b', v)}
                    disc={discFor('b')}
                    onDiscChange={(patch) => setCardDisc('b', patch)}
                    asternDiscAmt={asternDiscFor('b')}
                    onApplyAstern={() => applyAstern('b')}
                    sstDeduct={sstDeductFor('b')}
                    onSstDeductChange={(patch) => setCardSstDeduct('b', patch)}
                    om={sysFor('b', optB).om}
                    onOmChange={(v) => setCardOm('b', v)}
                    omPkg={cscOmPkg.b}
                    onOmPkgChange={(p) => setCardOmPkg('b', p)}
                    pricing={cardPricing('b', optB)}
                    addonRows={addonsForKey('b')}
                    catalog={addons}
                    onAddAddon={(id) => addAddonRow('b', id)}
                    onUpdateAddon={(rowId, patch) => updateAddonRow('b', rowId, patch)}
                    onRemoveAddon={(rowId) => removeAddonRow('b', rowId)}
                    mdWarn={null}
                />
                </Box>
            </Box>{/* /RIGHT column */}
            </Box>{/* /left-right grid */}

            {/* Option chooser removed (user 2026-06-29) — only Option B; selectedOpt is 'b'. */}

            {/* Load-profile chart (HTML-simulator style). Uses the REAL uploaded TNB
                interval data when present (lpAnalysis); otherwise a SYNTHETIC profile
                built from the bill/sizing — same chart, just estimated data. (user 2026-07-24) */}
            {(() => {
                if (lpAnalysis) {
                    return (
                        <CscLoadProfileChart
                            analysis={lpAnalysis}
                            title={customerName ? `${customerName} — Load Profile` : 'Load Profile Analysis'}
                            subtitle={`${lpAnalysis.stats?.nDays || 0} days · ${Math.round(lpAnalysis.stats?.kwp || 0)} kWp system · 3.4 PSH · uploaded interval data`}
                            peakFrac={isTou ? optBDisplay?.selfPeakFrac : null}
                        />
                    );
                }
                if (!optA || !(parseFloat(bill) > 0)) return null;
                const _synthKwp = Number(sysFor('b', optB).kwp) || optA.kwp;
                const _synthKwh = calcRate > 0 ? (parseFloat(calcBill) || 0) / calcRate : 0;
                const synth = buildSyntheticAnalysis({
                    kwp: _synthKwp, monthlyKwh: _synthKwh, operatingDays,
                    opStart, opEnd, offHourBaselinePct: _offHour, mode: _csMode,
                    batteryKwh: parseFloat(battKwhFor('b')) || 0,
                });
                if (!synth) return null;
                return (
                    <CscLoadProfileChart
                        analysis={synth}
                        title={customerName ? `${customerName} — Load Profile (estimated)` : 'Load Profile (estimated)'}
                        subtitle={`${Math.round(_synthKwp)} kWp · ${operatingDays}-day week · ${_csMode === 'selco' ? 'SELCo' : 'ATAP'} · estimated from bill (upload a TNB interval Excel for actuals)`}
                        peakFrac={isTou ? optBDisplay?.selfPeakFrac : null}
                    />
                );
            })()}

                    {/* SIX REAL BILLS. Optional - leave blank and nothing changes. Filled in,
                        it costs each month separately and shows how far the single-bill
                        figure above is from what these months actually support. The saving
                        is capped by consumption, so a light month cannot deliver the
                        percentage a heavy one does. (user 2026-10-23) */}
                    <Box sx={{ mt: 2, p: 1.5, border: `1px solid ${COLORS.border}`, borderRadius: '10px', background: '#F8FAFC' }}>
                        <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: COLORS.text, fontFamily: PP }}>
                            Saving month by month <span style={{ fontWeight: 400, color: COLORS.sub }}>· from the bills above</span>
                        </Typography>
                        <Typography sx={{ fontSize: '0.66rem', color: COLORS.sub, fontFamily: PP, mb: 1 }}>
                            Set Monthly Bill to <strong>custom</strong> and fill the months you have.
                            Fewer than 12 is read as ONE CYCLE that repeats — 3 months becomes 4 a year.
                        </Typography>
                        {(() => {
                            // Silence is the worst answer here: somebody types six bills,
                            // sees nothing, and concludes the feature is broken when the
                            // real reason is that no system has been chosen yet.
                            // Only count them in custom mode, or single mode advertises
                            // leftover months it is not using.
                            const filled = billMode === 'custom'
                                ? monthBills.filter(b => parseFloat(String(b).replace(/,/g, '')) > 0).length
                                : 0;
                            if (filled === 0) return null;
                            if (!billsAnalysis) return (
                                <Typography sx={{ fontSize: '0.68rem', color: '#B45309', fontFamily: PP, mt: 1 }}>
                                    Choose a system above and these {filled} bill{filled === 1 ? '' : 's'} will be costed.
                                </Typography>
                            );
                            if (billsAnalysis.count < 2) return (
                                <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, mt: 1 }}>
                                    Add at least one more month — a single bill is what this is checking against.
                                </Typography>
                            );
                            return null;
                        })()}
                        {billsAnalysis && billsAnalysis.count >= 2 && (
                            <Box sx={{ mt: 1.5 }}>
                                {/* BILL AND SAVING IN ONE BAR. The saving alone answered "how
                                    much", never "out of what" - and out of what is the question a
                                    customer actually asks. Each bar is the month's BILL; the
                                    filled part is what the system removes, the pale part is what
                                    they still pay. The shape of the year is then readable at a
                                    glance: tall bars mostly filled are good months, tall bars
                                    mostly pale are months the system cannot keep up with.
                                    (user 2026-10-23: "i need the chart include aso saving")

                                    Twelve bars always. Fewer months entered is a cycle that
                                    repeats, and the repeats are dashed so MEASURED and PROJECTED
                                    never look alike. */}
                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.4, height: 130, px: 0.5 }}>
                                    {Array.from({ length: 12 }, (_, i) => ({
                                        ...billsAnalysis.months[i % billsAnalysis.count],
                                        predicted: i >= billsAnalysis.count,
                                    })).map((m, i) => {
                                        const topBill = Math.max(...billsAnalysis.months.map(x => x.bill)) || 1;
                                        const barH = Math.max(3, (m.bill / topBill) * 96);
                                        const savedH = Math.max(0, Math.min(barH, barH * (m.saving / (m.bill || 1))));
                                        return (
                                            <Box key={i} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3, minWidth: 0 }}
                                                title={`${monthLabel(i)}${m.predicted ? ' (projected)' : ''} \u00b7 bill RM ${Math.round(m.bill).toLocaleString()} \u00b7 saves RM ${Math.round(m.saving).toLocaleString()} (${m.pct.toFixed(1)}%)`}>
                                                <Typography sx={{ fontSize: '0.54rem', color: COLORS.sub, fontFamily: PP, whiteSpace: 'nowrap' }}>
                                                    {m.pct.toFixed(0)}%
                                                </Typography>
                                                {/* The whole bar is the bill; the filled foot is the saving. */}
                                                <Box sx={{
                                                    width: '100%', height: barH, position: 'relative',
                                                    borderRadius: '4px 4px 0 0',
                                                    background: '#E2E8F0',
                                                    border: m.predicted ? '1px dashed #94A3B8' : 'none',
                                                }}>
                                                    <Box sx={{
                                                        position: 'absolute', left: 0, right: 0, bottom: 0, height: savedH,
                                                        borderRadius: savedH >= barH - 1 ? '4px 4px 0 0' : 0,
                                                        background: m.predicted
                                                            ? 'transparent'
                                                            : 'linear-gradient(180deg,#1A8CFF,#0F62CC)',
                                                        border: m.predicted ? '1px dashed #1A8CFF' : 'none',
                                                    }} />
                                                </Box>
                                                <Typography sx={{ fontSize: '0.54rem', color: COLORS.sub, fontFamily: PP }}>
                                                    {monthLabel(i)}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                                <Typography sx={{ fontSize: '0.62rem', color: COLORS.sub, fontFamily: PP, mt: 0.5, textAlign: 'center' }}>
                                    Full bar = the month’s bill · filled = what solar removes · dashed = the cycle repeating.
                                    The % above each bar is how much of that bill is covered.
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.25 }}>
                                    {[
                                        ['Monthly average', billsAnalysis.avgSaving, '#F8FAFC', COLORS.border, COLORS.sub, COLORS.text, 'across the 12 months'],
                                        ['Highest month', billsAnalysis.maxSaving, '#ECFDF5', '#A7F3D0', '#047857', '#065F46', 'best the system does'],
                                        ['Lowest month', billsAnalysis.minSaving, '#FFFBEB', '#FDE68A', '#B45309', '#92400E', 'the one they will ring about'],
                                        ['Yearly saving', billsAnalysis.annualSaving, '#EFF6FF', '#BFDBFE', '#1D4ED8', '#1E3A8A',
                                            billsAnalysis.annualised
                                                ? (12 % billsAnalysis.count === 0
                                                    ? `${billsAnalysis.count}-month cycle \u00d7 ${12 / billsAnalysis.count}`
                                                    : `${billsAnalysis.count} months \u2192 12`)
                                                : 'a real 12 months'],
                                    ].map(([label, value, bg, bd, labelCol, valCol, note]) => (
                                        <Box key={label} sx={{ flex: '1 1 150px', minWidth: 140, p: 1, borderRadius: '8px', background: bg, border: `1px solid ${bd}` }}>
                                            <Typography sx={{ fontSize: '0.58rem', color: labelCol, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: PP }}>
                                                {label}
                                            </Typography>
                                            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: valCol, fontFamily: PP }}>
                                                RM {Math.round(value).toLocaleString()}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.58rem', color: labelCol, fontFamily: PP }}>
                                                {note}
                                            </Typography>
                                        </Box>
                                    ))}
                                    {billsAnalysis.paybackYears && (
                                        <Box sx={{ flex: '1 1 150px', minWidth: 140, p: 1, borderRadius: '8px', background: '#F5F3FF', border: '1px solid #DDD6FE' }}>
                                            <Typography sx={{ fontSize: '0.58rem', color: '#6D28D9', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: PP }}>
                                                ROI · payback
                                            </Typography>
                                            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#4C1D95', fontFamily: PP }}>
                                                {billsAnalysis.paybackYears.toFixed(1)} yrs
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.58rem', color: '#6D28D9', fontFamily: PP }}>
                                                system cost ÷ the YEAR
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                                <Typography sx={{ fontSize: '0.62rem', color: COLORS.sub, fontFamily: PP, mt: 0.75 }}>
                                    Payback is worked out on the YEARLY saving, never on a good month — a
                                    multi-month bill set exists precisely so the quiet months are counted too.
                                    The proposal now uses this yearly figure too, so the deck and this panel
                                    agree.
                                </Typography>
                            </Box>
                        )}
                    </Box>

            {/* ── BESS peak shaving results (user 2026-09-25) ────────────────────────────────
                Sits between the load-profile chart and the WhatsApp message. Solar's saving is
                the ENERGY charge; this is the DEMAND charge, which solar cannot touch. Labelled
                Part A when it stands alone, Part B when a solar system is proposed with it. */}
            {bessPick && (() => {
                const solarKwp = Number(sel?.kwp) || 0;
                const partLabel = solarKwp > 0 ? 'Part B' : 'Part A';
                const md0 = bessPick.monthly.length ? Math.max(...bessPick.monthly.map(m => m.baseline)) : 0;
                const md1 = bessPick.monthly.length ? Math.max(...bessPick.monthly.map(m => m.after)) : 0;
                const Stat = ({ label, value, sub, color = '#0F172A' }) => (
                    <Box sx={{ bgcolor: '#F8FAFC', borderRadius: '10px', p: 1.25, flex: 1, minWidth: 130 }}>
                        <Typography sx={{ fontSize: '0.62rem', color: COLORS.sub, fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>{label}</Typography>
                        <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color, fontFamily: "'JetBrains Mono',monospace", mt: 0.3 }}>{value}</Typography>
                        {sub && <Typography sx={{ fontSize: '0.62rem', color: COLORS.sub, fontFamily: PP }}>{sub}</Typography>}
                    </Box>
                );
                return (
                    <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 16, marginBottom: 16 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>
                                {partLabel} · BESS — Peak Shaving
                            </Typography>
                            {/* The pack is spelled out in the Battery size card below; repeating
                                it here was the same line twice. (user 2026-11-02) */}
                        </Box>
                        <Typography sx={{ fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP, mb: 1.5 }}>
                            From {bess.months} month{bess.months === 1 ? '' : 's'} of interval data · maximum demand billed at RM {MV_DEMAND_RATE_PER_KW.toFixed(2)}/kW · {bessCredit}% credited
                        </Typography>
                        {/* ONE CARD PER FACT, in the order the question is asked: what the
                            demand was and becomes, how much came off, what hardware that
                            takes, what it saves, what it costs, when it is back.
                            Before/after share a card because they are one number changing,
                            and the three paybacks share a card because they are one number
                            under three incentive assumptions — the old layout printed the
                            with-CA payback twice. (user 2026-11-02) */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                            <Box sx={{ bgcolor: '#F8FAFC', borderRadius: '10px', p: 1.25, flex: 1, minWidth: 170 }}>
                                <Typography sx={{ fontSize: '0.62rem', color: COLORS.sub, fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>Maximum demand</Typography>
                                <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", mt: 0.3, color: '#0F766E' }}>
                                    <Box component="span" sx={{ color: COLORS.sub, textDecoration: 'line-through', fontWeight: 600, mr: 0.75 }}>
                                        {Math.round(md0).toLocaleString()}
                                    </Box>
                                    {Math.round(md1).toLocaleString()} kW
                                </Typography>
                                <Typography sx={{ fontSize: '0.62rem', color: COLORS.sub, fontFamily: PP }}>before → after</Typography>
                            </Box>
                            {/* kW stays at 100% on BOTH sides of the card and in the analyzer:
                                the credit factor is a commercial haircut on the money, not a
                                claim that the battery shaves less. Only the saving and the
                                payback carry the two bases. */}
                            <Stat label="MD shaved" value={`${Math.round(bessPick.avgShavedKw).toLocaleString()} kW`} sub="monthly average · simulated" color="#0F766E" />
                            <Stat
                                label="Battery size"
                                value={`${bessPick.units} × ${bessUnitKwh} = ${bessPick.energyKwh.toLocaleString()} kWh`}
                                sub={`${bessPick.powerKw.toLocaleString()} kW · ${bessBrand === BATT_BRAND_HUAWEI ? 'Huawei' : 'GoodWe'}`}
                            />
                            <Stat
                                label="Saving"
                                value={`RM ${Math.round((bessPick.annualYear1FullCredit ?? bessPick.annualYear1) / 12).toLocaleString()}`}
                                sub={`RM ${Math.round(bessPick.monthlyAvgSaving).toLocaleString()} at ${bessCredit}% · per month`}
                                color="#15803D"
                            />
                            <Stat label="Battery cost" value={`RM ${Math.round(bessPick.capex).toLocaleString()}`} />
                            {/* TWO PAYBACK CARDS, one per basis, each a complete set of three.
                                A single card mixing them made the reader work out which line
                                belonged to which assumption; side by side, each card answers
                                "if the battery shaves what the simulation says / what we credit,
                                when is it back". The 4-year test still judges the credited card.
                                (user 2026-11-03: "have two payback card, one is 100% and another
                                one is 90% one") */}
                            {[
                                {
                                    title: 'Payback at 100% shaved',
                                    /* The credit factor is not a second simulation — it is the
                                       same saving less a buffer. Saying so is the difference
                                       between a reader trusting the pair and wondering which
                                       model produced which number.
                                       (user 2026-11-03: "90% is just -10% of expected saving") */
                                    note: 'expected saving',
                                    rows: [
                                        { k: 'No incentive', v: bessPick.paybackYearsFullCredit, c: '#0F172A' },
                                        { k: 'With CA', v: bessPick.paybackWithCAFull, c: '#0F766E' },
                                        { k: 'With CA + GITA', v: bessPick.paybackWithCAGitaFull, c: '#15803D' },
                                    ],
                                },
                                {
                                    title: `Payback at ${bessCredit}% credited`,
                                    note: `expected saving less ${100 - (parseFloat(bessCredit) || 90)}% buffer`,
                                    rows: [
                                        { k: 'No incentive', v: bessPick.paybackYears, c: '#0F172A' },
                                        { k: 'With CA', v: bessPick.paybackWithCA, c: '#0F766E' },
                                        { k: 'With CA + GITA', v: bessPick.paybackWithCAGita, c: '#15803D' },
                                    ],
                                },
                            ].map(card => (
                                <Box key={card.title} sx={{ bgcolor: '#F8FAFC', borderRadius: '10px', p: 1.25, flex: '1.2 1 215px', minWidth: 200 }}>
                                    <Typography sx={{ fontSize: '0.62rem', color: COLORS.sub, fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>{card.title}</Typography>
                                    {card.rows.map(p => (
                                        <Box key={p.k} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 0.3 }}>
                                            <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP }}>{p.k}</Typography>
                                            <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: p.c, fontFamily: "'JetBrains Mono',monospace" }}>
                                                {Number.isFinite(p.v) ? `${p.v.toFixed(2)} yr` : '—'}
                                            </Typography>
                                        </Box>
                                    ))}
                                    <Typography sx={{ fontSize: '0.6rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>{card.note}</Typography>
                                </Box>
                            ))}
                        </Box>
                        {/* The sizing working behind the recommendation. (user 2026-09-25) */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                            {bessPick.worstDay && (
                                <Box sx={{ bgcolor: '#F8FAFC', borderRadius: '10px', p: 1.25, flex: '2 1 280px' }}>
                                    <Typography sx={{ fontSize: '0.62rem', color: COLORS.sub, fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>
                                        {/* The SIZING day, which is the heaviest-discharge day and
                                            usually NOT the tallest one the chart draws. Named apart
                                            so the two cannot be read as the same day. */}
                                        Sizing — heaviest discharge day ({bessPick.worstDay.dayKey})
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.72rem', color: '#0F172A', fontFamily: PP, mt: 0.3, lineHeight: 1.5 }}>
                                        Peak {Math.round(bessPick.worstDay.peakKw).toLocaleString()} kW → hold {Math.round(bessPick.worstDay.capKw).toLocaleString()} kW ·
                                        discharge {Math.round(bessPick.worstDay.dischargeKwh).toLocaleString()} kWh at {Math.round(bessPick.worstDay.dischargePowerKw).toLocaleString()} kW
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.66rem', color: COLORS.sub, fontFamily: PP, mt: 0.3 }}>
                                        ÷ (DoD 90% × efficiency 95% × 10-yr 78.51%) = {Math.round(bessPick.requiredInstalledKwh).toLocaleString()} kWh installed
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        {/* Month by month — what the bill's MD line actually becomes. */}
                        <Box sx={{ overflowX: 'auto', mb: 1.5 }}>
                            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: PP, fontSize: '0.75rem' }}>
                                <Box component="thead">
                                    <Box component="tr" sx={{ '& th': { textAlign: 'right', py: 0.5, px: 1, color: COLORS.sub, fontWeight: 700, borderBottom: '1px solid #E2E8F0', whiteSpace: 'nowrap' }, '& th:first-of-type': { textAlign: 'left' } }}>
                                        <th>Month</th><th>MD before</th><th>MD after</th><th>Shaved</th><th>Saving</th>
                                    </Box>
                                </Box>
                                <Box component="tbody">
                                    {bessPick.monthly.map(m => (
                                        <Box component="tr" key={m.month} sx={{ '& td': { textAlign: 'right', py: 0.45, px: 1, borderBottom: '1px solid #F1F5F9', fontFamily: "'JetBrains Mono',monospace" }, '& td:first-of-type': { textAlign: 'left', fontFamily: PP } }}>
                                            <td>{m.month}</td>
                                            <td>{Math.round(m.baseline).toLocaleString()}</td>
                                            <td>{Math.round(m.after).toLocaleString()}</td>
                                            <td style={{ color: '#0F766E' }}>{Math.round(m.shaved).toLocaleString()}</td>
                                            <td style={{ color: '#15803D' }}>RM {Math.round(m.saving).toLocaleString()}</td>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                        {/* Unit ladder — where adding another block stops paying for itself. */}
                        <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, mb: 0.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {/* The tick is judged on GROSS payback, not after CA — the label said
                                "after CA" long after the rule changed on 2026-10-26. */}
                            How many units · ✓ = the whole system pays back inside {bess.qualifyYears} years
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                            {bess.results.map(r => {
                                const chosen = r.units === bessPick.units;
                                return (
                                    <Box key={r.units} onClick={() => setBessUnits(String(r.units))}
                                        sx={{ cursor: 'pointer', borderRadius: '8px', px: 1.25, py: 0.75, minWidth: 150,
                                            border: `1px solid ${chosen ? COLORS.primary : '#E2E8F0'}`, bgcolor: chosen ? 'rgba(26,140,255,0.06)' : '#fff' }}>
                                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>
                                            {r.units} unit{r.units === 1 ? '' : 's'}
                                            {r.units === bess.best.units ? ' · recommended' : ''}
                                            <span style={{ color: r.qualifies ? '#15803D' : '#B91C1C', marginLeft: 4 }}>{r.qualifies ? '✓' : '✗'}</span>
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: "'JetBrains Mono',monospace" }}>
                                            {Math.round(r.avgShavedKw)} kW · RM {Math.round(r.monthlyAvgSaving).toLocaleString()}/mo
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.68rem', color: '#B45309', fontFamily: "'JetBrains Mono',monospace" }}>
                                            {r.paybackWithCA.toFixed(1)} yr with CA
                                        </Typography>
                                        {/* What THIS extra block costs against what it adds. Read on its
                                            own it looks like a second payback for the system and gets
                                            asked about every time, so it says whose figure it is: the
                                            LAST block only. A block can pay back in 5.1 years inside a
                                            system that pays back in 2.5. (user 2026-11-02: "5.1yr +52kw is?") */}
                                        {r.units > 1 && (
                                            <Typography sx={{ fontSize: '0.62rem', color: COLORS.sub, fontFamily: "'JetBrains Mono',monospace" }}>
                                                {/* What the LAST block cost and what it bought. Naming
                                                    the ringgit on both sides is the only version of
                                                    this line that has not had to be explained out loud.
                                                    (user 2026-11-03: "5.2 year is what?") */}
                                                +1 block RM {Math.round(r.capex / r.units).toLocaleString()} → +RM {Math.round(r.addedMonthlySaving).toLocaleString()}/mo{Number.isFinite(r.marginalPaybackYears) ? ` · ${r.marginalPaybackYears.toFixed(1)} yr` : ' · no gain'}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </div>
                );
            })()}

            {/* WhatsApp Preview */}
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <Box sx={{ bgcolor: '#075e54', p: '10px 16px', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ width: 36, height: 36, bgcolor: '#128c7e', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>💬</Box>
                    <div>
                        <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.85rem', fontFamily: PP }}>{tr(lang, 'Commercial Solar Proposal', '商业太阳能方案')}</Typography>
                        <Typography sx={{ color: '#80cbc4', fontSize: '0.72rem', fontFamily: PP }}>Consolidated Proposal</Typography>
                    </div>
                    {/* Option A/B toggle removed (user 2026-06-29) — Option A retired, only
                        the single Recommended Package (Option B) remains. */}
                    {/* Part chooser — only once a BESS is actually proposed. A (solar) /
                        B (peak shaving) / A+B (both, with a combined total). (user 2026-09-26) */}
                    {bessPick && (
                        <ToggleButtonGroup exclusive size="small" value={waPartEff} sx={{ ml: 'auto' }}
                            onChange={(_, v) => { if (v) setWaPart(v); }}>
                            {[['a', tr(lang, 'A · Solar', 'A · 太阳能')], ['b', 'B · BESS'], ['ab', 'A + B']].map(([k, lbl]) => (
                                <ToggleButton key={k} value={k}
                                    sx={{ fontFamily: PP, fontSize: '0.66rem', textTransform: 'none', py: 0.25, px: 1.1, color: 'rgba(255,255,255,0.75)', borderColor: 'rgba(255,255,255,0.3)', borderRadius: '6px !important', '&.Mui-selected': { background: 'rgba(255,255,255,0.22) !important', color: '#fff !important' } }}>
                                    {lbl}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    )}
                </Box>
                <Box sx={{ bgcolor: '#e5ddd5', minHeight: 120, p: 1.5 }}>
                    <Box sx={{ bgcolor: '#fff', borderRadius: '0 8px 8px 8px', p: 1.25, maxWidth: '90%', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                        <Typography component="pre" sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.72rem', lineHeight: 1.45, color: generatedMsg ? '#374151' : '#9CA3AF', fontStyle: generatedMsg ? 'normal' : 'italic', whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
                            {generatedMsg || tr(lang, 'Enter a bill to generate the WhatsApp message', '输入账单后自动生成 WhatsApp 信息')}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ p: '10px 16px', borderTop: '1px solid #E2E8F0', bgcolor: '#F8FAFC', display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Button onClick={onCopyMsg} disabled={!generatedMsg} variant="outlined" size="small"
                        startIcon={<ContentCopyIcon sx={{ fontSize: '0.85rem' }} />}
                        sx={{ fontFamily: PP, fontSize: '0.78rem', fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}>
                        {copied ? tr(lang, 'Copied', '已复制') : tr(lang, 'Copy Message', '复制信息')}
                    </Button>
                    <Button onClick={onOpenWA} disabled={!generatedMsg} variant="contained" size="small"
                        startIcon={<WhatsAppIcon sx={{ fontSize: '0.85rem' }} />}
                        sx={{ fontFamily: PP, fontSize: '0.78rem', fontWeight: 600, textTransform: 'none', borderRadius: '8px', background: 'linear-gradient(135deg,#25D366,#128C7E)', boxShadow: 'none', '&:hover': { background: 'linear-gradient(135deg,#20b558,#0f7360)', boxShadow: 'none' } }}>
                        {tr(lang, 'Open WhatsApp', '开启 WhatsApp')}
                    </Button>
                </Box>
            </div>

            {/* ───────── Section 2 — Proposal ───────── */}
            <SectionHeader title="Section 2 — Proposal" />

            {/* Shared Project Info form */}
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 20 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 2 }}>
                    Project Info
                </Typography>

                {/* Customer */}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Customer
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Field label="Company Name" value={customerName} onChange={setCustomerName} required />
                    {/* Director / contact person — drives the quotation "Attn" + signature
                        person line (separate from the company). Same field as PIC > Name
                        below; fill either. (user 2026-06-23) */}
                    <Field label="Director / Contact Name" value={picName} onChange={setPicName} />
                    <Field label="Phone" value={customerPhone} onChange={setCustomerPhone} />
                    <Field label="Email" value={customerEmail} onChange={setCustomerEmail} />
                    <Field label="Address" value={customerAddress} onChange={setCustomerAddress} />
                    {/* MV: the bill is calculated from peak / off-peak kWh + MD, so it isn't typed here. */}
                    <Field label={isTou ? 'Monthly Bill (RM) · calculated' : 'Monthly Bill (RM)'} value={bill} onChange={(v) => { if (isTou) return; setBill(v); setBillKwh(_kwhFromBill(v, tariffRate)); }} type="number" />
                </Box>

                {/* Site */}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Site
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                    <Box>
                        <Typography sx={{ fontSize: '0.74rem', color: '#374151', fontFamily: PP, mb: 0.4 }}>Site State (drives irradiance page)</Typography>
                        <FormControl fullWidth size="small">
                            <Select value={siteState} onChange={(e) => setSiteState(e.target.value)} displayEmpty
                                sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                <MenuItem value="" sx={{ fontFamily: PP, fontSize: '0.85rem', color: COLORS.sub }}>— Not specified —</MenuItem>
                                {Object.entries(MY_STATE_GHI).map(([k, v]) => (
                                    <MenuItem key={k} value={k} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                                        {v.name} <span style={{ color: COLORS.sub, marginLeft: 6 }}>· {v.ghi.toFixed(1)} kWh/m²/day</span>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Site Photos uploader (up to 10) */}
                <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: '0.74rem', color: '#374151', fontFamily: PP, mb: 0.4 }}>Site Photos (optional, up to 10)</Typography>
                    <Button variant="outlined" component="label" size="small"
                        sx={{ fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', borderRadius: '8px' }}>
                        Add photo(s)
                        <input type="file" hidden accept="image/*" multiple
                            onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                if (!files.length) return;
                                const slotsLeft = Math.max(0, 10 - sitePhotos.length);
                                const toRead = files.slice(0, slotsLeft);
                                Promise.all(toRead.map((f) => new Promise((resolve) => {
                                    const fr = new FileReader();
                                    fr.onload = () => resolve(fr.result);
                                    fr.onerror = () => resolve(null);
                                    fr.readAsDataURL(f);
                                }))).then((urls) => {
                                    setSitePhotos((prev) => [...prev, ...urls.filter(Boolean)].slice(0, 10));
                                });
                                e.target.value = '';
                            }} />
                    </Button>
                    <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, mt: 0.4, display: 'inline-block', ml: 1 }}>
                        {sitePhotos.length === 0 ? 'No photos — the Site Survey page will be omitted.' : `${sitePhotos.length} / 10 selected`}
                    </Typography>
                    {sitePhotos.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.8, mt: 1, flexWrap: 'wrap' }}>
                            {sitePhotos.map((src, i) => (
                                <Box key={i} sx={{ position: 'relative', width: 64, height: 64, borderRadius: '6px', overflow: 'hidden', border: `1px solid ${COLORS.border}` }}>
                                    <img src={src} alt={`Site ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    <Button size="small" onClick={() => setSitePhotos((prev) => prev.filter((_, j) => j !== i))}
                                        sx={{ position: 'absolute', top: -4, right: -4, minWidth: 0, width: 20, height: 20, p: 0, fontSize: '0.7rem', backgroundColor: 'rgba(0,0,0,0.65)', color: '#fff', borderRadius: '50%', '&:hover': { backgroundColor: 'rgba(0,0,0,0.85)' } }}>
                                        ×
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Financing */}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Financing
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                    {/* ONE RATE IS TYPED, THREE ARE CHARGED. The box is the 20-year base;
                        a shorter lease recovers the same system over fewer years, so 15yr
                        rides +0.04 and 10yr +0.08 on whatever is in it. Those two were
                        derived and never shown, so the proposal quoted rates the person
                        preparing it could not see anywhere on screen.
                        (user 2026-10-23: "sare rate for 10years, 15years, 20years") */}
                    <Box>
                        <Field label="SARE Tariff (RM/kWh) — 20-year base, auto from kWp tier + add-ons"
                            value={sareTariffOverride}
                            onChange={(v) => { setSareTariffOverride(v); setSareTouched(true); }}
                            type="number" />
                        {(math?.sareTariff > 0) && (
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                                {[
                                    ['20 yr', sareTariffOverride, setSareTariffOverride, math.sareTariff],
                                    ['15 yr', sareTariffOverride15, setSareTariffOverride15, math.sareTariff15],
                                    ['10 yr', sareTariffOverride10, setSareTariffOverride10, math.sareTariff10],
                                ].map(([term, val, setter, effective]) => (
                                    <Box key={term} sx={{ flex: '1 1 92px', minWidth: 92 }}>
                                        <Typography sx={{ fontSize: '0.58rem', color: COLORS.sub, fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            {term}
                                        </Typography>
                                        <TextField fullWidth size="small" type="number" value={val}
                                            onChange={(e) => setter(e.target.value)}
                                            placeholder={Number(effective || 0).toFixed(3)}
                                            inputProps={{ step: 0.005, min: 0, style: { padding: '6px 8px', fontSize: '0.82rem', fontWeight: 700 } }} />
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {(math?.sareTariff > 0) && (
                            <Typography sx={{ fontSize: '0.62rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>
                                Blank follows the rate card for this system size. Add-ons lift the 20-year
                                rate only — the 10 and 15 year uplift has not been worked out yet.
                            </Typography>
                        )}
                    </Box>
                    {/* LOAN PRINCIPAL IS NOT SHOWN. It finances the grand total incl. SST,
                        which is what every deal actually uses - "by right just normally use
                        system size" (user 2026-10-24). An editable box for a figure nobody
                        edits is an invitation to type a wrong one, and a wrong principal
                        changes the instalment on a customer-facing page.

                        The state and the override still exist: `loanPrincipal` is read by
                        the math hook exactly as before and is still carried in the draft and
                        Saved Sets, so an older quote that HAS an override keeps using it.
                        Restoring the input is this comment plus one Field. */}
                    <Field label="Loan Rate (% p.a.)" value={loanRate} onChange={setLoanRate} type="number" />
                    <Field label="Loan Term (months)" value={loanTermMonths} onChange={setLoanTermMonths} type="number" />
                    {/* With terms ticked below, this pair reads like a fourth loan. It is not:
                        it drives the single loan block and fills in for any term box cleared
                        to empty. One line beats guessing. (user 2026-10-17) */}
                    <Typography sx={{ gridColumn: { xs: 'auto', md: '1 / -1' }, fontSize: '0.7rem', color: COLORS.sub, fontFamily: PP, mt: -1 }}>
                        Used for the single loan block, and for any term box below left blank.
                    </Typography>
                </Box>

                {/* Loan terms to COMPARE in the proposal. Tick one, two or all three and the
                    deck carries a loan block per term; tick none and it behaves exactly as
                    before, on the single Loan Term above.
                    Each term has its own rate because a longer tenure usually prices higher
                    (the Astern HP sheet runs 3.6 / 3.6 / 4.0%); blank follows Loan Rate.
                    (user 2026-10-15) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Compare loan terms in the proposal
                    </Typography>
                    {/* Which KIND of rate the boxes below carry. Flat is converted to its
                        effective twin before anything is calculated, so the proposal's table
                        always runs on one kind. (user 2026-10-16) */}
                    <ToggleButtonGroup size="small" exclusive value={loanRateMode}
                        onChange={(_, v) => { if (v) switchLoanRateMode(v); }}
                        sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: { xs: '0.8rem', sm: '0.68rem' }, textTransform: 'none', px: { xs: 1.8, sm: 1.1 }, py: { xs: 0.9, sm: 0.2 }, minHeight: { xs: 40, sm: 'auto' }, borderRadius: '7px' }, '& .Mui-selected': { bgcolor: `${COLORS.primary} !important`, color: '#fff !important' } }}>
                        <ToggleButton value="flat">Flat (HP)</ToggleButton>
                        <ToggleButton value="effective">Effective</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1, mb: 1.5 }}>
                    {[[36, '3 Years'], [60, '5 Years'], [84, '7 Years']].map(([m, label]) => (
                        <Box key={m} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <FormControlLabel
                                sx={{ mr: 0 }}
                                control={<Checkbox size="small" checked={!!loanTermPick[m]}
                                    onChange={(e) => setLoanTermPick(p => ({ ...p, [m]: e.target.checked }))} />}
                                label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: COLORS.text, whiteSpace: 'nowrap' }}>{label}</Typography>}
                            />
                            {/* The % sits INSIDE the box: at this width a label above it is
                                wider than the input. Cleared back to empty = fall back to
                                the single Loan Rate above, so the field can be emptied
                                rather than being a number you are stuck with. */}
                            <Field value={loanTermRate[m]} type="number" suffix="%"
                                onChange={(v) => setLoanTermRate(r => ({ ...r, [m]: v }))} />
                        </Box>
                    ))}
                </Box>
                {loanTermsSelected.length > 0 && (
                    <Typography sx={{ fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP, mb: 1.5, fontStyle: 'italic' }}>
                        {/* Both rates are shown when one was converted, so the figure the
                            customer was quoted and the figure the table ran on are visible
                            side by side rather than one silently replacing the other. */}
                        {(math?.loanPlans || []).map(p => p.isFlat
                            ? `${p.years}yr @ ${(p.quotedRate * 100).toFixed(2)}% flat (= ${(p.rate * 100).toFixed(2)}% eff) = RM ${Math.round(p.monthlyPmt).toLocaleString()}/mo`
                            : `${p.years}yr @ ${(p.rate * 100).toFixed(2)}% = RM ${Math.round(p.monthlyPmt).toLocaleString()}/mo`
                        ).join('  ·  ')}
                    </Typography>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    <FormControlLabel
                        control={<Checkbox size="small" checked={includeOutright} onChange={(e) => setIncludeOutright(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: COLORS.text }}>Include Outright</Typography>}
                    />
                    {/* SARE only for ≥ 42 kWp — grey out + disable ONLY once a real system under
                        42 kWp is computed. Gate is on kWp (NOT inverter kW: a >30 kW inverter
                        could be paired with only ~20 kWp of panels, which must not qualify).
                        Before a system exists (kwp=0) it stays ON by default. (user 2026-08-01) */}
                    {/* Two independent terms (user 2026-08-23). The 10-year lease charges
                        +8 sen/kWh for half the commitment; both share the same ≥42 kWp gate
                        and the same Option-A banner + "how SARE works" pages. */}
                    {(() => { const _k = parseFloat(sel?.kwp) || 0; const _below = _k > 0 && _k < 42; return (<>
                    <FormControlLabel
                        disabled={_below}
                        control={<Checkbox size="small" checked={includeSARE && !_below} onChange={(e) => { setIncludeSARE(e.target.checked); setSareIncludeTouched(true); }} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: _below ? COLORS.sub : COLORS.text }}>Include SARE 20yr{_below ? ' (≥42 kWp)' : ''}</Typography>}
                    />
                    {/* Withheld, not mispriced: the add-on uplift is only worked out for the
                        20-year term, so with any add-on these two could only quote a rate
                        that recovers nothing for it. The reason is on the label, because a
                        greyed box with no explanation reads as a bug.
                        (user 2026-10-24: "15 years and 10years disable first if got add on") */}
                    <FormControlLabel
                        disabled={_below || _sareShortTermsBlocked}
                        control={<Checkbox size="small" checked={includeSARE10Eff && !_below} onChange={(e) => { setIncludeSARE10(e.target.checked); setSareIncludeTouched(true); }} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: (_below || _sareShortTermsBlocked) ? COLORS.sub : COLORS.text }}>Include SARE 10yr{_below ? ' (≥42 kWp)' : (_sareShortTermsBlocked ? (_sareAddOnCost > 0 ? ' (not with add-ons)' : ' (not with a mark-up)') : '')}</Typography>}
                    />
                    <FormControlLabel
                        disabled={_below || _sareShortTermsBlocked}
                        control={<Checkbox size="small" checked={includeSARE15Eff && !_below} onChange={(e) => { setIncludeSARE15(e.target.checked); setSareIncludeTouched(true); }} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: (_below || _sareShortTermsBlocked) ? COLORS.sub : COLORS.text }}>Include SARE 15yr{_below ? ' (≥42 kWp)' : (_sareShortTermsBlocked ? (_sareAddOnCost > 0 ? ' (not with add-ons)' : ' (not with a mark-up)') : '')}</Typography>}
                    />
                    {_sareShortTermsBlocked && !_below && (
                        <Typography sx={{ fontSize: '0.68rem', fontFamily: PP, color: COLORS.sub, fontStyle: 'italic', ml: 3.5, mt: -0.5 }}>
                            The uplift that recovers {_sareAddOnCost > 0 ? 'an add-on' : 'a mark-up'} is
                            calculated for the 20-year term only, so the 15- and 10-year leases are
                            left out while {_sareAddOnCost > 0 ? 'an add-on is priced' : 'a mark-up is applied'}.
                        </Typography>
                    )}
                    </>); })()}
                    <FormControlLabel
                        control={<Checkbox size="small" checked={includeLoan} onChange={(e) => setIncludeLoan(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: COLORS.text }}>Include Loan</Typography>}
                    />
                    <FormControlLabel
                        control={<Checkbox size="small" checked={includeCA} onChange={(e) => setIncludeCA(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: COLORS.text }}>Include CA</Typography>}
                    />
                    <FormControlLabel
                        disabled={(parseFloat(sel.kwp) || 0) < 40}
                        control={<Checkbox size="small" checked={includeITA && (parseFloat(sel.kwp) || 0) >= 40} onChange={(e) => { setItaTouched(true); setIncludeITA(e.target.checked); }} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: (parseFloat(sel.kwp) || 0) >= 40 ? COLORS.text : COLORS.sub }}>Include GITA{(parseFloat(sel.kwp) || 0) < 40 ? ' · ≥40 kWp only' : ''}</Typography>}
                    />
                </Box>

                {/* Prepared By */}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mt: 2.5, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Prepared By
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1 }}>
                    <Box>
                        <Typography sx={{ fontSize: '0.74rem', color: '#374151', fontFamily: PP, mb: 0.4 }}>Group</Typography>
                        <FormControl fullWidth size="small">
                            <Select value={selectedGroupId} onChange={(e) => onPickGroup(e.target.value)} displayEmpty
                                sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                <MenuItem value="" sx={{ fontFamily: PP, fontSize: '0.85rem', color: COLORS.sub }}>— Select group —</MenuItem>
                                {agentGroups.map(g => (
                                    <MenuItem key={g.groupId} value={g.groupId} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>{g.groupName}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '0.74rem', color: '#374151', fontFamily: PP, mb: 0.4 }}>Sales Agent Name</Typography>
                        <FormControl fullWidth size="small">
                            <Select value={selectedAgentId} onChange={(e) => onPickAgent(e.target.value)} displayEmpty
                                sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                <MenuItem value="" sx={{ fontFamily: PP, fontSize: '0.85rem', color: COLORS.sub }}>— Select agent —</MenuItem>
                                {groupAgents.map(a => (
                                    <MenuItem key={a.userId} value={a.userId} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                                        {handleAgentName(a.firstName, a.lastName, a.alias)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box>
                        <Field label="Agent Position" value={sales.title} disabled />
                        <Typography sx={{ fontSize: '0.64rem', color: COLORS.sub, fontFamily: PP, mt: 0.3 }}>Auto from selected agent</Typography>
                    </Box>
                    <Field label="Email" value={sales.email} onChange={(v) => setSales(s => ({ ...s, email: v }))} />
                    <Field label="WhatsApp" value={sales.whatsapp} onChange={(v) => setSales(s => ({ ...s, whatsapp: v }))} />
                </Box>

                {/* Output actions — CSC Proposal (matches the Proposal tab: a
                    language toggle + Print Preview (vector) + Download PDF
                    (rasterized), both fed by the SAME buildProposalMap()). The
                    WhatsApp export lives in Section 1; the commercial forms are
                    in the group below. */}
                <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 1 }}>
                        Generate Proposal
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        <Button variant="outlined" disabled={!customerName.trim() || !sel.systemCost} onClick={printPreview}
                            sx={{ fontFamily: PP, fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', borderColor: COLORS.primary, color: COLORS.primary, '&:hover': { borderColor: COLORS.dark, backgroundColor: 'rgba(26,140,255,0.08)' } }}>
                            Print Preview (Ctrl+P → Save as PDF)
                        </Button>
                        <Button variant="contained" disabled={busy || !customerName.trim() || !sel.systemCost} onClick={genProposal}
                            sx={{ fontFamily: PP, fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)', '&:hover': { background: 'linear-gradient(135deg,#0F62CC,#0A4FA8)', boxShadow: 'none' } }}>
                            {busy ? 'Generating…' : 'Download PDF'}
                        </Button>
                        <Button variant="outlined" disabled={profBusy} onClick={genCompanyProfile}
                            sx={{ fontFamily: PP, fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', borderColor: '#0F766E', color: '#0F766E', '&:hover': { borderColor: '#0B5A54', backgroundColor: 'rgba(15,118,110,0.08)' } }}>
                            {profBusy ? 'Generating…' : 'Company Profile (no proposal)'}
                        </Button>
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP, mt: 1 }}>
                        Print Preview = vector-quality (browser Save as PDF); Download PDF = rasterized (good for mobile). Company Profile = brand pages only, no customer/pricing.
                    </Typography>
                </Box>
                {err && <Typography sx={{ color: '#EF4444', fontSize: '0.74rem', fontFamily: PP, mt: 1 }}>{err}</Typography>}
            </div>

            {/* ───────── Section 3 — Quotation & Forms ───────── */}
            <SectionHeader title="Section 3 — Quotation & Forms" />

            {/* Company / Quotation Details — commercial form fields (feed ATAP Contract /
                Declaration / Insurance / Quotation). Moved here from the Proposal card. */}
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 20 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 2 }}>
                    Company / Quotation Details
                </Typography>

                {/* Company Details */}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Company Details
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Field label="Ref No" value={refNo} onChange={setRefNo} />
                    <Field label="Company TIN" value={companyTin} onChange={setCompanyTin} />
                    <Field label="Company Office (Tel)" value={companyOffice} onChange={setCompanyOffice} />
                    <Field label="Company Fax" value={companyFax} onChange={setCompanyFax} />
                    <Field label="Company Email" value={companyEmail} onChange={setCompanyEmail} />
                    <Field label="Company Website" value={companyWebsite} onChange={setCompanyWebsite} />
                </Box>

                {/* PIC (Person In Charge) */}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    PIC (Person In Charge)
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Field label="Salutation (Mr/Ms/Mdm/Dr)" value={picSalutation} onChange={setPicSalutation} />
                    <Field label="Name" value={picName} onChange={setPicName} />
                    <Field label="MyKad / IC" value={picMykad} onChange={setPicMykad} />
                    <Field label="Position" value={picPosition} onChange={setPicPosition} />
                    <Field label="Citizenship" value={picCitizenship} onChange={setPicCitizenship} />
                    <Field label="Mobile" value={picMobile} onChange={setPicMobile} />
                    <Field label="Email" value={picEmail} onChange={setPicEmail} />
                </Box>

                {/* Authorised / Alternate Person */}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Authorised / Alternate Person
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Field label="Salutation (Mr/Ms/Mdm/Dr)" value={altSalutation} onChange={setAltSalutation} />
                    <Field label="Name" value={altName} onChange={setAltName} />
                    <Field label="MyKad / IC" value={altMykad} onChange={setAltMykad} />
                    <Field label="Position" value={altPosition} onChange={setAltPosition} />
                    <Field label="Relationship" value={altRelationship} onChange={setAltRelationship} />
                    <Field label="Citizenship" value={altCitizenship} onChange={setAltCitizenship} />
                    <Field label="Mobile" value={altMobile} onChange={setAltMobile} />
                    <Field label="Email" value={altEmail} onChange={setAltEmail} />
                </Box>

                {/* Warranty & Terms */}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Warranty &amp; Terms
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    {/* TWO INPUTS, ONE STORED VALUE. The term and the cleaning are chosen
                        separately, then composed back into the single string the sale
                        record and the quotation already carry — so nothing downstream
                        migrates and a quote written last month still reads back.
                        Cleaning is only offered above 30 kWp; below that the list is just
                        "No Cleaning", so it cannot be promised by accident.
                        (user 2026-10-26: "just the input split") */}
                    <Field label="Operation Warranty (years)"
                        value={warrantyTermOnly(operationWarranty)}
                        onChange={(v) => setOperationWarranty(composeWarranty(v, warrantyCleaningTimes(operationWarranty)))}
                        options={WARRANTY_TERM_OPTIONS} />
                    <Field label="Cleaning"
                        value={cleaningOptionsFor(sel?.kwp)[warrantyCleaningTimes(operationWarranty)] || 'No Cleaning'}
                        onChange={(v) => { setCleaningTouched(true); setOperationWarranty(composeWarranty(warrantyTermOnly(operationWarranty), cleaningOptionsFor(sel?.kwp).indexOf(v))); }}
                        options={cleaningOptionsFor(sel?.kwp)} />
                    <Field label="Defect Warranty" value={defectWarranty} onChange={setDefectWarranty} />
                    <Field label="Insurance Amount (RM)" value={insuranceAmount} onChange={setInsuranceAmount} type="number" />
                </Box>
            </div>

            {/* Payment Method — drives the Commercial Quotation (CC converts; Loan marks up) */}
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 20 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 1 }}>
                    Payment Method
                </Typography>
                <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Type
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                    <ToggleButtonGroup exclusive size="small" value={payMethod} onChange={(_, v) => v && onPayMethod(v)}
                        sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', py: 0.5, px: 1.6, color: COLORS.sub, borderColor: COLORS.border, borderRadius: '6px !important' }, '& .Mui-selected': { background: `${COLORS.primary} !important`, color: '#fff !important', borderColor: `${COLORS.primary} !important` } }}>
                        <ToggleButton value="cash">Cash</ToggleButton>
                        <ToggleButton value="cc">CC</ToggleButton>
                        <ToggleButton value="loan">Loan</ToggleButton>
                    </ToggleButtonGroup>
                    {payTermsFor(payMethod).length > 0 && (
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <Select value={payTerm} onChange={(e) => setPayTerm(Number(e.target.value))}
                                sx={{ fontFamily: PP, fontSize: '0.82rem', borderRadius: '8px' }}>
                                {payTermsFor(payMethod).map(t => (
                                    <MenuItem key={t} value={t} sx={{ fontFamily: PP, fontSize: '0.82rem' }}>{t} months</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>
                {/* The deposit question belongs to the SALE, not to one payment type - the
                    customer puts the same deposit down whichever way they pay. It sits
                    directly under the type so it is answered once, in one place.
                    (user 2026-10-17: "Then deposit refundable below of it. Regardless of
                    choosing which") */}
                <FormControlLabel sx={{ mt: 1, mr: 0 }}
                    control={<Checkbox size="small" checked={depositRefundable} onChange={(e) => setDepositRefundable(e.target.checked)} />}
                    label={<Typography sx={{ fontSize: '0.78rem', fontFamily: PP, color: COLORS.text }}>Deposit refundable</Typography>} />
                {(() => {
                    const cash = (cardPricing(selectedOpt, selectedCard) || {}).cash || 0;
                    if (payMethod === 'cc') {
                        const conv = payAdjustedPrice(cash, 'cc', payTerm);
                        return (
                            <Typography sx={{ fontSize: '0.72rem', color: COLORS.text, fontFamily: PP, mt: 1 }}>
                                CC {payTerm}M price: <b>{fmtRM(conv)}</b> <span style={{ color: COLORS.sub }}>(cash {fmtRM(cash)} ÷ {CC_FACTORS[payTerm]}) — this converted amount drives the quotation's final columns.</span>
                            </Typography>
                        );
                    }
                    if (payMethod === 'loan') {
                        const L = buildLoanFigures({ c80: Math.round(cash * 0.80), c20: sstPortion(cash),
                            cSst: sstOn(cash), ltv: loanLtv, active: true });
                        return (
                            <Box sx={{ mt: 1.5 }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1 }}>
                                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP }}>
                                        Bank finances
                                    </Typography>
                                    {/* The four a bank usually offers, then Custom for anything else.
                                        Showing the % box beside the button you just pressed reads as
                                        "type it again" - it only appears when it is wanted.
                                        (user 2026-10-17: "why got two input? When select why still
                                        need to type 90% input") */}
                                    <ToggleButtonGroup exclusive size="small"
                                        value={(!loanLtvCustom && [100, 90, 80, 70].includes(Number(loanLtv))) ? Number(loanLtv) : 'custom'}
                                        onChange={(_, v) => {
                                            if (!v) return;
                                            if (v === 'custom') { setLoanLtvCustom(true); return; }
                                            setLoanLtvCustom(false); setLoanLtv(Number(v));
                                        }}
                                        sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: { xs: '0.8rem', sm: '0.72rem' }, textTransform: 'none', px: { xs: 1.6, sm: 1.2 }, py: { xs: 0.9, sm: 0.3 }, minHeight: { xs: 40, sm: 'auto' }, borderRadius: '7px' }, '& .Mui-selected': { bgcolor: `${COLORS.primary} !important`, color: '#fff !important' } }}>
                                        <ToggleButton value={100}>100%</ToggleButton>
                                        <ToggleButton value={90}>90%</ToggleButton>
                                        <ToggleButton value={80}>80%</ToggleButton>
                                        <ToggleButton value={70}>70%</ToggleButton>
                                        <ToggleButton value="custom">Custom</ToggleButton>
                                    </ToggleButtonGroup>
                                    {(loanLtvCustom || ![100, 90, 80, 70].includes(Number(loanLtv))) && (
                                        <Box sx={{ width: 110 }}>
                                            <Field value={loanLtv} onChange={setLoanLtv} type="number" suffix="%" />
                                        </Box>
                                    )}
                                </Box>
                                {L.markup > 0 ? (
                                    <Typography sx={{ fontSize: '0.72rem', color: COLORS.text, fontFamily: PP }}>
                                        Contract value <b>{fmtRM(L.contractTotal)}</b> = real {fmtRM(L.realTotal)} + markup <b>{fmtRM(L.markup)}</b>
                                        {' \u00b7 '}bank disburses {fmtRM(L.loanAmount)}
                                        {L.deposit > 0 ? ` \u00b7 customer tops up ${fmtRM(L.deposit)}` : ' \u00b7 nothing for the customer to pay'}
                                        {' \u00b7 '}credit note {fmtRM(L.creditNote)} on completion.
                                        <span style={{ color: COLORS.sub }}> The agreement page prints behind the quotation.</span>
                                    </Typography>
                                ) : L.realTotal <= 0 ? (
                                    /* Without a price there is nothing to mark up, and saying "100%
                                       financed" there is simply false. (user 2026-10-17) */
                                    <Typography sx={{ fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP }}>
                                        Enter the system price above and the financing figures appear here.
                                    </Typography>
                                ) : (
                                    <Typography sx={{ fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP }}>
                                        100% financed — no markup, no credit note, and no agreement page. The quotation is the ordinary one.
                                    </Typography>
                                )}
                            </Box>
                        );
                    }
                    return (
                        <Typography sx={{ fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP, mt: 1 }}>
                            Cash price — no conversion.
                        </Typography>
                    );
                })()}
            </div>

            {/* Create Sales — forms are downloaded from the sale page afterwards */}
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 20 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5, gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, fontFamily: PP }}>
                        Create Sales
                    </Typography>
                    <Button size="small" variant="outlined"
                        onClick={() => setClearDialogOpen(true)}
                        sx={{ fontFamily: PP, fontSize: '0.74rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', flexShrink: 0, borderColor: '#CBD5E1', color: '#64748B', '&:hover': { borderColor: '#EF4444', color: '#EF4444', bgcolor: 'rgba(239,68,68,0.06)' } }}>
                        Clear Data
                    </Button>
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP, mb: 2 }}>
                    Create the sale to store the data — download the quotation and commercial forms from the sale page afterwards.
                </Typography>
                {(() => {
                    const disabledBase = !customerName.trim() || !sel.systemCost;
                    const anyRunning = quoteBusy || !!formBusy;
                    const canCreate = user?.roleRights?.includes('CreateSale');
                    if (!canCreate) {
                        return (
                            <Typography sx={{ fontSize: '0.74rem', color: COLORS.sub, fontFamily: PP, fontStyle: 'italic' }}>
                                You don't have permission to create sales.
                            </Typography>
                        );
                    }
                    return (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1 }}>
                            <Button size="small" variant="contained" disabled={sendBotBusy || !sel.systemCost || anyRunning}
                                onClick={sendToSalesBot}
                                startIcon={<TelegramIcon sx={{ fontSize: '0.9rem' }} />}
                                sx={{ fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none', background: 'linear-gradient(135deg,#229ED9,#1B7FB5)', '&:hover': { background: 'linear-gradient(135deg,#1B87BC,#166a99)', boxShadow: 'none' } }}>
                                {sendBotBusy ? 'Preparing…' : 'Create Sales with AI (Telegram)'}
                            </Button>
                            <Button size="small" variant="contained" disabled={disabledBase || anyRunning || submitBusy}
                                onClick={() => doSubmitSale(false)}
                                sx={{ fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none', background: 'linear-gradient(135deg,#16A34A,#15803D)', '&:hover': { background: 'linear-gradient(135deg,#15803D,#166534)', boxShadow: 'none' } }}>
                                {submitBusy ? 'Working…' : 'Create Sales'}
                            </Button>
                            <Button size="small" variant="outlined" disabled={disabledBase || anyRunning || submitBusy}
                                onClick={() => doSubmitSale(true)}
                                sx={{ fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none', borderColor: '#16A34A', color: '#16A34A', '&:hover': { borderColor: '#15803D', backgroundColor: 'rgba(22,163,74,0.08)' } }}>
                                {submitBusy ? 'Working…' : 'Create Sales & Another'}
                            </Button>
                        </Box>
                    );
                })()}
                {/* Quotation + commercial form PDF downloads — straight from the calculator. */}
                {(() => {
                    const disabledBase = !customerName.trim() || !sel.systemCost;
                    const anyRunning = quoteBusy || !!formBusy;
                    const primarySx = { fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none', background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)', '&:hover': { boxShadow: 'none', background: 'linear-gradient(135deg,#0F62CC,#0B4DA3)' }, '&.Mui-disabled': { background: '#E2E8F0', color: '#CBD5E1' } };
                    const outlinedSx = { fontFamily: PP, fontSize: '0.74rem', textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none', borderColor: '#CBD5E1', color: '#475569', '&:hover': { borderColor: '#1A8CFF', bgcolor: 'rgba(26,140,255,0.06)' }, '&.Mui-disabled': { borderColor: '#E2E8F0', color: '#CBD5E1' } };
                    return (
                        <>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(6, 1fr)' }, gap: 1, mt: 1 }}>
                                <Button size="small" variant="contained" disabled={disabledBase || anyRunning} onClick={genQuotation} sx={primarySx}>
                                    {quoteBusy ? 'Working…' : 'Quotation'}
                                </Button>
                                <Button size="small" variant="outlined" disabled={disabledBase || anyRunning} onClick={() => runCommercialForm('atap', generateCommercialAtap)} sx={outlinedSx}>
                                    {formBusy === 'atap' ? 'Working…' : 'ATAP Contract'}
                                </Button>
                                <Button size="small" variant="outlined" disabled={disabledBase || anyRunning} onClick={() => runCommercialForm('declaration', generateCommercialDeclaration)} sx={outlinedSx}>
                                    {formBusy === 'declaration' ? 'Working…' : 'Declaration'}
                                </Button>
                                <Button size="small" variant="outlined" disabled={disabledBase || anyRunning} onClick={() => runCommercialForm('insurance', generateCommercialInsurance2)} sx={outlinedSx}>
                                    {formBusy === 'insurance' ? 'Working…' : 'Insurance'}
                                </Button>
                                <Button size="small" variant="outlined" disabled={disabledBase || anyRunning} onClick={() => runCommercialForm('booking', generateCommercialBooking)} sx={outlinedSx}>
                                    {formBusy === 'booking' ? 'Working…' : 'Booking'}
                                </Button>
                                <Button size="small" variant="contained" disabled={disabledBase || anyRunning} onClick={() => runCommercialForm('all', generateCommercialAll)} sx={primarySx}>
                                    {formBusy === 'all' ? 'Working…' : 'All (ZIP)'}
                                </Button>
                            </Box>
                        </>
                    );
                })()}
                {err && <Typography sx={{ color: '#EF4444', fontSize: '0.74rem', fontFamily: PP, mt: 1.25 }}>{err}</Typography>}
            </div>
        </Box>
    );
}

// `suffix` prints a unit INSIDE the box (e.g. "%"), for a field narrow enough that a label
// above it would be wider than the input itself. (user 2026-10-15: "the percentage put
// inside input")
function Field({ label, value, onChange, type = 'text', disabled, required, options, suffix, placeholder }) {
    const isSelect = Array.isArray(options);
    // A saved sale may carry an old free-text warranty value not in the option list —
    // include it so the dropdown still displays it (user can then switch to a standard one).
    const opts = isSelect && value && !options.includes(value) ? [value, ...options] : options;
    return (
        <Box>
            {label && (
                <Typography sx={{ fontSize: '0.74rem', color: '#374151', fontFamily: PP, mb: 0.4 }}>
                    {label}{required && <span style={{ color: '#EF4444', marginLeft: 2 }}>*</span>}
                </Typography>
            )}
            <TextField fullWidth size="small" type={type} value={value} disabled={disabled}
                select={isSelect} placeholder={placeholder}
                onChange={e => onChange && onChange(e.target.value)}
                InputProps={{
                    sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' },
                    ...(suffix ? {
                        endAdornment: (
                            <InputAdornment position="end">
                                <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', color: '#64748B' }}>{suffix}</Typography>
                            </InputAdornment>
                        ),
                    } : {}),
                }}>
                {isSelect && opts.map(o => (
                    <MenuItem key={o} value={o} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>{o}</MenuItem>
                ))}
            </TextField>
        </Box>
    );
}

